import React, { useState, useEffect, useCallback, useRef, useMemo, useContext, useTransition } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { apiClient, AppContext } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { X, Activity, Grid3X3, Settings2, Filter, Save, Clock, RefreshCw, Search, ExternalLink, Download, ChevronDown, ChevronUp, Loader2, Copy, Plus, Eye, EyeOff, Archive, Bell, BellOff, Volume2, Table2 } from "lucide-react";
import { 
  SmartFiltersPanel, 
  DEFAULT_SMART_FILTERS, 
  calculateMaxValues, 
  applySmartFilters,
  applySmartFiltersAsync,
  getSmartFilterRowClass,
  getFilterBadges,
  getFilterHeaderRow,
  sendSmartFilterTelegramAlert,
  sendBasicFilterTelegramAlert
} from "./SmartFilters";

// ==================== PERFORMANCE OPTIMIZATION UTILITIES ====================
// These help prevent UI freezing when processing large datasets with multiple filters

// Throttle function - limits how often a function can be called
const throttle = (fn, delay) => {
  let lastCall = 0;
  let timeoutId = null;
  return (...args) => {
    const now = Date.now();
    if (now - lastCall >= delay) {
      lastCall = now;
      return fn(...args);
    } else if (!timeoutId) {
      timeoutId = setTimeout(() => {
        lastCall = Date.now();
        timeoutId = null;
        fn(...args);
      }, delay - (now - lastCall));
    }
  };
};

// Batch filter processing - processes filters in chunks to prevent blocking
const batchProcess = (items, filterFn, batchSize = 1000) => {
  const results = [];
  for (let i = 0; i < items.length; i += batchSize) {
    const batch = items.slice(i, i + batchSize);
    results.push(...batch.filter(filterFn));
  }
  return results;
};

// Memoization cache for expensive filter results
const filterResultCache = new Map();
const CACHE_MAX_SIZE = 50;
const CACHE_TTL = 5000; // 5 seconds

const getCachedFilterResult = (cacheKey) => {
  const cached = filterResultCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.result;
  }
  return null;
};

const setCachedFilterResult = (cacheKey, result) => {
  // Limit cache size
  if (filterResultCache.size >= CACHE_MAX_SIZE) {
    const oldestKey = filterResultCache.keys().next().value;
    filterResultCache.delete(oldestKey);
  }
  filterResultCache.set(cacheKey, { result, timestamp: Date.now() });
};

// ==================== ERROR BOUNDARY FOR STABILITY ====================
class TickHistoryErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('[TickHistory] Error caught:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="flex items-center justify-center h-full bg-[#0a0a0a] text-white p-4">
          <div className="text-center">
            <div className="text-red-400 text-lg mb-2">Something went wrong</div>
            <div className="text-[#666] text-sm mb-4">{this.state.error?.message || 'Unknown error'}</div>
            <button 
              onClick={() => {
                this.setState({ hasError: false, error: null });
                window.location.reload();
              }}
              className="px-4 py-2 bg-blue-600 rounded hover:bg-blue-700"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// CONSTANTS - OPTIMIZED FOR SPEED (Now configurable via Performance Settings)
const MAX_SYMBOLS = 30; // Max 30 symbols in Grid/Multi (increased for Light Mode)
const MAX_INSTANCES_PER_SYMBOL = 7; // Max instances of same symbol in Grid view
const DEFAULT_UPDATE_INTERVAL = 100; // Default 100ms updates
const DEFAULT_MAX_TICKS = 5000; // Default max ticks per symbol (reduced from 50K for performance)
// PERF MODE V2: how many ticks we keep in the FILTER/ALERT buffer (not what we render).
// Render still uses maxTicksPerSymbol. This lets filters/alerts evaluate against full history
// (PDH/PDL/VWAP need it) without bloating React's virtual DOM.
// Toggle off via localStorage.setItem('perf_mode_v2','off') and reload to fall back to old behavior.
const PERF_V2_BUFFER_MAX = 200000;
const isPerfV2Enabled = () => {
  try { return (typeof window !== 'undefined') && window.localStorage?.getItem('perf_mode_v2') !== 'off'; }
  catch { return true; }
};
const AUTO_SAVE_TIMES = { afternoon: 15.5, night: 23.917 }; // 3:30 PM and 11:55 PM IST

// Performance preset options - 4 modes for different use cases
const PERFORMANCE_PRESETS = {
  // INSTANT MODE - True real-time updates (High CPU, every tick visible)
  // Best for: Active trading, scalping, watching single symbol closely
  // Uses 20ms update interval for smooth real-time feel
  instant: { interval: 20, maxTicks: 200000, label: '⚡ Instant (Real-time)', mode: 'instant', minInterval: 20 },
  
  // BATCH MODE - Batched updates every 200ms (Low CPU, 5-10 ticks at once)
  // Best for: Multiple symbols, grid view, lower-end devices
  // Deliberately slower to reduce CPU usage
  batch: { interval: 200, maxTicks: 200000, label: '📦 Batch (Low CPU)', mode: 'instant', minInterval: 200 },
  
  // BACKEND MODES - Fetch from MongoDB (Lowest CPU) - For Pop-out History
  backend_30s: { interval: 30000, maxTicks: 5000, label: '🔄 Refresh 30s', mode: 'backend', minInterval: 30000 },
  backend_1min: { interval: 60000, maxTicks: 5000, label: '🔄 Refresh 1min', mode: 'backend', minInterval: 60000 },
};

// Market Hours Configuration (IST)
const MARKET_HOURS = {
  NSE: { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  BSE: { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  NFO: { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  BFO: { start: { hour: 9, minute: 15 }, end: { hour: 15, minute: 30 } },
  MCX: { start: { hour: 9, minute: 0 }, end: { hour: 23, minute: 54 } },
  CDS: { start: { hour: 9, minute: 0 }, end: { hour: 17, minute: 0 } },
};

// Check if time is within market hours for given exchange
// Check if current time is within market hours for any major exchange
const getCurrentMarketStatus = () => {
  const now = new Date();
  const dayOfWeek = now.getDay();
  
  // Helper to get last trading day date string
  const getLastTradingDayStr = () => {
    const today = new Date();
    let daysBack = 0;
    
    if (dayOfWeek === 0) daysBack = 2; // Sunday -> Friday
    else if (dayOfWeek === 6) daysBack = 1; // Saturday -> Friday
    
    if (daysBack > 0) {
      const lastDay = new Date(today);
      lastDay.setDate(lastDay.getDate() - daysBack);
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      return `${lastDay.getDate()} ${months[lastDay.getMonth()]}`;
    }
    return null;
  };
  
  // Weekend check
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    const lastDayStr = getLastTradingDayStr();
    return { isOpen: false, exchange: 'ALL', message: `Weekend - Showing ${lastDayStr} Data`, closedAt: null, isPreMarket: false, minutesToOpen: null, lastTradingDay: lastDayStr };
  }
  
  // Get IST time
  const istOffset = 5.5 * 60;
  const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const istMinutes = utcMinutes + istOffset;
  const istHour = Math.floor((istMinutes % (24 * 60)) / 60);
  const istMinute = Math.floor(istMinutes % 60);
  const currentMinutes = istHour * 60 + istMinute;
  
  // Format current IST time for display
  const istTimeStr = `${String(istHour).padStart(2, '0')}:${String(istMinute).padStart(2, '0')}`;
  
  // Check NSE/NFO (9:15 AM - 3:30 PM)
  const nseStart = 9 * 60 + 15;
  const nseEnd = 15 * 60 + 30;
  const nseOpen = currentMinutes >= nseStart && currentMinutes <= nseEnd;
  
  // Check MCX (9:00 AM - 11:54 PM / 23:54)
  const mcxStart = 9 * 60;
  const mcxEnd = 23 * 60 + 54;
  const mcxOpen = currentMinutes >= mcxStart && currentMinutes <= mcxEnd;
  
  // Calculate minutes to next market open (for pre-market refresh)
  let minutesToNseOpen = nseStart - currentMinutes;
  let minutesToMcxOpen = mcxStart - currentMinutes;
  if (minutesToNseOpen < 0) minutesToNseOpen = 24 * 60 + minutesToNseOpen; // Next day
  if (minutesToMcxOpen < 0) minutesToMcxOpen = 24 * 60 + minutesToMcxOpen; // Next day
  const minutesToOpen = Math.min(minutesToNseOpen, minutesToMcxOpen);
  
  // Check if we're within 5 minutes of market open (pre-market refresh zone)
  const isPreMarketRefreshZone = !nseOpen && !mcxOpen && minutesToOpen <= 5 && minutesToOpen > 0;
  
  if (nseOpen && mcxOpen) {
    return { isOpen: true, exchange: 'ALL', message: `LIVE (${istTimeStr} IST)`, closedAt: null, isPreMarket: false, minutesToOpen: null };
  } else if (nseOpen) {
    return { isOpen: true, exchange: 'NSE', message: `NSE LIVE (${istTimeStr})`, closedAt: null, isPreMarket: false, minutesToOpen: null };
  } else if (mcxOpen) {
    return { isOpen: true, exchange: 'MCX', message: `MCX LIVE (${istTimeStr})`, closedAt: '15:30', isPreMarket: false, minutesToOpen: null };
  } else {
    // Both closed - show historical ticks
    if (currentMinutes < nseStart) {
      const msg = isPreMarketRefreshZone 
        ? `Pre-Market (${istTimeStr}) - Refreshing in ${minutesToOpen} min`
        : `Pre-Market (${istTimeStr}) - Showing Previous Day`;
      return { isOpen: false, exchange: 'ALL', message: msg, closedAt: null, isPreMarket: true, minutesToOpen, isPreMarketRefreshZone };
    } else {
      return { isOpen: false, exchange: 'ALL', message: `Closed (${istTimeStr}) - Showing Today's Data`, closedAt: '23:54', isPreMarket: false, minutesToOpen };
    }
  }
};

// Helper: Extract timestamp from tick (handles null timestamp_ms with fallback to timestamp string)
const getTickTimestamp = (tick) => {
  if (!tick) return null;
  
  // Prefer timestamp_ms if available
  if (tick.timestamp_ms) return tick.timestamp_ms;
  
  // Fallback to parsing timestamp string
  if (tick.timestamp) {
    try {
      const parsed = new Date(tick.timestamp).getTime();
      if (!isNaN(parsed)) return parsed;
    } catch (e) {
      // Ignore parsing errors
    }
  }
  
  // Try exchange_timestamp as last resort
  if (tick.exchange_timestamp) {
    try {
      const parsed = new Date(tick.exchange_timestamp).getTime();
      if (!isNaN(parsed)) return parsed;
    } catch (e) {
      // Ignore parsing errors
    }
  }
  
  return null;
};

const isWithinMarketHours = (timestamp, exchange) => {
  // If no timestamp, reject the tick
  if (!timestamp) return false;
  
  // Default to NSE if no exchange (most common)
  const effectiveExchange = exchange || 'NSE';
  
  const date = new Date(timestamp);
  
  // Validate date is valid
  if (isNaN(date.getTime())) return false;
  
  // Weekend check (Saturday=6, Sunday=0)
  const dayOfWeek = date.getDay();
  if (dayOfWeek === 0 || dayOfWeek === 6) {
    return false; // Markets closed on weekends
  }
  
  // DATE CHECK - Only allow today's ticks OR last trading day if market closed
  const now = new Date();
  const tickDateStr = date.toISOString().split('T')[0];
  const todayStr = now.toISOString().split('T')[0];
  
  // Convert to IST for time calculations
  const istOffset = 5.5 * 60; // minutes
  const utcMinutes = date.getUTCHours() * 60 + date.getUTCMinutes();
  const istMinutes = utcMinutes + istOffset;
  const istHour = Math.floor((istMinutes % (24 * 60)) / 60);
  const istMinute = Math.floor(istMinutes % 60);
  
  const hours = MARKET_HOURS[effectiveExchange] || MARKET_HOURS['NSE'];
  const startMinutes = hours.start.hour * 60 + hours.start.minute;
  const endMinutes = hours.end.hour * 60 + hours.end.minute;
  const currentMinutes = istHour * 60 + istMinute;
  
  // Get current IST time
  const nowUtcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
  const nowIstMinutes = nowUtcMinutes + istOffset;
  const nowIstHour = Math.floor((nowIstMinutes % (24 * 60)) / 60);
  const nowIstMinute = Math.floor(nowIstMinutes % 60);
  const nowCurrentMinutes = nowIstHour * 60 + nowIstMinute;
  
  // Check if market is currently open
  const isMarketCurrentlyOpen = effectiveExchange === 'MCX' 
    ? (nowCurrentMinutes >= startMinutes && nowCurrentMinutes <= endMinutes)
    : (nowCurrentMinutes >= startMinutes && nowCurrentMinutes <= endMinutes);
  
  // If tick is from today - check if within market hours
  if (tickDateStr === todayStr) {
    if (effectiveExchange === 'MCX') {
      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    }
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }
  
  // If tick is from previous day - only allow if market is currently CLOSED
  if (tickDateStr < todayStr) {
    // Don't show previous day's data when market is open
    if (isMarketCurrentlyOpen) {
      return false;
    }
    // Market is closed - check if tick was within that day's market hours
    if (effectiveExchange === 'MCX') {
      return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
    }
    return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
  }
  
  // Future dated ticks should not be shown
  return false;
};

// Helper: Check if tick is within market hours (handles null timestamp_ms)
const isTickWithinMarketHours = (tick, defaultExchange = 'NSE') => {
  const ts = getTickTimestamp(tick);
  if (!ts) return false;
  const exchange = tick.exchange || defaultExchange;
  return isWithinMarketHours(ts, exchange);
};

// Get exchange from instrument - handles various naming conventions
const getExchangeFromInstrument = (instrument) => {
  if (!instrument) return 'NSE';
  
  // Check exchange field
  const exchange = (instrument.exchange || '').toUpperCase();
  const segment = (instrument.segment || '').toUpperCase();
  const tradingsymbol = (instrument.tradingsymbol || '').toUpperCase();
  
  // MCX detection - check exchange, segment, or symbol patterns (GOLD, SILVER, CRUDE, etc.)
  if (exchange.includes('MCX') || segment.includes('MCX')) return 'MCX';
  
  // Also detect MCX by common commodity symbols in tradingsymbol
  const mcxPatterns = ['GOLD', 'SILVER', 'CRUDE', 'COPPER', 'ZINC', 'LEAD', 'ALUMIN', 'NICKEL', 'NATGAS', 'COTTON', 'CRUDEOIL'];
  for (const pattern of mcxPatterns) {
    if (tradingsymbol.includes(pattern)) return 'MCX';
  }
  
  // Other exchanges
  if (exchange.includes('CDS') || segment.includes('CDS')) return 'CDS';
  if (exchange.includes('NFO') || segment.includes('NFO')) return 'NFO';
  if (exchange.includes('BFO') || segment.includes('BFO')) return 'BFO';
  if (exchange.includes('BSE') || segment.includes('BSE')) return 'BSE';
  
  return 'NSE';
};

// Formatters
const formatNumber = (num, decimals = 2) => {
  if (num === undefined || num === null) return "-";
  return Number(num).toLocaleString('en-IN', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
};

const formatVolume = (num) => {
  if (!num) return "-";
  if (num >= 10000000) return (num / 10000000).toFixed(2) + " Cr";
  if (num >= 100000) return (num / 100000).toFixed(2) + " L";
  if (num >= 1000) return (num / 1000).toFixed(1) + " K";
  return num.toLocaleString('en-IN');
};

const formatTime = (ts) => {
  if (!ts) return "-";
  const d = new Date(ts);
  return `${d.getHours().toString().padStart(2,'0')}:${d.getMinutes().toString().padStart(2,'0')}:${d.getSeconds().toString().padStart(2,'0')}`;
};

const getSeconds = (ts) => ts ? new Date(ts).getSeconds() : -1;

// Helper function to get instrument by token (handles both number and string keys)
const getInstrumentByToken = (instruments, token) => {
  return instruments[token] || instruments[token?.toString()] || instruments[parseInt(token)] || null;
};

// Get symbol name from instrument (with fallback)
const getSymbolName = (instruments, token) => {
  const inst = getInstrumentByToken(instruments, token);
  return inst?.tradingsymbol || `Token_${token}`;
};

// ==================== PERSISTENT TICK STORAGE ====================
// Store ticks to backend periodically
const storeTicksToBackend = async (tickHistory, instruments, lastStoredIndex, setLastStoredIndex) => {
  try {
    const batches = [];
    const newIndexes = { ...lastStoredIndex };
    
    for (const [tokenStr, ticks] of Object.entries(tickHistory)) {
      const token = parseInt(tokenStr);
      const lastIdx = lastStoredIndex[token] || 0;
      const newTicks = ticks.slice(lastIdx);
      
      if (newTicks.length > 0) {
        batches.push({
          token,
          symbol: getSymbolName(instruments, token),
          ticks: newTicks
        });
        newIndexes[token] = ticks.length;
      }
    }
    
    if (batches.length > 0) {
      await apiClient.post('/history/ticks/store', { batches });
      setLastStoredIndex(newIndexes);
    }
  } catch (err) {
    console.error('Error storing ticks:', err);
  }
};

// Load historical ticks from backend
const loadHistoricalTicks = async (token, date = null, limit = 5000, skip = 0, exchange = 'NSE') => {
  try {
    const params = new URLSearchParams({ 
      limit: limit.toString(), 
      skip: skip.toString(),
      latest_first: 'true',  // Get newest data first
      exchange: exchange     // Pass exchange for correct market hours validation
    });
    if (date) params.append('date', date);
    
    const response = await apiClient.get(`/history/ticks/${token}?${params}`);
    return response.data;
  } catch (err) {
    console.error('Error loading historical ticks:', err);
    return { ticks: [], total: 0, has_more: false };
  }
};

// Get available dates for a token
const getTickDates = async (token) => {
  try {
    const response = await apiClient.get(`/history/ticks/dates/${token}`);
    return response.data.dates || [];
  } catch (err) {
    console.error('Error getting tick dates:', err);
    return [];
  }
};

// ==================== POP-OUT WINDOW MANAGEMENT ====================
const popOutWindows = new Map(); // Track open pop-out windows: Map<token, Map<windowId, window>>
const BROADCAST_CHANNEL_NAME = 'ticklog_sync';
const MAX_WINDOWS_PER_SYMBOL = 5;

// Get count of open windows for a token
const getOpenWindowCount = (token) => {
  const tokenWindows = popOutWindows.get(token);
  if (!tokenWindows) return 0;
  
  // Clean up closed windows
  let count = 0;
  tokenWindows.forEach((win, id) => {
    if (win && !win.closed) {
      count++;
    } else {
      tokenWindows.delete(id);
    }
  });
  
  return count;
};

// Open a pop-out window for a symbol (allows multiple up to MAX_WINDOWS_PER_SYMBOL)
// Now accepts ticks parameter to send full history on initial open
const openPopOutWindow = (token, symbol, exchange, forceNew = false, ticks = null) => {
  // Initialize Map for this token if not exists
  if (!popOutWindows.has(token)) {
    popOutWindows.set(token, new Map());
  }
  
  const tokenWindows = popOutWindows.get(token);
  
  // Clean up closed windows
  tokenWindows.forEach((win, id) => {
    if (win && win.closed) {
      tokenWindows.delete(id);
    }
  });
  
  // If not forcing new and there's an existing window, focus it
  if (!forceNew && tokenWindows.size > 0) {
    const firstWindow = tokenWindows.values().next().value;
    if (firstWindow && !firstWindow.closed) {
      firstWindow.focus();
      return firstWindow;
    }
  }
  
  // Check if we've reached the max windows limit
  if (tokenWindows.size >= MAX_WINDOWS_PER_SYMBOL) {
    alert(`Maximum ${MAX_WINDOWS_PER_SYMBOL} windows allowed per symbol. Please close an existing window first.`);
    return null;
  }
  
  // Send full tick history if available (for initial load in pop-out)
  if (ticks && ticks.length > 0) {
    const storedCount = sendFullTickHistoryToPopout(token, ticks);
    console.log(`[PopOut] Opening window with ${storedCount} ticks available`);
  }
  
  const windowId = Date.now(); // Unique ID for this window
  const windowNum = tokenWindows.size + 1;
  
  const width = 800;
  const height = 600;
  const left = window.screenX + 50 + (windowNum * 30);
  const top = window.screenY + 50 + (windowNum * 30);
  
  const popOutUrl = `${window.location.origin}/popout?token=${token}&symbol=${encodeURIComponent(symbol)}&exchange=${exchange}&wid=${windowId}`;
  
  const newWindow = window.open(
    popOutUrl,
    `ticklog_${token}_${windowId}`,
    `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes`
  );
  
  if (newWindow) {
    tokenWindows.set(windowId, newWindow);
    
    // Clean up when window closes
    const checkClosed = setInterval(() => {
      if (newWindow.closed) {
        tokenWindows.delete(windowId);
        if (tokenWindows.size === 0) {
          popOutWindows.delete(token);
        }
        clearInterval(checkClosed);
      }
    }, 1000);
  }
  
  return newWindow;
};

// Open additional window for same symbol (forces new window)
const openAdditionalPopOutWindow = (token, symbol, exchange, ticks = null) => {
  return openPopOutWindow(token, symbol, exchange, true, ticks);
};

// Broadcast tick data to pop-out windows
const broadcastTickData = (token, ticks) => {
  try {
    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    // Send last 500 ticks for live updates (continuous streaming)
    channel.postMessage({ type: 'tick_update', token, ticks: ticks.slice(-500) });
    channel.close();
  } catch (err) {
    // BroadcastChannel not supported, use localStorage fallback
    localStorage.setItem(`ticklog_live_${token}`, JSON.stringify({
      timestamp: Date.now(),
      ticks: ticks.slice(-500)
    }));
  }
};

// Send FULL tick history to pop-out window on initial open
// This stores all current ticks in localStorage for the pop-out to pick up
const sendFullTickHistoryToPopout = (token, ticks) => {
  try {
    // Store full tick history (up to 100K ticks) for pop-out window to load
    const fullHistory = ticks.slice(0, 100000);
    localStorage.setItem(`ticklog_full_history_${token}`, JSON.stringify({
      timestamp: Date.now(),
      ticks: fullHistory,
      count: fullHistory.length
    }));
    console.log(`[PopOut] Stored ${fullHistory.length} ticks for pop-out window (token: ${token})`);
    return fullHistory.length;
  } catch (err) {
    console.error('[PopOut] Error storing full history:', err);
    // If localStorage is full, try storing less
    try {
      const reducedHistory = ticks.slice(0, 20000);
      localStorage.setItem(`ticklog_full_history_${token}`, JSON.stringify({
        timestamp: Date.now(),
        ticks: reducedHistory,
        count: reducedHistory.length
      }));
      console.log(`[PopOut] Stored reduced ${reducedHistory.length} ticks due to storage limits`);
      return reducedHistory.length;
    } catch (err2) {
      console.error('[PopOut] Failed to store even reduced history:', err2);
      return 0;
    }
  }
};

// GRADIENT Data bar component - like watchlist
// PERFORMANCE OPTIMIZED: DataBar with memoization
const DataBar = React.memo(({ value, max, color, showSign = false, originalValue = null }) => {
  // Use originalValue for sign display if provided, otherwise use value
  const displayValue = originalValue !== null ? originalValue : value;
  const isNegative = displayValue < 0;
  const absValue = Math.abs(value);
  
  const pct = max > 0 ? Math.min((absValue / max) * 100, 100) : 0;
  const intensity = 0.3 + (pct / 100) * 0.7;
  
  // Pre-computed gradient strings for common colors
  const gradients = {
    green: `linear-gradient(90deg, rgba(34,197,94,${(intensity * 0.2).toFixed(2)}) 0%, rgba(34,197,94,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    red: `linear-gradient(90deg, rgba(239,68,68,${(intensity * 0.2).toFixed(2)}) 0%, rgba(239,68,68,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    blue: `linear-gradient(90deg, rgba(59,130,246,${(intensity * 0.2).toFixed(2)}) 0%, rgba(59,130,246,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    cyan: `linear-gradient(90deg, rgba(6,182,212,${(intensity * 0.2).toFixed(2)}) 0%, rgba(6,182,212,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    yellow: `linear-gradient(90deg, rgba(234,179,8,${(intensity * 0.2).toFixed(2)}) 0%, rgba(234,179,8,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    purple: `linear-gradient(90deg, rgba(168,85,247,${(intensity * 0.2).toFixed(2)}) 0%, rgba(168,85,247,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
    orange: `linear-gradient(90deg, rgba(249,115,22,${(intensity * 0.2).toFixed(2)}) 0%, rgba(249,115,22,${intensity.toFixed(2)}) ${pct.toFixed(1)}%, transparent ${pct.toFixed(1)}%)`,
  };
  
  // Format the display text with sign if needed
  const displayText = showSign && isNegative ? `-${formatVolume(absValue)}` : formatVolume(absValue);
  
  return (
    <div className="relative h-5 bg-[#1a1a1a] rounded-sm overflow-hidden">
      <div className="absolute inset-0" style={{ background: gradients[color] || gradients.blue }} />
      <span className="absolute inset-0 flex items-center justify-center text-[10px] font-mono text-white drop-shadow-sm">{displayText}</span>
    </div>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if values changed significantly
  const valueSame = prevProps.value === nextProps.value;
  const maxSame = prevProps.max === nextProps.max;
  const colorSame = prevProps.color === nextProps.color;
  const showSignSame = prevProps.showSign === nextProps.showSign;
  const originalValueSame = prevProps.originalValue === nextProps.originalValue;
  return valueSame && maxSame && colorSame && showSignSame && originalValueSame;
});

// Format time in IST HH:MM:SS
const formatTimeIST = (ts) => {
  if (!ts) return "-";
  const d = new Date(ts);
  // Add 5:30 hours for IST
  const istTime = new Date(d.getTime() + (5.5 * 60 * 60 * 1000));
  return `${istTime.getUTCHours().toString().padStart(2,'0')}:${istTime.getUTCMinutes().toString().padStart(2,'0')}:${istTime.getUTCSeconds().toString().padStart(2,'0')}`;
};

// Column definitions - ALL columns including bid_price, ask_price, trade_pos
// Note: agg_buy and agg_sell are only available in Pop-out window
const COLUMNS = [
  { key: 'ltp', label: 'LTP' },
  { key: 'ltp_diff', label: 'LTP Diff' },
  { key: 'bid_price', label: 'Bid Price' },
  { key: 'ask_price', label: 'Ask Price' },
  { key: 'bid_qty', label: 'Bid Qty' },
  { key: 'ask_qty', label: 'Ask Qty' },
  { key: 'trade_pos', label: 'Agg %' },
  { key: 'spread', label: 'Spread' },
  { key: 'ltq', label: 'LTQ' },
  { key: 'vol_chg', label: 'Vol Chg' },
  { key: 'volume', label: 'Volume' },
  { key: 'oi', label: 'OI' },
  { key: 'oi_change', label: 'OI Chg' },
  { key: 'time', label: 'Time (IST)' },
];

// Calculate Trade Position % - where the trade occurred within the spread
// 0% = at bid (seller aggressive), 100% = at ask (buyer aggressive)
// >100% = breakout above ask, <0% = breakdown below bid
const calculateTradePosition = (tick) => {
  const ltp = tick.last_price || tick.ltp || 0;
  const bid = tick.bid_price || 0;
  const ask = tick.ask_price || 0;
  const spread = ask - bid;
  
  if (spread <= 0 || bid <= 0 || ask <= 0) return null;
  
  return ((ltp - bid) / spread) * 100;
};

// ==================== ALERT SOUND UTILITY ====================
// Play notification sound with different types
const playAlertSound = (soundType = 'chime') => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    const audioCtx = new AudioContext();
    
    const playTone = (frequency, startTime, duration, type = 'sine', volume = 0.3) => {
      const oscillator = audioCtx.createOscillator();
      const gainNode = audioCtx.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioCtx.destination);
      
      oscillator.frequency.value = frequency;
      oscillator.type = type;
      
      // Envelope for smooth sound
      gainNode.gain.setValueAtTime(0, startTime);
      gainNode.gain.linearRampToValueAtTime(volume, startTime + 0.02);
      gainNode.gain.linearRampToValueAtTime(volume * 0.7, startTime + duration * 0.5);
      gainNode.gain.linearRampToValueAtTime(0, startTime + duration);
      
      oscillator.start(startTime);
      oscillator.stop(startTime + duration);
    };
    
    const now = audioCtx.currentTime;
    
    switch (soundType) {
      case 'chime':
        // Pleasant two-tone chime: C5 then E5
        playTone(523.25, now, 0.12);
        playTone(659.25, now + 0.12, 0.18);
        break;
      case 'ding':
        // Single high ding
        playTone(880, now, 0.25, 'sine', 0.4);
        break;
      case 'double':
        // Double beep
        playTone(800, now, 0.1);
        playTone(800, now + 0.15, 0.1);
        break;
      case 'rising':
        // Rising three-tone
        playTone(440, now, 0.1);
        playTone(554.37, now + 0.1, 0.1);
        playTone(659.25, now + 0.2, 0.15);
        break;
      case 'urgent':
        // Urgent fast triple beep
        playTone(1000, now, 0.08, 'square', 0.2);
        playTone(1000, now + 0.1, 0.08, 'square', 0.2);
        playTone(1200, now + 0.2, 0.12, 'square', 0.25);
        break;
      default:
        // Default chime
        playTone(523.25, now, 0.12);
        playTone(659.25, now + 0.12, 0.18);
    }
    
    // Close context after sound plays
    setTimeout(() => audioCtx.close(), 600);
    return true;
  } catch (e) {
    console.warn('Could not play alert sound:', e);
    return false;
  }
};

// ==================== FILTER SAVE MODE UTILITIES ====================
// Constants for filter save mode
const MAX_SAVED_TICKS = 10000; // Maximum saved ticks per table (filtered ticks)
const FILTER_SAVE_MODES = { OFF: 'off', SAVE: 'save', SHOW_ALL: 'show_all', HIDE: 'hide' };

// Market close times for auto-download (IST)
const MARKET_CLOSE_TIMES = {
  NSE: { hour: 15, minute: 31 }, // 3:31 PM IST (1 min after close)
  MCX: { hour: 23, minute: 55 }, // 11:55 PM IST (1 min after close)
};

// CSV Download utility - exports ticks to CSV file
// Now accepts visibleColumns (array of column keys) and columnOrder for correct sequence
const downloadTicksAsCSV = (ticks, symbol, visibleColumns = null, columnOrder = null) => {
  console.log('[downloadTicksAsCSV] Called with:', {
    ticksCount: ticks?.length || 0,
    symbol,
    hasVisibleColumns: !!visibleColumns,
    hasColumnOrder: !!columnOrder
  });
  
  if (!ticks || ticks.length === 0) {
    console.log('[downloadTicksAsCSV] No ticks - showing toast');
    toast.error('No saved ticks to download!', {
      description: 'Enable "Save" mode first, then filtered ticks will be recorded automatically.',
      duration: 5000
    });
    return false;
  }
  
  // Show starting toast
  toast.loading('Preparing download...', { id: 'download-progress' });
  
  // Determine which columns to export (only visible, in order)
  let columnsToExport = COLUMNS;
  if (visibleColumns && columnOrder) {
    // Filter and order columns based on visibility and order
    columnsToExport = columnOrder
      .filter(key => visibleColumns.includes(key))
      .map(key => COLUMNS.find(c => c.key === key))
      .filter(Boolean);
  } else if (visibleColumns) {
    columnsToExport = COLUMNS.filter(c => visibleColumns.includes(c.key));
  }
  
  // Build CSV header
  const headers = ['Symbol', 'Timestamp', ...columnsToExport.map(c => c.label)];
  
  // Build CSV rows
  const rows = ticks.map(tick => {
    const row = [
      symbol || tick.trading_symbol || 'Unknown',
      tick.timestamp || new Date(tick.timestamp_ms || tick.exchange_timestamp).toISOString(),
    ];
    
    columnsToExport.forEach(col => {
      switch(col.key) {
        case 'ltp': row.push(tick.last_price || 0); break;
        case 'ltp_diff': row.push(tick.ltp_diff || 0); break;
        case 'bid_price': row.push(tick.bid_price || 0); break;
        case 'ask_price': row.push(tick.ask_price || 0); break;
        case 'bid_qty': row.push(tick.bid_qty || 0); break;
        case 'ask_qty': row.push(tick.ask_qty || 0); break;
        case 'trade_pos': {
          const pos = calculateTradePosition(tick);
          row.push(pos !== null ? pos.toFixed(2) : '');
          break;
        }
        case 'spread': row.push(tick.spread || ((tick.ask_price || 0) - (tick.bid_price || 0))); break;
        case 'ltq': row.push(tick.last_traded_quantity || 0); break;
        case 'vol_chg': row.push(tick.volume_change || 0); break;
        case 'volume': row.push(tick.volume || tick.volume_traded || 0); break;
        case 'oi': row.push(tick.oi || 0); break;
        case 'oi_change': row.push(tick.oi_change || 0); break;
        case 'time': row.push(tick.timestamp || ''); break;
        default: row.push('');
      }
    });
    
    return row;
  });
  
  // Convert to CSV string
  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.map(cell => {
      // Escape quotes and wrap in quotes if contains comma
      const str = String(cell);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return `"${str.replace(/"/g, '""')}"`;
      }
      return str;
    }).join(','))
  ].join('\n');
  
  // Create and download file using file-saver
  try {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const date = new Date().toISOString().split('T')[0];
    const time = new Date().toTimeString().split(' ')[0].replace(/:/g, '-');
    const filename = `${symbol || 'ticks'}_filtered_${date}_${time}.csv`;
    
    console.log('[downloadTicksAsCSV] Triggering download with file-saver:', { filename, blobSize: blob.size });
    
    // Use file-saver library for reliable downloads
    saveAs(blob, filename);
    
    console.log('[downloadTicksAsCSV] Download triggered successfully:', filename);
    
    // Show success toast
    toast.success(`Download started: ${filename}`, {
      id: 'download-progress',
      description: `${ticks.length.toLocaleString()} ticks • ${(blob.size / 1024).toFixed(1)} KB`,
      duration: 4000
    });
    
    return true; // Return success for auto-download tracking
    
  } catch (error) {
    console.error('[downloadTicksAsCSV] Error creating download:', error);
    toast.error('Download failed!', {
      id: 'download-progress',
      description: error.message,
      duration: 5000
    });
    return false;
  }
};

// Filter Save Mode Toggle UI Component - Now always shows CSV button and auto-download toggle
const FilterSaveModeToggle = ({ mode, setMode, savedCount, onDownload, onDownloadFiltered, filteredCount = 0, onClear, autoDownload, setAutoDownload, alertEnabled, setAlertEnabled, compact = false }) => {
  const buttonSize = compact ? 'h-5 px-1.5 text-[8px]' : 'h-6 px-2 text-[9px]';
  const iconSize = compact ? 'w-2.5 h-2.5' : 'w-3 h-3';
  
  // Test sound when right-clicking the alert button
  const handleAlertClick = (e) => {
    if (e.shiftKey || e.ctrlKey) {
      // Test sound
      playAlertSound('chime');
      return;
    }
    setAlertEnabled && setAlertEnabled(!alertEnabled);
  };
  
  return (
    <div className="flex items-center gap-1">
      {/* Alert Toggle - NEW */}
      <button
        onClick={handleAlertClick}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all ${
          alertEnabled 
            ? 'bg-yellow-500/30 text-yellow-300 border border-yellow-400 shadow-lg shadow-yellow-500/20 animate-pulse' 
            : 'bg-[#1a1a1a] text-yellow-400 border border-yellow-500/40 hover:bg-yellow-500/10'
        }`}
        title={alertEnabled ? "Alert ON - Click to disable | Shift+Click to test sound" : "Alert OFF - Click to enable notifications | Shift+Click to test sound"}
      >
        {alertEnabled ? <Bell className={iconSize} /> : <BellOff className={iconSize} />}
        {alertEnabled ? 'ON' : 'Alert'}
      </button>
      
      {/* Separator */}
      <div className="w-px h-4 bg-[#333] mx-0.5" />
      
      {/* Save Toggle */}
      <button
        onClick={() => setMode(mode === FILTER_SAVE_MODES.SAVE ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.SAVE)}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all ${
          mode === FILTER_SAVE_MODES.SAVE 
            ? 'bg-green-500/30 text-green-300 border border-green-400 shadow-lg shadow-green-500/20' 
            : 'bg-[#1a1a1a] text-green-400 border border-green-500/40 hover:bg-green-500/10'
        }`}
        title="Save NEW filtered results as they come (prevents loss when tick limit reached)"
      >
        <Archive className={iconSize} />
        Save
        {savedCount > 0 && <span className="ml-0.5 text-[7px] opacity-80">({savedCount})</span>}
      </button>
      
      {/* Show All Toggle */}
      <button
        onClick={() => setMode(mode === FILTER_SAVE_MODES.SHOW_ALL ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.SHOW_ALL)}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all ${
          mode === FILTER_SAVE_MODES.SHOW_ALL 
            ? 'bg-blue-500/30 text-blue-300 border border-blue-400 shadow-lg shadow-blue-500/20' 
            : 'bg-[#1a1a1a] text-blue-400 border border-blue-500/40 hover:bg-blue-500/10'
        }`}
        title="Show all saved + current filtered results"
      >
        <Eye className={iconSize} />
        Show All
      </button>
      
      {/* Hide Toggle */}
      <button
        onClick={() => setMode(mode === FILTER_SAVE_MODES.HIDE ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.HIDE)}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all ${
          mode === FILTER_SAVE_MODES.HIDE 
            ? 'bg-orange-500/30 text-orange-300 border border-orange-400 shadow-lg shadow-orange-500/20' 
            : 'bg-[#1a1a1a] text-orange-400 border border-orange-500/40 hover:bg-orange-500/10'
        }`}
        title="Hide all current results, only show NEW matches"
      >
        <EyeOff className={iconSize} />
        Hide
      </button>
      
      {/* Separator */}
      <div className="w-px h-4 bg-[#333] mx-0.5" />
      
      {/* Download Filtered (current visible) - NEW BUTTON */}
      {filteredCount > 0 && onDownloadFiltered && (
        <button
          type="button"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log('[CSV Filtered] Clicked - downloading visible filtered ticks');
            onDownloadFiltered();
          }}
          className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all cursor-pointer bg-emerald-500/20 text-emerald-300 border border-emerald-400 hover:bg-emerald-500/30`}
          title={`Download ${filteredCount} currently visible filtered ticks`}
        >
          <Download className={iconSize} />
          Visible ({filteredCount})
        </button>
      )}
      
      {/* Download Saved CSV Button */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('[CSV Saved] Clicked!');
          if (typeof onDownload === 'function') {
            onDownload();
          } else {
            console.error('[CSV Button] onDownload is not a function:', onDownload);
            window.alert('Error: Download function not available');
          }
        }}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all cursor-pointer ${
          savedCount > 0 
            ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400 hover:bg-cyan-500/30' 
            : 'bg-[#1a1a1a] text-cyan-400 border border-cyan-500/40 hover:bg-cyan-500/10'
        }`}
        title={savedCount > 0 ? `Download ${savedCount} SAVED ticks (from Save mode)` : 'Download saved ticks (enable Save mode first to record new ticks)'}
      >
        <Download className={iconSize} />
        Saved {savedCount > 0 && `(${savedCount})`}
      </button>
      
      {/* Auto-Download Toggle */}
      <button
        onClick={() => setAutoDownload(!autoDownload)}
        className={`${buttonSize} rounded flex items-center gap-0.5 font-medium transition-all ${
          autoDownload 
            ? 'bg-purple-500/30 text-purple-300 border border-purple-400 shadow-lg shadow-purple-500/20' 
            : 'bg-[#1a1a1a] text-purple-400 border border-purple-500/40 hover:bg-purple-500/10'
        }`}
        title="Auto-download CSV at market close (3:31 PM NSE / 11:55 PM MCX) then clear data"
      >
        <Clock className={iconSize} />
        Auto
      </button>
      
      {/* Clear Saved Button */}
      {savedCount > 0 && (
        <button
          onClick={onClear}
          className={`${buttonSize} rounded flex items-center gap-0.5 font-medium bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all`}
          title="Clear all saved ticks"
        >
          <X className={iconSize} />
        </button>
      )}
    </div>
  );
};

// GLOBAL Filter Save Mode Toggle - Controls ALL tables in Multi/Grid views
const GlobalFilterSaveModeToggle = ({ 
  mode, setMode, savedCount, onDownload, onClear, 
  autoDownload, setAutoDownload 
}) => {
  return (
    <div className="flex items-center gap-1.5 px-3 py-2 bg-gradient-to-r from-indigo-900/30 via-purple-900/20 to-indigo-900/30 rounded-lg border border-indigo-500/30">
      <Filter className="w-4 h-4 text-indigo-400" />
      <span className="text-[10px] text-indigo-300 font-bold uppercase tracking-wider mr-1">Filtered Result:</span>
      
      {/* Save Toggle */}
      <button
        onClick={() => setMode(mode === FILTER_SAVE_MODES.SAVE ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.SAVE)}
        className={`h-7 px-2.5 text-[10px] rounded flex items-center gap-1 font-medium transition-all ${
          mode === FILTER_SAVE_MODES.SAVE 
            ? 'bg-green-500/30 text-green-300 border border-green-400 shadow-lg shadow-green-500/20' 
            : 'bg-[#1a1a1a] text-green-400 border border-green-500/40 hover:bg-green-500/10'
        }`}
        title="Save ALL filtered results from all tables"
      >
        <Archive className="w-3.5 h-3.5" />
        Save All
        {savedCount > 0 && <span className="ml-1 text-[9px] px-1.5 py-0.5 rounded bg-green-900/50">({savedCount.toLocaleString()})</span>}
      </button>
      
      {/* Show All Toggle */}
      <button
        onClick={() => setMode(mode === FILTER_SAVE_MODES.SHOW_ALL ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.SHOW_ALL)}
        className={`h-7 px-2.5 text-[10px] rounded flex items-center gap-1 font-medium transition-all ${
          mode === FILTER_SAVE_MODES.SHOW_ALL 
            ? 'bg-blue-500/30 text-blue-300 border border-blue-400 shadow-lg shadow-blue-500/20' 
            : 'bg-[#1a1a1a] text-blue-400 border border-blue-500/40 hover:bg-blue-500/10'
        }`}
        title="Show all saved results in all tables"
      >
        <Eye className="w-3.5 h-3.5" />
        Show All
      </button>
      
      {/* Hide Toggle */}
      <button
        onClick={() => setMode(mode === FILTER_SAVE_MODES.HIDE ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.HIDE)}
        className={`h-7 px-2.5 text-[10px] rounded flex items-center gap-1 font-medium transition-all ${
          mode === FILTER_SAVE_MODES.HIDE 
            ? 'bg-orange-500/30 text-orange-300 border border-orange-400 shadow-lg shadow-orange-500/20' 
            : 'bg-[#1a1a1a] text-orange-400 border border-orange-500/40 hover:bg-orange-500/10'
        }`}
        title="Hide all tables, only show NEW matches"
      >
        <EyeOff className="w-3.5 h-3.5" />
        Hide All
      </button>
      
      {/* Separator */}
      <div className="w-px h-6 bg-indigo-500/30 mx-1" />
      
      {/* Download CSV Button - Always clickable */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          console.log('[Global CSV Button] Clicked!');
          if (typeof onDownload === 'function') {
            onDownload();
          } else {
            console.error('[Global CSV Button] onDownload is not a function:', onDownload);
            window.alert('Error: Download function not available');
          }
        }}
        className={`h-7 px-2.5 text-[10px] rounded flex items-center gap-1 font-medium transition-all cursor-pointer ${
          savedCount > 0 
            ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-400 hover:bg-cyan-500/30' 
            : 'bg-[#1a1a1a] text-cyan-400 border border-cyan-500/40 hover:bg-cyan-500/10'
        }`}
        title={savedCount > 0 ? `Download ALL ${savedCount} saved ticks as CSV` : 'Download all saved ticks (enable Save All first)'}
      >
        <Download className="w-3.5 h-3.5" />
        CSV {savedCount > 0 && `(${savedCount.toLocaleString()})`}
      </button>
      
      {/* Auto-Download Toggle */}
      <button
        onClick={() => setAutoDownload(!autoDownload)}
        className={`h-7 px-2.5 text-[10px] rounded flex items-center gap-1 font-medium transition-all ${
          autoDownload 
            ? 'bg-purple-500/30 text-purple-300 border border-purple-400 shadow-lg shadow-purple-500/20' 
            : 'bg-[#1a1a1a] text-purple-400 border border-purple-500/40 hover:bg-purple-500/10'
        }`}
        title="Auto-download CSV at market close (3:31 PM) then clear"
      >
        <Clock className="w-3.5 h-3.5" />
        Auto
      </button>
      
      {/* Clear Button */}
      {savedCount > 0 && (
        <button
          onClick={onClear}
          className="h-7 px-2 text-[10px] rounded flex items-center gap-1 font-medium bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all"
          title="Clear ALL saved ticks"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      
      {/* Status indicators */}
      {mode === FILTER_SAVE_MODES.SAVE && (
        <span className="text-[9px] text-green-400 animate-pulse ml-1">● Recording all tables</span>
      )}
      {mode === FILTER_SAVE_MODES.HIDE && (
        <span className="text-[9px] text-orange-400 ml-1">● Waiting for new data</span>
      )}
      {autoDownload && (
        <span className="text-[9px] text-purple-400 ml-1">● Auto @3:31PM</span>
      )}
    </div>
  );
};

// Custom hook for filter save mode functionality with localStorage persistence
// Now accepts global mode props to sync with global controls
const useFilterSaveMode = (storageKey, symbol, visibleColumns = null, columnOrder = null, exchange = 'NSE', globalMode = null, globalHideTimestamp = null, onAddToGlobal = null, globalAlertEnabled = false) => {
  // Filter save mode state - LOCAL
  const [localFilterSaveMode, setLocalFilterSaveMode] = useState(() => {
    try {
      const saved = localStorage.getItem(`filterSaveMode_${storageKey}`);
      return saved || FILTER_SAVE_MODES.OFF;
    } catch { return FILTER_SAVE_MODES.OFF; }
  });
  
  // Track if user has overridden global mode for this table
  const [localOverride, setLocalOverride] = useState(false);
  
  // EFFECTIVE mode logic:
  // 1. If local override is active, use local mode
  // 2. If global mode is active and no local override, use global mode
  // 3. Otherwise use local mode
  const isGlobalActive = globalMode && globalMode !== FILTER_SAVE_MODES.OFF;
  const filterSaveMode = (isGlobalActive && !localOverride) ? globalMode : localFilterSaveMode;
  
  // Custom setter that also sets local override when user clicks a button
  const setFilterSaveMode = useCallback((newMode) => {
    setLocalFilterSaveMode(newMode);
    // When user explicitly sets a mode (even OFF), enable local override if global is active
    if (isGlobalActive) {
      setLocalOverride(true);
    }
  }, [isGlobalActive]);
  
  // Reset local override when global mode changes to OFF
  useEffect(() => {
    if (!isGlobalActive) {
      setLocalOverride(false);
    }
  }, [isGlobalActive]);
  
  // Auto-download toggle state
  const [autoDownload, setAutoDownload] = useState(() => {
    try {
      return localStorage.getItem(`autoDownload_${storageKey}`) === 'true';
    } catch { return false; }
  });
  
  // Saved filtered ticks - Load from localStorage on mount
  const [savedTicks, setSavedTicks] = useState(() => {
    try {
      const saved = localStorage.getItem(`savedTicks_${storageKey}`);
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const savedTickIdsRef = useRef(new Set(savedTicks.map(t => getTickUniqueId(t))));
  
  // Alert enabled state
  const [alertEnabled, setAlertEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem(`alertEnabled_${storageKey}`);
      return saved === 'true';
    } catch { return false; }
  });
  
  // Track last alerted tick to avoid duplicate alerts
  const lastAlertedTickRef = useRef(null);
  const alertCooldownRef = useRef(false);
  
  // Hide mode timestamp - only show ticks after this time
  const hideTimestampRef = useRef(null);
  
  // Track if auto-download already happened today
  const autoDownloadDoneRef = useRef(() => {
    try {
      const lastDownload = localStorage.getItem(`lastAutoDownload_${storageKey}`);
      if (lastDownload) {
        const today = new Date().toISOString().split('T')[0];
        return lastDownload === today;
      }
    } catch {}
    return false;
  });
  
  // Save mode to localStorage
  useEffect(() => {
    localStorage.setItem(`filterSaveMode_${storageKey}`, filterSaveMode);
    
    // Set hide timestamp when entering hide mode
    if (filterSaveMode === FILTER_SAVE_MODES.HIDE) {
      hideTimestampRef.current = Date.now();
    }
  }, [filterSaveMode, storageKey]);
  
  // Save auto-download setting to localStorage
  useEffect(() => {
    localStorage.setItem(`autoDownload_${storageKey}`, autoDownload.toString());
  }, [autoDownload, storageKey]);
  
  // Save alert setting to localStorage
  useEffect(() => {
    localStorage.setItem(`alertEnabled_${storageKey}`, alertEnabled.toString());
  }, [alertEnabled, storageKey]);
  
  // Show notification for new filtered tick
  // Only triggers if EITHER local OR global alert is enabled
  const showTickAlert = useCallback((tick) => {
    // Check if alert is enabled (local OR global)
    const isAlertActive = alertEnabled || globalAlertEnabled;
    
    if (!isAlertActive || alertCooldownRef.current) {
      return;
    }
    
    const tickId = getTickUniqueId(tick);
    if (lastAlertedTickRef.current === tickId) return;
    
    lastAlertedTickRef.current = tickId;
    alertCooldownRef.current = true;
    
    // Create notification
    const price = tick.last_price?.toFixed(2) || 'N/A';
    const ltq = tick.last_traded_quantity || 'N/A';
    
    // Try browser notification first
    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification(`${symbol} - New Tick`, {
        body: `LTP: ₹${price} | LTQ: ${ltq}`,
        icon: '/favicon.ico',
        tag: `tick-${symbol}`,
        renotify: true
      });
    } else if ('Notification' in window && Notification.permission !== 'denied') {
      Notification.requestPermission();
    }
    
    // Play alert sound
    playAlertSound('chime');
    
    // Console log
    console.log(`[Alert] ${symbol}: New filtered tick - LTP: ${price}, LTQ: ${ltq}`);
    
    // Reset cooldown after 2 seconds to avoid spam
    setTimeout(() => {
      alertCooldownRef.current = false;
    }, 2000);
  }, [alertEnabled, globalAlertEnabled, symbol]);
  
  // Save ticks to localStorage when they change (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      try {
        // Only save last 10000 ticks to localStorage to avoid quota issues
        const ticksToSave = savedTicks.slice(0, 10000);
        localStorage.setItem(`savedTicks_${storageKey}`, JSON.stringify(ticksToSave));
      } catch (e) {
        console.warn('Could not save ticks to localStorage:', e);
      }
    }, 1000);
    
    return () => clearTimeout(timeoutId);
  }, [savedTicks, storageKey]);
  
  // Auto-download at market close
  useEffect(() => {
    if (!autoDownload || savedTicks.length === 0) return;
    
    const checkMarketClose = () => {
      const now = new Date();
      const istOffset = 5.5 * 60; // IST is UTC+5:30
      const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
      const istMinutes = utcMinutes + istOffset;
      const istHour = Math.floor((istMinutes % (24 * 60)) / 60);
      const istMinute = Math.floor(istMinutes % 60);
      
      const today = new Date().toISOString().split('T')[0];
      const alreadyDownloaded = localStorage.getItem(`lastAutoDownload_${storageKey}`) === today;
      
      if (alreadyDownloaded) return;
      
      // Check NSE close time (3:31 PM)
      const nseClose = MARKET_CLOSE_TIMES.NSE;
      if ((exchange === 'NSE' || exchange === 'NFO' || exchange === 'BSE') && 
          istHour === nseClose.hour && istMinute === nseClose.minute) {
        console.log(`[FilterSaveMode] Auto-downloading CSV at NSE market close for ${symbol}`);
        downloadTicksAsCSV(savedTicks, symbol, visibleColumns, columnOrder);
        localStorage.setItem(`lastAutoDownload_${storageKey}`, today);
        // Clear saved ticks after download
        setSavedTicks([]);
        savedTickIdsRef.current.clear();
        return;
      }
      
      // Check MCX close time (11:55 PM)
      const mcxClose = MARKET_CLOSE_TIMES.MCX;
      if (exchange === 'MCX' && istHour === mcxClose.hour && istMinute === mcxClose.minute) {
        console.log(`[FilterSaveMode] Auto-downloading CSV at MCX market close for ${symbol}`);
        downloadTicksAsCSV(savedTicks, symbol, visibleColumns, columnOrder);
        localStorage.setItem(`lastAutoDownload_${storageKey}`, today);
        // Clear saved ticks after download
        setSavedTicks([]);
        savedTickIdsRef.current.clear();
      }
    };
    
    // Check every minute
    const interval = setInterval(checkMarketClose, 60000);
    // Also check immediately
    checkMarketClose();
    
    return () => clearInterval(interval);
  }, [autoDownload, savedTicks, symbol, storageKey, visibleColumns, columnOrder, exchange]);
  
  // Process filtered ticks based on mode
  const processFilteredTicks = useCallback((filteredTicks) => {
    // If save mode is ON (local OR global), save new filtered ticks
    if (filterSaveMode === FILTER_SAVE_MODES.SAVE) {
      const newTicks = [];
      filteredTicks.forEach(tick => {
        const tickId = getTickUniqueId(tick);
        if (!savedTickIdsRef.current.has(tickId)) {
          savedTickIdsRef.current.add(tickId);
          newTicks.push({ ...tick, _savedAt: Date.now() });
        }
      });
      
      if (newTicks.length > 0) {
        // Show alert for first new tick (only if local OR global alert is enabled)
        const isAlertActive = alertEnabled || globalAlertEnabled;
        if (isAlertActive && newTicks.length > 0) {
          showTickAlert(newTicks[0]);
        }
        
        // Save to LOCAL storage
        setSavedTicks(prev => {
          const updated = [...newTicks, ...prev];
          // Trim to max limit
          if (updated.length > MAX_SAVED_TICKS) {
            const removed = updated.splice(MAX_SAVED_TICKS);
            removed.forEach(t => savedTickIdsRef.current.delete(getTickUniqueId(t)));
          }
          return updated;
        });
        
        // Also add to GLOBAL storage if global mode is active
        if (globalMode === FILTER_SAVE_MODES.SAVE && onAddToGlobal) {
          onAddToGlobal(newTicks, symbol);
        }
      }
    }
    
    // Also check for alerts even if not in save mode - alert on any new filtered tick
    // Only if local OR global alert is enabled
    const isAlertActive = alertEnabled || globalAlertEnabled;
    if (isAlertActive && filterSaveMode !== FILTER_SAVE_MODES.SAVE && filteredTicks.length > 0) {
      const latestTick = filteredTicks[0];
      const tickId = getTickUniqueId(latestTick);
      if (lastAlertedTickRef.current !== tickId) {
        showTickAlert(latestTick);
      }
    }
    
    // Determine what to display based on mode
    switch (filterSaveMode) {
      case FILTER_SAVE_MODES.SHOW_ALL: {
        // Merge saved ticks with current filtered ticks (deduplicated)
        const mergedIds = new Set();
        const merged = [];
        
        // Add current filtered first (newer)
        filteredTicks.forEach(tick => {
          const id = getTickUniqueId(tick);
          if (!mergedIds.has(id)) {
            mergedIds.add(id);
            merged.push(tick);
          }
        });
        
        // Add saved ticks that aren't already in current
        savedTicks.forEach(tick => {
          const id = getTickUniqueId(tick);
          if (!mergedIds.has(id)) {
            mergedIds.add(id);
            merged.push(tick);
          }
        });
        
        // Sort by timestamp (newest first)
        merged.sort((a, b) => {
          const tsA = a.timestamp_ms || a.exchange_timestamp || 0;
          const tsB = b.timestamp_ms || b.exchange_timestamp || 0;
          return tsB - tsA;
        });
        
        return merged;
      }
      
      case FILTER_SAVE_MODES.HIDE: {
        // Use global hide timestamp if global mode is active, otherwise local
        const effectiveHideTimestamp = (globalMode === FILTER_SAVE_MODES.HIDE && globalHideTimestamp) 
          ? globalHideTimestamp 
          : hideTimestampRef.current;
        
        if (!effectiveHideTimestamp) return [];
        return filteredTicks.filter(tick => {
          const tickTs = tick.timestamp_ms || tick.exchange_timestamp || 0;
          return tickTs > effectiveHideTimestamp;
        });
      }
      
      default:
        // OFF or SAVE mode - just return filtered ticks as-is
        return filteredTicks;
    }
  }, [filterSaveMode, savedTicks, globalMode, globalHideTimestamp, onAddToGlobal, symbol]);
  
  // Download saved ticks as CSV (uses visible columns)
  const downloadSavedTicks = useCallback(() => {
    console.log('[CSV Download] Attempting download...', {
      savedTicksCount: savedTicks.length,
      symbol,
      visibleColumns,
      columnOrder
    });
    downloadTicksAsCSV(savedTicks, symbol, visibleColumns, columnOrder);
  }, [savedTicks, symbol, visibleColumns, columnOrder]);
  
  // Clear saved ticks
  const clearSavedTicks = useCallback(() => {
    setSavedTicks([]);
    savedTickIdsRef.current.clear();
    hideTimestampRef.current = null;
    try {
      localStorage.removeItem(`savedTicks_${storageKey}`);
    } catch {}
  }, [storageKey]);
  
  return {
    filterSaveMode,
    setFilterSaveMode,
    savedTicksCount: savedTicks.length,
    processFilteredTicks,
    downloadSavedTicks,
    clearSavedTicks,
    autoDownload,
    setAutoDownload,
    alertEnabled,
    setAlertEnabled,
    isGlobalModeActive: isGlobalActive && !localOverride,
    hasLocalOverride: localOverride,
  };
};

// Pre-calculate ltp_diff for an array of ticks (sorted newest first)
// This runs ONCE when ticks are processed, not on every render
const preCalculateLtpDiff = (ticks) => {
  if (!ticks || ticks.length === 0) return ticks;
  
  for (let i = 0; i < ticks.length; i++) {
    if (i === ticks.length - 1) {
      // Last tick (oldest) has no previous tick
      ticks[i].ltp_diff = 0;
    } else {
      const currentLtp = ticks[i].last_price || 0;
      const prevLtp = ticks[i + 1].last_price || 0;
      ticks[i].ltp_diff = currentLtp - prevLtp;
    }
  }
  return ticks;
};

// Column Settings with Reordering (Up/Down)
const ColumnSettings = ({ visibleCols, setVisibleCols, columnOrder, setColumnOrder }) => {
  const [open, setOpen] = useState(false);
  const [isPending, startTransition] = useTransition();
  
  const toggle = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    // Use startTransition for non-blocking UI update
    startTransition(() => {
      setVisibleCols(prev => {
        const newCols = prev.includes(key) ? prev.filter(c => c !== key) : [...prev, key];
        localStorage.setItem('ticklog_cols', JSON.stringify(newCols));
        return newCols;
      });
    });
  };
  
  const moveUp = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    startTransition(() => {
      setColumnOrder(prev => {
        const idx = prev.indexOf(key);
        if (idx > 0) {
          const newOrder = [...prev];
          [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
          localStorage.setItem('ticklog_order', JSON.stringify(newOrder));
          return newOrder;
        }
        return prev;
      });
    });
  };
  
  const moveDown = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    startTransition(() => {
      setColumnOrder(prev => {
        const idx = prev.indexOf(key);
        if (idx < prev.length - 1) {
          const newOrder = [...prev];
          [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
          localStorage.setItem('ticklog_order', JSON.stringify(newOrder));
          return newOrder;
        }
        return prev;
      });
    });
  };
  
  // Order columns by columnOrder
  const orderedCols = columnOrder.map(key => COLUMNS.find(c => c.key === key)).filter(Boolean);
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 px-2 bg-[#111] border-[#27272A] text-white text-[11px]">
          <Settings2 className="w-3 h-3 mr-1" />Columns ({visibleCols.length})
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-[#111] border-[#27272A] w-48" align="end">
        <DropdownMenuLabel className="text-[#71717A] text-[11px]">Show & Reorder Columns</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#27272A]" />
        {orderedCols.map((col, idx) => (
          <div key={col.key} className="flex items-center gap-1 px-2 py-1 hover:bg-[#27272A]">
            <div 
              className={`w-4 h-4 border rounded flex items-center justify-center cursor-pointer ${visibleCols.includes(col.key) ? 'bg-blue-600 border-blue-600' : 'border-[#52525B]'}`}
              onClick={(e) => toggle(col.key, e)}
            >
              {visibleCols.includes(col.key) && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>}
            </div>
            <span className="text-white text-[11px] flex-1">{col.label}</span>
            <button onClick={(e) => moveUp(col.key, e)} className="text-[#71717A] hover:text-white px-1" disabled={idx === 0}>▲</button>
            <button onClick={(e) => moveDown(col.key, e)} className="text-[#71717A] hover:text-white px-1" disabled={idx === orderedCols.length - 1}>▼</button>
          </div>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// LIGHT MODE Column Selector - Select specific columns for light mode with sorting
const LightweightColumnSelector = ({ lightweightCols, setLightweightCols }) => {
  const [open, setOpen] = useState(false);
  
  const toggle = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    setLightweightCols(prev => {
      let newCols;
      if (prev.includes(key)) {
        // Don't allow removing if it's the last column
        if (prev.length <= 1) return prev;
        newCols = prev.filter(c => c !== key);
      } else {
        newCols = [...prev, key];
      }
      localStorage.setItem('ticklog_lightweight_cols', JSON.stringify(newCols));
      return newCols;
    });
  };
  
  // Move column up in order
  const moveUp = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    setLightweightCols(prev => {
      const idx = prev.indexOf(key);
      if (idx > 0) {
        const newOrder = [...prev];
        [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
        localStorage.setItem('ticklog_lightweight_cols', JSON.stringify(newOrder));
        return newOrder;
      }
      return prev;
    });
  };
  
  // Move column down in order
  const moveDown = (key, e) => {
    e.preventDefault();
    e.stopPropagation();
    setLightweightCols(prev => {
      const idx = prev.indexOf(key);
      if (idx >= 0 && idx < prev.length - 1) {
        const newOrder = [...prev];
        [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
        localStorage.setItem('ticklog_lightweight_cols', JSON.stringify(newOrder));
        return newOrder;
      }
      return prev;
    });
  };
  
  // Quick presets
  const applyPreset = (preset, e) => {
    e.preventDefault();
    e.stopPropagation();
    let cols;
    switch(preset) {
      case 'ltq_time': cols = ['ltq', 'time']; break;
      case 'ltp_ltq_time': cols = ['ltp', 'ltq', 'time']; break;
      case 'volume': cols = ['ltq', 'vol_chg', 'volume', 'time']; break;
      case 'aggressor': cols = ['ltq', 'agg_buy', 'agg_sell', 'time']; break;
      case 'oi': cols = ['ltp', 'oi', 'oi_change', 'time']; break;
      default: cols = ['ltq', 'time'];
    }
    setLightweightCols(cols);
    localStorage.setItem('ticklog_lightweight_cols', JSON.stringify(cols));
  };
  
  // Get ordered columns - selected first, then unselected
  const selectedCols = lightweightCols.map(key => COLUMNS.find(c => c.key === key)).filter(Boolean);
  const unselectedCols = COLUMNS.filter(col => !lightweightCols.includes(col.key));
  
  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="h-7 px-2 bg-[#111] border-yellow-500/50 text-yellow-400 text-[10px] hover:bg-yellow-900/20">
          <Settings2 className="w-3 h-3 mr-1" />Light Cols ({lightweightCols.length})
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="bg-[#111] border-[#27272A] w-64" align="end">
        <DropdownMenuLabel className="text-yellow-400 text-[11px]">Light Mode Columns</DropdownMenuLabel>
        <DropdownMenuSeparator className="bg-[#27272A]" />
        
        {/* Quick Presets */}
        <div className="px-2 py-1.5 border-b border-[#27272A]">
          <span className="text-[9px] text-[#71717A] uppercase tracking-wider">Quick Presets</span>
          <div className="flex flex-wrap gap-1 mt-1">
            <button onClick={(e) => applyPreset('ltq_time', e)} className="px-1.5 py-0.5 text-[9px] bg-blue-900/30 text-blue-400 rounded hover:bg-blue-900/50">LTQ+Time</button>
            <button onClick={(e) => applyPreset('ltp_ltq_time', e)} className="px-1.5 py-0.5 text-[9px] bg-green-900/30 text-green-400 rounded hover:bg-green-900/50">LTP+LTQ+Time</button>
            <button onClick={(e) => applyPreset('aggressor', e)} className="px-1.5 py-0.5 text-[9px] bg-red-900/30 text-red-400 rounded hover:bg-red-900/50">Aggressor</button>
            <button onClick={(e) => applyPreset('volume', e)} className="px-1.5 py-0.5 text-[9px] bg-purple-900/30 text-purple-400 rounded hover:bg-purple-900/50">Volume</button>
            <button onClick={(e) => applyPreset('oi', e)} className="px-1.5 py-0.5 text-[9px] bg-orange-900/30 text-orange-400 rounded hover:bg-orange-900/50">OI Track</button>
          </div>
        </div>
        
        {/* Selected Columns with Reordering */}
        {selectedCols.length > 0 && (
          <div className="border-b border-[#27272A]">
            <div className="px-2 py-1 text-[9px] text-green-400 uppercase tracking-wider">Selected ({selectedCols.length})</div>
            <div className="max-h-[150px] overflow-y-auto">
              {selectedCols.map((col, idx) => (
                <div key={col.key} className="flex items-center gap-1 px-2 py-1 hover:bg-[#27272A] bg-yellow-900/10">
                  <div 
                    className="w-4 h-4 border rounded flex items-center justify-center cursor-pointer bg-yellow-600 border-yellow-600"
                    onClick={(e) => toggle(col.key, e)}
                  >
                    <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <span className="text-white text-[11px] flex-1 font-medium">{col.label}</span>
                  <button 
                    onClick={(e) => moveUp(col.key, e)} 
                    className={`text-[#71717A] hover:text-white px-1 text-[10px] ${idx === 0 ? 'opacity-30' : ''}`}
                    disabled={idx === 0}
                  >▲</button>
                  <button 
                    onClick={(e) => moveDown(col.key, e)} 
                    className={`text-[#71717A] hover:text-white px-1 text-[10px] ${idx === selectedCols.length - 1 ? 'opacity-30' : ''}`}
                    disabled={idx === selectedCols.length - 1}
                  >▼</button>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Unselected Columns */}
        <div>
          <div className="px-2 py-1 text-[9px] text-[#71717A] uppercase tracking-wider">Available</div>
          <div className="max-h-[150px] overflow-y-auto">
            {unselectedCols.map((col) => (
              <div key={col.key} className="flex items-center gap-2 px-2 py-1 hover:bg-[#27272A]">
                <div 
                  className="w-4 h-4 border rounded flex items-center justify-center cursor-pointer border-[#52525B]"
                  onClick={(e) => toggle(col.key, e)}
                />
                <span className="text-[#71717A] text-[11px] flex-1">{col.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        <DropdownMenuSeparator className="bg-[#27272A]" />
        <div className="px-2 py-1.5 text-[9px] text-[#52525B]">
          Fewer columns = More tables, Less CPU
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// ADVANCED Scan Filter with all column filters
// Accepts individual props for compatibility with GridCell and VirtualizedTickTable
const ScanFilter = ({ filter, setFilter, minLtq, setMinLtq, minBidQty, setMinBidQty, minAskQty, setMinAskQty }) => {
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  return (
    <div className="flex items-center gap-2">
      <Select value={filter || 'all'} onValueChange={(v) => setFilter(v)}>
        <SelectTrigger className="h-7 w-28 text-[11px] bg-[#111] border-[#27272A] text-white">
          <SelectValue />
        </SelectTrigger>
        <SelectContent className="bg-[#111] border-[#27272A]">
          <SelectItem value="all">All Ticks</SelectItem>
          <SelectItem value="buy">Buys Only</SelectItem>
          <SelectItem value="sell">Sells Only</SelectItem>
          <SelectItem value="large_ltq">Large LTQ</SelectItem>
          <SelectItem value="large_vol">Large Vol</SelectItem>
          <SelectItem value="oi_up">OI Up</SelectItem>
          <SelectItem value="oi_down">OI Down</SelectItem>
        </SelectContent>
      </Select>
      <Button
        variant={showAdvanced ? "default" : "outline"}
        size="sm"
        onClick={() => setShowAdvanced(!showAdvanced)} 
        className={`h-7 px-2 ${showAdvanced ? 'bg-blue-600 text-white' : 'bg-[#111] border-[#27272A] text-[#71717A] hover:text-white'}`}
        title="Advanced Filters"
      >
        <Filter className="w-3.5 h-3.5 mr-1" />
        <span className="text-[10px]">Filter</span>
      </Button>
      {showAdvanced && (
        <div className="flex items-center gap-2 bg-[#111] border border-[#27272A] rounded-md px-2 py-1">
          <div className="flex items-center gap-1">
            <span className="text-[10px] text-[#71717A]">LTQ≥</span>
            <Input 
              type="number" 
              value={minLtq || ''} 
              onChange={(e) => setMinLtq && setMinLtq(parseInt(e.target.value) || 0)}
              placeholder="0"
              className="h-6 w-16 text-[11px] bg-[#0a0a0a] border-[#27272A] px-1 text-white"
            />
          </div>
          {setMinBidQty && (
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-[#71717A]">Bid≥</span>
              <Input 
                type="number" 
                value={minBidQty || ''} 
                onChange={(e) => setMinBidQty(parseInt(e.target.value) || 0)}
                placeholder="0"
                className="h-6 w-16 text-[11px] bg-[#0a0a0a] border-[#27272A] px-1 text-white"
              />
            </div>
          )}
          {setMinAskQty && (
            <div className="flex items-center gap-1">
              <span className="text-[10px] text-[#71717A]">Ask≥</span>
              <Input 
                type="number" 
                value={minAskQty || ''} 
                onChange={(e) => setMinAskQty(parseInt(e.target.value) || 0)}
                placeholder="0"
                className="h-6 w-16 text-[11px] bg-[#0a0a0a] border-[#27272A] px-1 text-white"
              />
            </div>
          )}
          <Button 
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilter('all');
              setMinLtq && setMinLtq(0);
              setMinVol && setMinVol(0);
              setMinBidQty && setMinBidQty(0);
              setMinAskQty && setMinAskQty(0);
            }}
            className="h-6 px-2 text-[10px] text-red-400 hover:text-red-300 hover:bg-red-900/20"
          >
            Clear
          </Button>
        </div>
      )}
    </div>
  );
};

// Get unique tick ID - uses tick_id from backend (includes timestamp + ltq + ltp)
// This ensures ticks with same timestamp but different data are NOT deduplicated
const getTickUniqueId = (tick) => {
  // Prefer tick_id from backend (most accurate)
  if (tick.tick_id) return tick.tick_id;
  
  // Fallback: create ID from available data
  const token = tick.instrument_token || tick.token || 0;
  const ts = tick.timestamp_ms || new Date(tick.timestamp).getTime() || 0;
  const ltq = tick.last_traded_quantity || tick.ltq || 0;
  const ltp = tick.last_price || tick.ltp || 0;
  return `${token}_${ts}_${ltq}_${ltp}`;
};

// Deduplicate and sort ticks - PRESERVES ALL UNIQUE TICKS from Zerodha
// NO deduplication of ticks with different ltq/ltp even if same timestamp
const deduplicateAndSortTicks = (ticks, shouldSort = true) => {
  if (!ticks || ticks.length === 0) return [];
  if (ticks.length === 1) return ticks;
  
  // Use a Map to deduplicate by tick_id (includes ltq+ltp, so different trades are kept)
  const tickMap = new Map();
  for (let i = 0; i < ticks.length; i++) {
    const tick = ticks[i];
    const id = getTickUniqueId(tick);
    if (!tickMap.has(id)) {
      tickMap.set(id, tick);
    }
  }
  
  const result = Array.from(tickMap.values());
  
  // Only sort if requested (default: sort by time, newest first)
  if (shouldSort) {
    result.sort((a, b) => {
      const tsA = a.timestamp_ms || 0;
      const tsB = b.timestamp_ms || 0;
      return tsB - tsA; // Descending (newest first)
    });
  }
  
  // NOTE: ltp_diff is calculated lazily in renderCell (not here) to avoid blocking tick processing
  
  return result;
};

// Apply filters function - used by all views (accepts individual filter params)
// OPTIMIZED for large datasets (200k+ ticks)
const applyFilters = (ticks, filter, minLtq, minBidQty, minAskQty, maxVals, minVolChg = 0, spreadLessThan = null, spreadGreaterThan = null, bidQtyEnabled = false, askQtyEnabled = false, bidAskQtyMode = 'AND', posLessThan = null, posGreaterThan = null, posFilterMode = 'OR', ltpDiffLessThan = null, ltpDiffGreaterThan = null, ltpDiffFilterMode = 'OR') => {
  if (!ticks || ticks.length === 0) return [];
  
  // PERFORMANCE: Limit processing to prevent UI hang
  const MAX_PROCESS = 10000;
  let ticksToProcess = ticks.length > MAX_PROCESS ? ticks.slice(0, MAX_PROCESS) : ticks;
  
  // CRITICAL: Pre-calculate ltp_diff BEFORE filtering if LTP Diff filter is enabled
  // This ensures ltp_diff values are available for filtering
  const hasLtpDiffFilter = (ltpDiffLessThan !== null && ltpDiffLessThan !== '') || 
                           (ltpDiffGreaterThan !== null && ltpDiffGreaterThan !== '');
  if (hasLtpDiffFilter && ticksToProcess.length > 0) {
    for (let i = 0; i < ticksToProcess.length; i++) {
      if (ticksToProcess[i].ltp_diff === undefined) {
        if (i === ticksToProcess.length - 1) {
          ticksToProcess[i].ltp_diff = 0;
        } else {
          const currentLtp = ticksToProcess[i].last_price || 0;
          const prevLtp = ticksToProcess[i + 1].last_price || 0;
          ticksToProcess[i].ltp_diff = currentLtp - prevLtp;
        }
      }
    }
  }
  
  const type = filter || 'all';
  
  // Check market status - skip market hours filtering when market is closed
  const marketStatus = getCurrentMarketStatus();
  
  let result;
  if (marketStatus.isOpen) {
    // CRITICAL: Filter out ticks that are outside market hours
    // This ensures NO ticks from after market close are shown during live trading
    // Also filter out dummy ticks with 0 bid/ask prices
    result = ticksToProcess.filter(t => {
      // Get timestamp - prefer timestamp_ms, fallback to parsing timestamp string
      let ts = t.timestamp_ms;
      if (!ts && t.timestamp) {
        try {
          ts = new Date(t.timestamp).getTime();
        } catch (e) {
          return false;
        }
      }
      if (!ts) return false;
      
      // Filter out dummy ticks with 0 bid/ask prices
      if (!t.bid_price || t.bid_price === 0 || !t.ask_price || t.ask_price === 0) {
        return false;
      }
      
      // Use isWithinMarketHours - it will check against exchange-specific hours
      const exchange = t.exchange || 'NSE';
      return isWithinMarketHours(ts, exchange);
    });
  } else {
    // Market closed - show all ticks (historical view mode)
    // Filter out ticks without valid timestamps AND dummy ticks (0 bid/ask)
    result = ticksToProcess.filter(t => {
      // Check for valid timestamp
      let ts = t.timestamp_ms;
      if (!ts && t.timestamp) {
        try {
          ts = new Date(t.timestamp).getTime();
        } catch (e) {
          return false;
        }
      }
      if (!ts) return false;
      
      // Filter out dummy ticks with 0 bid/ask prices
      if (!t.bid_price || t.bid_price === 0 || !t.ask_price || t.ask_price === 0) {
        return false;
      }
      
      return true;
    });
  }
  
  // Deduplicate and sort (ensures no duplicates even if filtering didn't catch them)
  result = deduplicateAndSortTicks(result);
  
  // Basic filters
  if (type === 'buy') result = result.filter(t => t.agg_buy > 0);
  else if (type === 'sell') result = result.filter(t => t.agg_sell > 0);
  else if (type === 'large_ltq') result = result.filter(t => (t.last_traded_quantity || 0) > maxVals.maxLtq * 0.3);
  else if (type === 'large_vol') result = result.filter(t => (t.volume_change || 0) > maxVals.maxVolChg * 0.3);
  else if (type === 'oi_up') result = result.filter(t => (t.oi_change || 0) > 0);
  else if (type === 'oi_down') result = result.filter(t => (t.oi_change || 0) < 0);
  
  // Advanced filters
  if (minLtq > 0) result = result.filter(t => (t.last_traded_quantity || 0) >= minLtq);
  
  // Bid/Ask Qty filters with AND/OR mode
  const hasBidQtyFilter = bidQtyEnabled && minBidQty > 0;
  const hasAskQtyFilter = askQtyEnabled && minAskQty > 0;
  
  if (hasBidQtyFilter || hasAskQtyFilter) {
    result = result.filter(t => {
      const bidQtyMatch = (t.bid_qty || 0) >= minBidQty;
      const askQtyMatch = (t.ask_qty || 0) >= minAskQty;
      
      // Both filters enabled
      if (hasBidQtyFilter && hasAskQtyFilter) {
        return bidAskQtyMode === 'AND' ? (bidQtyMatch && askQtyMatch) : (bidQtyMatch || askQtyMatch);
      }
      // Only Bid Qty enabled
      if (hasBidQtyFilter) {
        return bidQtyMatch;
      }
      // Only Ask Qty enabled
      if (hasAskQtyFilter) {
        return askQtyMatch;
      }
      return true;
    });
  }
  
  // Position % filters with AND/OR mode
  const hasPosLessThan = posLessThan !== null && posLessThan !== '';
  const hasPosGreaterThan = posGreaterThan !== null && posGreaterThan !== '';
  const posLessVal = parseFloat(posLessThan) || 0;
  const posGreaterVal = parseFloat(posGreaterThan) || 0;
  
  if (hasPosLessThan || hasPosGreaterThan) {
    result = result.filter(t => {
      const tradePos = calculateTradePosition(t);
      if (tradePos === null) return false;
      
      const lessThanMatch = tradePos < posLessVal;
      const greaterThanMatch = tradePos > posGreaterVal;
      
      // Both filters enabled
      if (hasPosLessThan && hasPosGreaterThan) {
        return posFilterMode === 'AND' ? (lessThanMatch && greaterThanMatch) : (lessThanMatch || greaterThanMatch);
      }
      // Only less than enabled
      if (hasPosLessThan) {
        return lessThanMatch;
      }
      // Only greater than enabled
      if (hasPosGreaterThan) {
        return greaterThanMatch;
      }
      return true;
    });
  }
  
  // LTP Diff filters with AND/OR mode (similar to AGG %)
  // Note: ltp_diff is PRE-CALCULATED when ticks are processed (no new objects created here)
  const hasLtpDiffLessThan = ltpDiffLessThan !== null && ltpDiffLessThan !== '';
  const hasLtpDiffGreaterThan = ltpDiffGreaterThan !== null && ltpDiffGreaterThan !== '';
  const ltpDiffLessVal = parseFloat(ltpDiffLessThan) || 0;
  const ltpDiffGreaterVal = parseFloat(ltpDiffGreaterThan) || 0;
  
  if (hasLtpDiffLessThan || hasLtpDiffGreaterThan) {
    result = result.filter(t => {
      // Use pre-calculated ltp_diff (already on tick object)
      const ltpDiff = t.ltp_diff || 0;
      
      const lessThanMatch = ltpDiff < ltpDiffLessVal;
      const greaterThanMatch = ltpDiff > ltpDiffGreaterVal;
      
      // Both filters enabled
      if (hasLtpDiffLessThan && hasLtpDiffGreaterThan) {
        return ltpDiffFilterMode === 'AND' ? (lessThanMatch && greaterThanMatch) : (lessThanMatch || greaterThanMatch);
      }
      // Only less than enabled
      if (hasLtpDiffLessThan) {
        return lessThanMatch;
      }
      // Only greater than enabled
      if (hasLtpDiffGreaterThan) {
        return greaterThanMatch;
      }
      return true;
    });
  }
  
  // Volume Change filter (separate from Vol filter which checks total volume)
  if (minVolChg > 0) result = result.filter(t => Math.abs(t.volume_change || 0) >= minVolChg);
  
  // Spread filters - OR logic when both enabled, single condition when only one enabled
  const hasSpreadLessThan = spreadLessThan !== null && spreadLessThan > 0;
  const hasSpreadGreaterThan = spreadGreaterThan !== null && spreadGreaterThan > 0;
  
  if (hasSpreadLessThan || hasSpreadGreaterThan) {
    result = result.filter(t => {
      const spread = (t.ask_price || 0) - (t.bid_price || 0);
      
      // Both filters enabled - use OR logic (show if spread < X OR spread > Y)
      if (hasSpreadLessThan && hasSpreadGreaterThan) {
        return spread < spreadLessThan || spread > spreadGreaterThan;
      }
      // Only less than enabled
      if (hasSpreadLessThan) {
        return spread < spreadLessThan;
      }
      // Only greater than enabled
      if (hasSpreadGreaterThan) {
        return spread > spreadGreaterThan;
      }
      return true;
    });
  }
  
  return result;
};

// Column widths configuration - minimum widths for each column type
const COLUMN_MIN_WIDTHS = {
  ltp: 95,
  ltp_diff: 75,
  bid_price: 90,
  ask_price: 90,
  bid_qty: 75,
  ask_qty: 75,
  trade_pos: 70,
  spread: 55,
  ltq: 65,
  vol_chg: 70,
  volume: 75,
  oi: 75,
  oi_change: 65,
  time: 85,
};

// Compact column widths - auto-fit to content for more symbols on screen
const COLUMN_COMPACT_WIDTHS = {
  ltp: 75,
  ltp_diff: 60,
  bid_price: 70,
  ask_price: 70,
  bid_qty: 55,
  ask_qty: 55,
  trade_pos: 55,
  spread: 45,
  ltq: 50,
  vol_chg: 55,
  volume: 60,
  oi: 55,
  oi_change: 50,
  time: 65,
};

// Premium Loading Component - Shows in center of table while data is loading
const PremiumLoadingUI = ({ message = "Loading tick data..." }) => {
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0a]/80 backdrop-blur-sm z-50">
      <div className="flex flex-col items-center gap-4 p-8 rounded-xl bg-gradient-to-b from-[#1a1a2e] to-[#16213e] border border-cyan-500/20 shadow-2xl shadow-cyan-500/10">
        {/* Animated Logo/Icon */}
        <div className="relative">
          <div className="w-16 h-16 rounded-full border-4 border-cyan-500/20 border-t-cyan-500 animate-spin"></div>
          <div className="absolute inset-0 flex items-center justify-center">
            <svg className="w-8 h-8 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
            </svg>
          </div>
        </div>
        
        {/* Loading Text */}
        <div className="text-center">
          <p className="text-cyan-400 font-semibold text-sm animate-pulse">{message}</p>
          <p className="text-[#71717A] text-xs mt-1">Fetching market data...</p>
        </div>
        
        {/* Progress Dots */}
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-cyan-500 animate-bounce" style={{ animationDelay: '0ms' }}></div>
          <div className="w-2 h-2 rounded-full bg-cyan-500 animate-bounce" style={{ animationDelay: '150ms' }}></div>
          <div className="w-2 h-2 rounded-full bg-cyan-500 animate-bounce" style={{ animationDelay: '300ms' }}></div>
        </div>
      </div>
    </div>
  );
};

// Calculate dynamic table width based on visible columns and compact mode
const calculateTableWidth = (visibleCols, compact = false) => {
  const widths = compact ? COLUMN_COMPACT_WIDTHS : COLUMN_MIN_WIDTHS;
  const baseWidth = visibleCols.reduce((sum, col) => sum + (widths[col] || (compact ? 50 : 70)), 0);
  return Math.max(baseWidth + (compact ? 20 : 40), compact ? 300 : 400);
};

// Virtualized Table for 100k+ ticks - GRADIENT BARS, GREEN TIME TEXT, AGG COLORS
const VirtualizedTickTable = React.memo(({ ticks, columns, columnOrder, maxVals, filter, minLtq, minBidQty, minAskQty, minVolChg = 0, spreadLessThan = null, spreadGreaterThan = null, bidQtyEnabled = false, askQtyEnabled = false, bidAskQtyMode = 'AND', posLessThan = null, posGreaterThan = null, posFilterMode = 'OR', ltpDiffLessThan = null, ltpDiffGreaterThan = null, ltpDiffFilterMode = 'OR', compact = false, tableId = 'single', smartFilters = DEFAULT_SMART_FILTERS, symbol = '', exchange = 'NSE' }) => {
  const parentRef = useRef(null);
  
  // FILTER SAVE MODE - Save/Show All/Hide functionality
  const {
    filterSaveMode,
    setFilterSaveMode,
    savedTicksCount,
    processFilteredTicks,
    downloadSavedTicks,
    clearSavedTicks,
    autoDownload,
    setAutoDownload,
    alertEnabled,
    setAlertEnabled,
  } = useFilterSaveMode(tableId, symbol, columns, columnOrder, exchange);
  
  // Get default column widths based on compact mode
  const defaultColWidths = compact ? COLUMN_COMPACT_WIDTHS : COLUMN_MIN_WIDTHS;
  
  // Custom column widths - persisted to localStorage
  const [customColWidths, setCustomColWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(`ticklog_colwidths_${tableId}`);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [resizing, setResizing] = useState(null);
  
  // Save column widths to localStorage when they change
  useEffect(() => {
    if (Object.keys(customColWidths).length > 0) {
      localStorage.setItem(`ticklog_colwidths_${tableId}`, JSON.stringify(customColWidths));
    }
  }, [customColWidths, tableId]);
  
  // Get column width - custom if set, otherwise default
  const getColWidth = (key) => customColWidths[key] || defaultColWidths[key] || (compact ? 50 : 60);
  
  // Handle column resize
  const handleResizeStart = (e, colKey) => {
    e.preventDefault();
    setResizing(colKey);
    const startX = e.clientX;
    const startWidth = getColWidth(colKey);
    
    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      setCustomColWidths(prev => ({
        ...prev,
        [colKey]: Math.max(10, startWidth + diff) // No restriction
      }));
    };
    
    const handleMouseUp = () => {
      setResizing(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };
  
  // Merged column widths for compatibility
  const colWidths = { ...defaultColWidths, ...customColWidths };
  
  // Memoize smart filter config to prevent unnecessary recalculations
  const smartFilterConfig = useMemo(() => ({
    enabled: smartFilters.enabled,
    mode: smartFilters.mode,
    levelSweep: smartFilters.levelSweep,
    consecutiveHigh: smartFilters.consecutiveHigh,
    volumeSpike: smartFilters.volumeSpike,
    iceberg: smartFilters.iceberg,
    algoBurst: smartFilters.algoBurst,
  }), [
    smartFilters.enabled, 
    smartFilters.mode,
    smartFilters.levelSweep?.enabled, smartFilters.levelSweep?.ltqNumber, smartFilters.levelSweep?.useBarPercent, smartFilters.levelSweep?.ltqBarPercent, smartFilters.levelSweep?.sweepType,
    smartFilters.consecutiveHigh?.enabled, smartFilters.consecutiveHigh?.ltqNumber, smartFilters.consecutiveHigh?.useBarPercent, smartFilters.consecutiveHigh?.ltqBarPercent, smartFilters.consecutiveHigh?.minTicks,
    smartFilters.volumeSpike?.enabled, smartFilters.volumeSpike?.ltqNumber, smartFilters.volumeSpike?.volNumber,
    smartFilters.iceberg?.enabled, smartFilters.iceberg?.ltqNumber, smartFilters.iceberg?.minTicks, smartFilters.iceberg?.priceTolerance,
    smartFilters.algoBurst?.enabled, smartFilters.algoBurst?.ltqNumber, smartFilters.algoBurst?.minTradesPerSecond,
  ]);
  
  // Apply all filters and smart filters
  const filteredTicks = useMemo(() => {
    // DEBUG: Log input ticks count before filtering
    console.log(`[VirtualizedTickTable] INPUT ticks: ${ticks?.length || 0}, filter: ${filter}`);
    
    // CRITICAL: Pre-calculate ltp_diff BEFORE filtering
    // ltp_diff = difference between current tick's LTP and previous tick's LTP
    if (ticks && ticks.length > 0) {
      for (let i = 0; i < ticks.length; i++) {
        if (i === ticks.length - 1) {
          ticks[i].ltp_diff = 0;
        } else {
          const currentLtp = ticks[i].last_price || 0;
          const prevLtp = ticks[i + 1].last_price || 0;
          ticks[i].ltp_diff = currentLtp - prevLtp;
        }
      }
    }
    
    let result = applyFilters(ticks, filter, minLtq || 0, minBidQty || 0, minAskQty || 0, maxVals, minVolChg || 0, spreadLessThan, spreadGreaterThan, bidQtyEnabled, askQtyEnabled, bidAskQtyMode, posLessThan, posGreaterThan, posFilterMode, ltpDiffLessThan, ltpDiffGreaterThan, ltpDiffFilterMode);
    
    // DEBUG: Log output ticks count after filtering
    console.log(`[VirtualizedTickTable] AFTER applyFilters: ${result?.length || 0}`);
    
    // Apply smart filters
    if (smartFilterConfig.enabled) {
      const smartMaxValues = calculateMaxValues(result);
      // Process ALL ticks - use async worker for large datasets
      const filtered = applySmartFilters(result, smartFilterConfig, smartMaxValues);
      
      // If filter mode, only show matching rows
      if (smartFilterConfig.mode === 'filter') {
        result = filtered.filter(t => t._smartFilterMatch);
        // Recalculate group boundaries for filtered results
        result = result.map((tick, i, arr) => ({
          ...tick,
          _isFirstInGroup: i === 0 || !arr[i - 1]._smartFilterMatch,
          _isLastInGroup: i === arr.length - 1 || !arr[i + 1]._smartFilterMatch,
        }));
      } else {
        // Use the filtered results directly
        result = filtered;
      }
    }
    
    // Apply filter save mode processing (Save/Show All/Hide)
    return processFilteredTicks(result);
  }, [ticks, filter, minLtq, minBidQty, minAskQty, bidQtyEnabled, askQtyEnabled, bidAskQtyMode, posLessThan, posGreaterThan, posFilterMode, ltpDiffLessThan, ltpDiffGreaterThan, ltpDiffFilterMode, minVolChg, spreadLessThan, spreadGreaterThan, maxVals, smartFilterConfig, processFilteredTicks]);
  
  // Download currently visible filtered ticks as CSV
  const downloadFilteredTicks = useCallback(() => {
    if (filteredTicks.length === 0) {
      window.alert('No filtered ticks to download. Apply some filters first!');
      return;
    }
    console.log(`[Download Filtered] Downloading ${filteredTicks.length} visible filtered ticks`);
    downloadTicksAsCSV(filteredTicks, symbol, columns, columnOrder);
  }, [filteredTicks, symbol, columns, columnOrder]);
  
  // Order columns
  const orderedCols = columnOrder.filter(k => columns.includes(k));
  
  // Calculate row height including potential filter header
  const getRowHeight = (index) => {
    const tick = filteredTicks[index];
    const baseHeight = compact ? 24 : 28;
    // Add header height if this is first in a filter group
    if (smartFilters.enabled && tick?._smartFilterMatch && tick?._isFirstInGroup) {
      return baseHeight + (compact ? 20 : 24);
    }
    return baseHeight;
  };
  
  const rowVirtualizer = useVirtualizer({
    count: filteredTicks.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => getRowHeight(index),
    overscan: 30,
  });

  // Render cell with proper colors - LTP shows REVERSED price change color (Red=UP, Green=DOWN)
  // sameSecond parameter for time column light blue text (consecutive same-second ticks)
  const renderCell = (tick, key, prevTick, sameSecond = false) => {
    const fontSize = compact ? 'text-[10px]' : 'text-[12px]';
    const fontSizeLarge = compact ? 'text-[11px]' : 'text-[13px]';
    switch(key) {
      case 'ltp': {
        const priceUp = prevTick && tick.last_price > prevTick.last_price;
        const priceDown = prevTick && tick.last_price < prevTick.last_price;
        // REVERSED: Red for price UP, Green for price DOWN
        const colorClass = priceUp ? 'text-red-400' : priceDown ? 'text-green-400' : 'text-white';
        return <span className={`font-mono ${fontSizeLarge} font-semibold ${colorClass}`}>{formatNumber(tick.last_price)}</span>;
      }
      case 'ltp_diff': {
        // Calculate ltp_diff lazily and cache on tick object (only for visible rows)
        if (tick.ltp_diff === undefined && prevTick) {
          tick.ltp_diff = (tick.last_price || 0) - (prevTick.last_price || 0);
        }
        const ltpDiff = tick.ltp_diff || 0;
        
        if (ltpDiff === 0) return <span className={`text-[#71717A] ${fontSize} font-mono`}>0.00</span>;
        
        const isPositive = ltpDiff > 0;
        const absDiff = Math.abs(ltpDiff);
        const isSignificant = absDiff >= 1;
        const isLarge = absDiff >= 2;
        
        let bgClass = 'bg-transparent';
        let textClass = isPositive ? 'text-green-400' : 'text-red-400';
        let symbol = '';
        
        if (isLarge) {
          bgClass = isPositive ? 'bg-green-600/40' : 'bg-red-600/40';
          textClass = isPositive ? 'text-green-300 font-bold' : 'text-red-300 font-bold';
          symbol = isPositive ? '🔥' : '❄️';
        } else if (isSignificant) {
          bgClass = isPositive ? 'bg-green-600/25' : 'bg-red-600/25';
          textClass = isPositive ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold';
        }
        
        const displayValue = (isPositive ? '+' : '') + ltpDiff.toFixed(2);
        
        if (bgClass !== 'bg-transparent') {
          return (
            <div className={`relative h-5 ${bgClass} rounded-sm overflow-hidden flex items-center justify-center`}>
              <span className={`${textClass} ${fontSize} font-mono`}>
                {symbol}{displayValue}
              </span>
            </div>
          );
        }
        
        return <span className={`${textClass} ${fontSize} font-mono`}>{displayValue}</span>;
      }
      case 'bid_price': return <span className={`text-green-400 font-mono ${fontSize}`}>{formatNumber(tick.bid_price)}</span>;
      case 'ask_price': return <span className={`text-red-400 font-mono ${fontSize}`}>{formatNumber(tick.ask_price)}</span>;
      case 'bid_qty': return <DataBar value={tick.bid_qty} max={maxVals.maxBidQty} color="green" />;
      case 'ask_qty': return <DataBar value={tick.ask_qty} max={maxVals.maxAskQty} color="red" />;
      case 'trade_pos': {
        const tradePos = calculateTradePosition(tick);
        if (tradePos === null) return <span className={`text-[#3f3f46] ${fontSize}`}>-</span>;
        
        // Color and intensity based on position
        // <20% = seller aggressive (red), >80% = buyer aggressive (green)
        // >100% or <0% = extreme (show fire/ice symbol)
        const isExtreme = tradePos > 100 || tradePos < 0;
        const isBuyerAggressive = tradePos >= 80;
        const isSellerAggressive = tradePos <= 20;
        const isHeavyBuyer = tradePos >= 90;
        const isHeavySeller = tradePos <= 10;
        
        let bgClass = 'bg-[#1a1a1a]';
        let textClass = 'text-white';
        
        if (isHeavyBuyer || tradePos > 100) {
          bgClass = 'bg-green-600/40';
          textClass = 'text-green-300 font-bold';
        } else if (isBuyerAggressive) {
          bgClass = 'bg-green-600/25';
          textClass = 'text-green-400';
        } else if (isHeavySeller || tradePos < 0) {
          bgClass = 'bg-red-600/40';
          textClass = 'text-red-300 font-bold';
        } else if (isSellerAggressive) {
          bgClass = 'bg-red-600/25';
          textClass = 'text-red-400';
        }
        
        return (
          <div className={`relative h-5 ${bgClass} rounded-sm overflow-hidden flex items-center justify-center`}>
            <span className={`${textClass} ${fontSize} font-mono`}>
              {isExtreme && (tradePos > 100 ? '🔥' : '❄️')}{tradePos.toFixed(0)}%
            </span>
          </div>
        );
      }
      case 'spread': return <DataBar value={tick.spread} max={maxVals.maxSpread} color="yellow" />;
      case 'ltq': return <DataBar value={tick.last_traded_quantity} max={maxVals.maxLtq} color="blue" />;
      case 'vol_chg': return <DataBar value={Math.abs(tick.volume_change || 0)} max={maxVals.maxVolChg} color="cyan" />;
      case 'volume': return <DataBar value={tick.volume || tick.volume_traded} max={maxVals.maxVolume} color="purple" />;
      case 'oi': return <DataBar value={tick.oi} max={maxVals.maxOi} color="orange" />;
      case 'oi_change': {
        const oiChg = tick.oi_change || 0;
        return <DataBar 
          value={Math.abs(oiChg)} 
          max={maxVals.maxOiChg} 
          color={oiChg >= 0 ? "green" : "red"} 
          showSign={true}
          originalValue={oiChg}
        />;
      }
      case 'time': {
        // Green text for same-second ticks instead of yellow background
        const timeColor = sameSecond ? 'text-cyan-400 font-bold' : 'text-[#a1a1aa]';
        return <span className={`${timeColor} ${fontSize} font-mono`}>{formatTimeIST(tick.timestamp)}</span>;
      }
      default: return '-';
    }
  };

  return (
    <div className="flex-1 flex flex-col overflow-hidden h-full">
      {/* Scrollable container - vertical scroll only, columns fill width */}
      <div className="flex-1 overflow-auto" ref={parentRef}>
        <div className="min-w-full">
          {/* Sticky Header - Flexible columns with minimum widths and resize handles */}
          <div className={`sticky top-0 z-10 flex bg-[#0A0A0A] border-b border-[#27272A] ${compact ? 'text-[10px]' : 'text-[12px]'} text-[#71717A] font-semibold select-none`}>
            {orderedCols.map((key, idx) => (
              <div 
                key={key} 
                style={{ flex: customColWidths[key] ? 0 : 1, width: customColWidths[key] || undefined, minWidth: colWidths[key] || (compact ? 50 : 60), flexShrink: customColWidths[key] ? 0 : 1 }}
                className={`relative px-1 py-1.5 text-center whitespace-nowrap ${key === 'agg_buy' ? 'text-green-400 bg-green-900/20' : key === 'agg_sell' ? 'text-red-400 bg-red-900/20' : ''}`}
              >
                {COLUMNS.find(c => c.key === key)?.label}
                {/* Resize Handle */}
                {idx < orderedCols.length - 1 && (
                  <div
                    className={`absolute right-0 top-0 bottom-0 w-2 cursor-col-resize flex items-center justify-center ${resizing === key ? 'bg-blue-500/30' : 'hover:bg-blue-500/20'}`}
                    onMouseDown={(e) => handleResizeStart(e, key)}
                  >
                    <div className={`w-0.5 h-3/5 rounded-full transition-all ${resizing === key ? 'bg-blue-500' : 'bg-[#444] hover:bg-blue-400'}`} />
                  </div>
                )}
              </div>
            ))}
          </div>
          {/* Virtualized Rows - Agg colors ONLY on agg columns */}
          <div 
            style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative', willChange: 'contents' }}
            className="contain-paint"
          >
            {rowVirtualizer.getVirtualItems().map(vRow => {
              const tick = filteredTicks[vRow.index];
              const prevTick = vRow.index > 0 ? filteredTicks[vRow.index - 1] : null;
              // Same second - ONLY highlight time column
              const sameSecond = prevTick && getSeconds(tick.timestamp) === getSeconds(prevTick.timestamp);
              
              // Smart filter highlight class
              const smartFilterClass = smartFilters.enabled ? getSmartFilterRowClass(tick, smartFilters) : '';
              
              // Check if this is first row in a filter group - show header (only if labels enabled)
              const showFilterHeader = smartFilters.enabled && tick._smartFilterMatch && tick._isFirstInGroup && smartFilters.showLabels;
              const filterHeader = showFilterHeader ? getFilterHeaderRow(tick, true, compact, smartFilters.showLabels) : null;
              
              // Calculate extra height for header
              const headerHeight = showFilterHeader ? (compact ? 20 : 24) : 0;
              const rowHeight = compact ? 24 : 28;
              
              return (
                <div
                  key={vRow.index}
                  className="absolute left-0 right-0"
                  style={{ 
                    top: `${vRow.start}px`,
                    height: rowHeight + headerHeight,
                  }}
                >
                  {/* Filter Header Row - shows above matched group */}
                  {filterHeader}
                  
                  {/* Data Row - optimized for performance */}
                  <div
                    className={`flex items-center ${compact ? 'text-[10px]' : 'text-[12px]'} border-b border-[#1a1a1a] hover:bg-[#1a1a1a] ${smartFilterClass}`}
                    style={{ 
                      height: `${rowHeight}px`,
                      contain: 'layout style paint'
                    }}
                  >
                    {orderedCols.map(key => {
                      // Agg colors ONLY on agg_buy/agg_sell columns - time gets light blue text (not background)
                      let cellBg = '';
                      if (key === 'agg_buy' && tick.agg_buy > 0) cellBg = 'bg-green-900/40';
                      else if (key === 'agg_sell' && tick.agg_sell > 0) cellBg = 'bg-red-900/40';
                      
                      // Don't truncate important columns
                      const noTruncate = ['ltp', 'bid_price', 'ask_price', 'time'].includes(key);
                      
                      return (
                        <div 
                          key={key} 
                          style={{ flex: customColWidths[key] ? 0 : 1, width: customColWidths[key] || undefined, minWidth: colWidths[key] || (compact ? 50 : 60), flexShrink: customColWidths[key] ? 0 : 1 }}
                          className={`px-1 text-center ${noTruncate ? 'whitespace-nowrap overflow-hidden' : ''} ${cellBg}`}
                        >
                          {renderCell(tick, key, prevTick, sameSecond)}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
      {/* FOOTER with Filter Save Mode */}
      <div className="h-8 bg-[#0A0A0A] border-t border-[#27272A] flex items-center justify-between px-2 gap-2">
        <div className="flex items-center gap-2">
          <span className={`${compact ? 'text-[10px]' : 'text-[12px]'} text-[#52525B]`}>Showing {filteredTicks.length} / {ticks.length} ticks</span>
          {savedTicksCount > 0 && (
            <span className={`${compact ? 'text-[9px]' : 'text-[10px]'} px-1.5 py-0.5 rounded bg-purple-900/40 text-purple-400`}>
              💾 {savedTicksCount} saved
            </span>
          )}
        </div>
        <FilterSaveModeToggle
          mode={filterSaveMode}
          setMode={setFilterSaveMode}
          savedCount={savedTicksCount}
          onDownload={downloadSavedTicks}
          onDownloadFiltered={downloadFilteredTicks}
          filteredCount={filteredTicks.length}
          onClear={clearSavedTicks}
          autoDownload={autoDownload}
          setAutoDownload={setAutoDownload}
          alertEnabled={alertEnabled}
          setAlertEnabled={setAlertEnabled}
          compact={compact}
        />
      </div>
    </div>
  );
});

// Grid Cell Component - Premium Design with ADVANCED Filter, Resizable Columns
// NOW WITH LOCAL SMART FILTERS - Each table can have different conditions
// NOW WITH MULTI-INSTANCE SUPPORT - Same symbol can appear multiple times with unique gridId
const GridCell = React.memo(({ token, gridId, instanceNum, symbol, ticks, columns, columnOrder, onRemove, exchange, compact = false, onLoadMore, isLoadingMore = false, hasMoreHistory = true, isInitialLoading = false, smartFilters: globalSmartFilters = DEFAULT_SMART_FILTERS, globalFilterMode = null, globalHideTimestamp = null, onAddToGlobalSaved = null, globalAlertEnabled = false }) => {
  // Use gridId for localStorage keys to allow independent settings per instance
  const storageKey = gridId || token;
  
  // ===== STABLE DATA HANDLING - Prevents flickering =====
  // Cache last valid ticks to prevent flickering when data briefly becomes empty
  const lastValidTicksRef = useRef([]);
  const [dataStable, setDataStable] = useState(false);
  const initialLoadTimeoutRef = useRef(null);
  
  // Use stable ticks - either current ticks or last valid ticks
  const stableTicks = useMemo(() => {
    if (ticks && ticks.length > 0) {
      // We have valid data - cache it
      lastValidTicksRef.current = ticks;
      return ticks;
    }
    // No current data - use cached data if available
    return lastValidTicksRef.current;
  }, [ticks]);
  
  // Track when data becomes stable (received at least once)
  useEffect(() => {
    if (stableTicks.length > 0 && !dataStable) {
      // Clear any pending timeout
      if (initialLoadTimeoutRef.current) {
        clearTimeout(initialLoadTimeoutRef.current);
      }
      // Set stable after brief delay to prevent rapid state changes
      initialLoadTimeoutRef.current = setTimeout(() => {
        setDataStable(true);
      }, 300);
    }
    return () => {
      if (initialLoadTimeoutRef.current) {
        clearTimeout(initialLoadTimeoutRef.current);
      }
    };
  }, [stableTicks.length, dataStable]);
  
  // Show loading only on initial mount before any data arrives
  const showLoadingOverlay = isInitialLoading && !dataStable && stableTicks.length === 0;
  
  // Load saved filter settings from localStorage
  const savedFilters = useMemo(() => {
    try {
      const saved = localStorage.getItem(`gridcell_filters_${storageKey}`);
      return saved ? JSON.parse(saved) : null;
    } catch { return null; }
  }, [storageKey]);
  
  // Local filter state for this cell - including advanced filters (initialized from localStorage)
  const [localFilter, setLocalFilter] = useState(savedFilters?.localFilter || 'all');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [minLtq, setMinLtq] = useState(savedFilters?.minLtq || 0);
  const [minBidQty, setMinBidQty] = useState(savedFilters?.minBidQty || 0);
  const [minAskQty, setMinAskQty] = useState(savedFilters?.minAskQty || 0);
  
  // Bid/Ask Qty filter toggles and mode
  const [bidQtyEnabled, setBidQtyEnabled] = useState(savedFilters?.bidQtyEnabled || false);
  const [askQtyEnabled, setAskQtyEnabled] = useState(savedFilters?.askQtyEnabled || false);
  const [bidAskQtyMode, setBidAskQtyMode] = useState(savedFilters?.bidAskQtyMode || 'AND');
  
  // Position % filters with AND/OR mode
  const [posLessThanEnabled, setPosLessThanEnabled] = useState(savedFilters?.posLessThanEnabled || false);
  const [posLessThanValue, setPosLessThanValue] = useState(savedFilters?.posLessThanValue || '');
  const [posGreaterThanEnabled, setPosGreaterThanEnabled] = useState(savedFilters?.posGreaterThanEnabled || false);
  const [posGreaterThanValue, setPosGreaterThanValue] = useState(savedFilters?.posGreaterThanValue || '');
  const [posFilterMode, setPosFilterMode] = useState(savedFilters?.posFilterMode || 'OR');
  
  // LTP Diff filters with AND/OR mode (similar to AGG %)
  const [ltpDiffLessThanEnabled, setLtpDiffLessThanEnabled] = useState(savedFilters?.ltpDiffLessThanEnabled || false);
  const [ltpDiffLessThanValue, setLtpDiffLessThanValue] = useState(savedFilters?.ltpDiffLessThanValue || '');
  const [ltpDiffGreaterThanEnabled, setLtpDiffGreaterThanEnabled] = useState(savedFilters?.ltpDiffGreaterThanEnabled || false);
  const [ltpDiffGreaterThanValue, setLtpDiffGreaterThanValue] = useState(savedFilters?.ltpDiffGreaterThanValue || '');
  const [ltpDiffFilterMode, setLtpDiffFilterMode] = useState(savedFilters?.ltpDiffFilterMode || 'OR');
  
  // Debounced LTP Diff values - prevents expensive re-filtering on every keystroke
  const [debouncedLtpDiffLess, setDebouncedLtpDiffLess] = useState(savedFilters?.ltpDiffLessThanValue || '');
  const [debouncedLtpDiffGreater, setDebouncedLtpDiffGreater] = useState(savedFilters?.ltpDiffGreaterThanValue || '');
  const ltpDiffDebounceRef = useRef(null);
  
  useEffect(() => {
    if (ltpDiffDebounceRef.current) clearTimeout(ltpDiffDebounceRef.current);
    ltpDiffDebounceRef.current = setTimeout(() => {
      setDebouncedLtpDiffLess(ltpDiffLessThanValue);
      setDebouncedLtpDiffGreater(ltpDiffGreaterThanValue);
    }, 300);
    return () => { if (ltpDiffDebounceRef.current) clearTimeout(ltpDiffDebounceRef.current); };
  }, [ltpDiffLessThanValue, ltpDiffGreaterThanValue]);
  
  // Volume Change and Spread filters
  const [minVolChg, setMinVolChg] = useState(savedFilters?.minVolChg || 0);
  const [spreadLessThanEnabled, setSpreadLessThanEnabled] = useState(savedFilters?.spreadLessThanEnabled || false);
  const [spreadLessThanValue, setSpreadLessThanValue] = useState(savedFilters?.spreadLessThanValue || 0);
  const [spreadGreaterThanEnabled, setSpreadGreaterThanEnabled] = useState(savedFilters?.spreadGreaterThanEnabled || false);
  const [spreadGreaterThanValue, setSpreadGreaterThanValue] = useState(savedFilters?.spreadGreaterThanValue || 0);
  
  // Telegram Alert Toggle for this table
  const [telegramAlertEnabled, setTelegramAlertEnabled] = useState(() => {
    try {
      const saved = localStorage.getItem(`telegram_alert_${storageKey}`);
      return saved === 'true';
    } catch { return false; }
  });
  const [lastTelegramAlertTime, setLastTelegramAlertTime] = useState(0);
  const telegramAlertSentRef = useRef(new Set()); // Track sent alerts to avoid duplicates
  
  // Save Telegram toggle state
  useEffect(() => {
    localStorage.setItem(`telegram_alert_${storageKey}`, telegramAlertEnabled.toString());
  }, [telegramAlertEnabled, storageKey]);
  
  // Save filter settings to localStorage whenever they change
  useEffect(() => {
    const filterSettings = {
      localFilter,
      minLtq,
      minBidQty,
      minAskQty,
      bidQtyEnabled,
      askQtyEnabled,
      bidAskQtyMode,
      posLessThanEnabled,
      posLessThanValue,
      posGreaterThanEnabled,
      posGreaterThanValue,
      posFilterMode,
      ltpDiffLessThanEnabled,
      ltpDiffLessThanValue,
      ltpDiffGreaterThanEnabled,
      ltpDiffGreaterThanValue,
      ltpDiffFilterMode,
      minVolChg,
      spreadLessThanEnabled,
      spreadLessThanValue,
      spreadGreaterThanEnabled,
      spreadGreaterThanValue
    };
    localStorage.setItem(`gridcell_filters_${storageKey}`, JSON.stringify(filterSettings));
  }, [storageKey, localFilter, minLtq, minBidQty, minAskQty, bidQtyEnabled, askQtyEnabled, bidAskQtyMode, posLessThanEnabled, posLessThanValue, posGreaterThanEnabled, posGreaterThanValue, posFilterMode, ltpDiffLessThanEnabled, ltpDiffLessThanValue, ltpDiffGreaterThanEnabled, ltpDiffGreaterThanValue, ltpDiffFilterMode, minVolChg, spreadLessThanEnabled, spreadLessThanValue, spreadGreaterThanEnabled, spreadGreaterThanValue]);
  
  // LOCAL SMART FILTERS - Each GridCell has its own smart filter settings (using storageKey for multi-instance)
  const [localSmartFilters, setLocalSmartFilters] = useState(() => {
    // Try to load saved settings for this instance (gridId) or fall back to token
    try {
      const saved = localStorage.getItem(`smartfilters_${storageKey}`);
      return saved ? JSON.parse(saved) : { ...DEFAULT_SMART_FILTERS };
    } catch { return { ...DEFAULT_SMART_FILTERS }; }
  });
  
  // Save local smart filters when they change
  useEffect(() => {
    if (localSmartFilters.enabled || Object.keys(localSmartFilters).some(k => localSmartFilters[k]?.enabled)) {
      localStorage.setItem(`smartfilters_${storageKey}`, JSON.stringify(localSmartFilters));
    }
  }, [localSmartFilters, storageKey]);
  
  // Use local smart filters (allows per-symbol configuration)
  const smartFilters = localSmartFilters;
  
  // Column widths state for resizing - persisted to localStorage per instance
  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(`ticklog_colwidths_${storageKey}`);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [resizing, setResizing] = useState(null);
  
  // Valid column keys for filtering out removed columns from localStorage
  const validColumnKeys = COLUMNS.map(c => c.key);
  
  // LOCAL COLUMN VISIBILITY - Each GridCell has its own column settings
  const [localVisibleCols, setLocalVisibleCols] = useState(() => {
    try {
      const saved = localStorage.getItem(`gridcell_cols_${storageKey}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.filter(col => validColumnKeys.includes(col));
      }
      // Default columns for grid cell
      return ['time', 'ltp', 'ltp_diff', 'trade_pos', 'bid_qty', 'ask_qty', 'ltq', 'spread', 'vol_chg'];
    } catch { 
      return ['time', 'ltp', 'ltp_diff', 'trade_pos', 'bid_qty', 'ask_qty', 'ltq', 'spread', 'vol_chg']; 
    }
  });
  
  // LOCAL COLUMN ORDER - Each GridCell has its own column order (for reordering)
  const [localColumnOrder, setLocalColumnOrder] = useState(() => {
    try {
      const saved = localStorage.getItem(`gridcell_order_${storageKey}`);
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.filter(col => validColumnKeys.includes(col));
      }
      // Default order - all columns
      return COLUMNS.map(c => c.key);
    } catch { 
      return COLUMNS.map(c => c.key); 
    }
  });
  
  // FILTER SAVE MODE - Save/Show All/Hide functionality for filtered results
  // Must be defined AFTER localVisibleCols and localColumnOrder
  // Now integrates with global mode when passed
  const {
    filterSaveMode,
    setFilterSaveMode,
    savedTicksCount,
    processFilteredTicks,
    downloadSavedTicks,
    clearSavedTicks,
    autoDownload,
    setAutoDownload,
    alertEnabled,
    setAlertEnabled,
    isGlobalModeActive,
    hasLocalOverride,
  } = useFilterSaveMode(storageKey, symbol, localVisibleCols, localColumnOrder, exchange, globalFilterMode, globalHideTimestamp, onAddToGlobalSaved, globalAlertEnabled);
  
  // Save local column visibility when it changes
  useEffect(() => {
    localStorage.setItem(`gridcell_cols_${storageKey}`, JSON.stringify(localVisibleCols));
  }, [localVisibleCols, storageKey]);
  
  // Save local column order when it changes
  useEffect(() => {
    localStorage.setItem(`gridcell_order_${storageKey}`, JSON.stringify(localColumnOrder));
  }, [localColumnOrder, storageKey]);
  
  // Toggle column visibility for this cell
  const toggleLocalColumn = (colKey) => {
    setLocalVisibleCols(prev => 
      prev.includes(colKey) 
        ? prev.filter(c => c !== colKey) 
        : [...prev, colKey]
    );
  };
  
  // Move column up in order
  const moveColumnUp = (colKey) => {
    setLocalColumnOrder(prev => {
      const idx = prev.indexOf(colKey);
      if (idx > 0) {
        const newOrder = [...prev];
        [newOrder[idx - 1], newOrder[idx]] = [newOrder[idx], newOrder[idx - 1]];
        return newOrder;
      }
      return prev;
    });
  };
  
  // Move column down in order
  const moveColumnDown = (colKey) => {
    setLocalColumnOrder(prev => {
      const idx = prev.indexOf(colKey);
      if (idx < prev.length - 1) {
        const newOrder = [...prev];
        [newOrder[idx], newOrder[idx + 1]] = [newOrder[idx + 1], newOrder[idx]];
        return newOrder;
      }
      return prev;
    });
  };
  
  // Save column widths to localStorage when they change (use storageKey for multi-instance)
  useEffect(() => {
    if (Object.keys(colWidths).length > 0) {
      localStorage.setItem(`ticklog_colwidths_${storageKey}`, JSON.stringify(colWidths));
    }
  }, [colWidths, storageKey]);
  
  // Get column widths based on compact mode
  const defaultColWidths = compact ? COLUMN_COMPACT_WIDTHS : COLUMN_MIN_WIDTHS;
  
  // Filter ticks by market hours AND deduplicate - CRITICAL: no ticks outside market hours, no duplicates
  // BUT: When market is closed, show all ticks (backend already filtered them)
  // ALSO: Filter out dummy ticks with 0 bid/ask prices
  // Use stableTicks instead of ticks to prevent flickering
  const marketFilteredTicks = useMemo(() => {
    if (!stableTicks || stableTicks.length === 0) return [];
    
    const marketStatus = getCurrentMarketStatus();
    
    // First, always filter out dummy ticks regardless of market status
    const nonDummyTicks = stableTicks.filter(t => 
      t.bid_price && t.bid_price > 0 && t.ask_price && t.ask_price > 0
    );
    
    // When market is closed, backend already returns valid ticks from last trading day
    // Just deduplicate and sort, no need to filter by market hours again
    if (!marketStatus.isOpen) {
      return deduplicateAndSortTicks(nonDummyTicks);
    }
    
    // Market is open - filter by market hours
    const validTicks = nonDummyTicks.filter(t => {
      const ts = t.timestamp_ms || (t.timestamp && new Date(t.timestamp).getTime());
      if (!ts) return false;
      const tickExchange = t.exchange || exchange || 'NSE';
      return isWithinMarketHours(ts, tickExchange);
    });
    
    // Then deduplicate and sort (newest first)
    return deduplicateAndSortTicks(validTicks);
  }, [stableTicks, exchange]);
  
  // STABLE COUNT STATE - Using useState + useEffect instead of refs inside useMemo
  // This prevents flickering by controlling updates separately from renders
  const [stableTotalCount, setStableTotalCount] = useState(0);
  const totalCountTimerRef = useRef(null);
  
  useEffect(() => {
    const currentCount = marketFilteredTicks.length;
    
    // During initial loading, update immediately
    if (isInitialLoading || currentCount === 0) {
      setStableTotalCount(currentCount);
      return;
    }
    
    // Clear any pending update
    if (totalCountTimerRef.current) {
      clearTimeout(totalCountTimerRef.current);
    }
    
    // Only update if count is growing or significant change
    setStableTotalCount(prev => {
      const diff = Math.abs(currentCount - prev);
      // Always update if growing, or if significant change
      if (currentCount > prev || diff > 100 || prev === 0) {
        return currentCount;
      }
      // For decreases, batch updates with a slight delay to prevent flicker
      totalCountTimerRef.current = setTimeout(() => {
        setStableTotalCount(currentCount);
      }, 2000);
      return prev;
    });
    
    return () => {
      if (totalCountTimerRef.current) {
        clearTimeout(totalCountTimerRef.current);
      }
    };
  }, [marketFilteredTicks.length, isInitialLoading]);
  
  // Calculate max values for bars
  const maxVals = useMemo(() => {
    const sample = marketFilteredTicks.slice(0, 500);
    return {
      maxLtq: Math.max(1, ...sample.map(t => t.last_traded_quantity || 0)),
      maxBidQty: Math.max(1, ...sample.map(t => t.bid_qty || 0)),
      maxAskQty: Math.max(1, ...sample.map(t => t.ask_qty || 0)),
      maxVolChg: Math.max(1, ...sample.map(t => Math.abs(t.volume_change || 0))),
      maxOiChg: Math.max(1, ...sample.map(t => Math.abs(t.oi_change || 0))),
      maxSpread: Math.max(1, ...sample.map(t => t.spread || 0)),
      maxVolume: Math.max(1, ...sample.map(t => t.volume || t.volume_traded || 0)),
      maxOi: Math.max(1, ...sample.map(t => t.oi || 0)),
    };
  }, [marketFilteredTicks]);
  
  // Order columns - use local column order and visibility for this cell
  const orderedCols = localColumnOrder.filter(k => localVisibleCols.includes(k));
  
  // OPTIMIZED: Apply user filter AND advanced filters on top of market-filtered ticks
  // Uses caching and early termination to prevent unnecessary recalculation
  const filteredTicks = useMemo(() => {
    // Generate cache key based on filter config
    const filterKey = `${storageKey}_${localFilter}_${minLtq}_${minBidQty}_${minAskQty}_${bidQtyEnabled}_${askQtyEnabled}_${bidAskQtyMode}_${posLessThanEnabled}_${posLessThanValue}_${posGreaterThanEnabled}_${posGreaterThanValue}_${posFilterMode}_${ltpDiffLessThanEnabled}_${debouncedLtpDiffLess}_${debouncedLtpDiffGreater}_${ltpDiffFilterMode}_${minVolChg}_${spreadLessThanEnabled}_${spreadLessThanValue}_${spreadGreaterThanEnabled}_${spreadGreaterThanValue}_${smartFilters.enabled}_${marketFilteredTicks.length}`;
    
    // Check cache first (only for large datasets where filtering is expensive)
    if (marketFilteredTicks.length > 1000) {
      const cached = getCachedFilterResult(filterKey);
      if (cached) return cached;
    }
    
    let result = marketFilteredTicks;
    
    // CRITICAL: Pre-calculate ltp_diff BEFORE filtering
    // ltp_diff = difference between current tick's LTP and previous tick's LTP
    if (result && result.length > 0) {
      for (let i = 0; i < result.length; i++) {
        if (i === result.length - 1) {
          result[i].ltp_diff = 0;
        } else {
          const currentLtp = result[i].last_price || 0;
          const prevLtp = result[i + 1].last_price || 0;
          result[i].ltp_diff = currentLtp - prevLtp;
        }
      }
    }
    
    // EARLY EXIT: If no filters active, return immediately
    const hasAnyFilter = localFilter !== 'all' || minLtq > 0 || minVolChg > 0 || 
      (bidQtyEnabled && minBidQty > 0) || (askQtyEnabled && minAskQty > 0) ||
      (spreadLessThanEnabled && spreadLessThanValue > 0) || (spreadGreaterThanEnabled && spreadGreaterThanValue > 0) ||
      (posLessThanEnabled && posLessThanValue !== '') || (posGreaterThanEnabled && posGreaterThanValue !== '') ||
      (ltpDiffLessThanEnabled && debouncedLtpDiffLess !== '') || (ltpDiffGreaterThanEnabled && debouncedLtpDiffGreater !== '') ||
      smartFilters.enabled;
    
    if (!hasAnyFilter) {
      return processFilteredTicks(result);
    }
    
    // Basic filter
    if (localFilter === 'buy') result = result.filter(t => t.agg_buy > 0);
    else if (localFilter === 'sell') result = result.filter(t => t.agg_sell > 0);
    else if (localFilter === 'large_ltq') result = result.filter(t => (t.last_traded_quantity || 0) > maxVals.maxLtq * 0.3);
    else if (localFilter === 'large_vol') result = result.filter(t => (t.volume_change || 0) > maxVals.maxVolChg * 0.3);
    else if (localFilter === 'oi_up') result = result.filter(t => (t.oi_change || 0) > 0);
    else if (localFilter === 'oi_down') result = result.filter(t => (t.oi_change || 0) < 0);
    // Advanced filters
    if (minLtq > 0) result = result.filter(t => (t.last_traded_quantity || 0) >= minLtq);
    
    // Bid/Ask Qty filters with AND/OR mode
    const hasBidQtyFilter = bidQtyEnabled && minBidQty > 0;
    const hasAskQtyFilter = askQtyEnabled && minAskQty > 0;
    
    if (hasBidQtyFilter || hasAskQtyFilter) {
      result = result.filter(t => {
        const bidQtyMatch = (t.bid_qty || 0) >= minBidQty;
        const askQtyMatch = (t.ask_qty || 0) >= minAskQty;
        
        if (hasBidQtyFilter && hasAskQtyFilter) {
          return bidAskQtyMode === 'AND' ? (bidQtyMatch && askQtyMatch) : (bidQtyMatch || askQtyMatch);
        }
        if (hasBidQtyFilter) return bidQtyMatch;
        if (hasAskQtyFilter) return askQtyMatch;
        return true;
      });
    }
    
    // Position % filters with AND/OR mode
    const posLessVal = parseFloat(posLessThanValue) || 0;
    const posGreaterVal = parseFloat(posGreaterThanValue) || 0;
    const hasPosLessThan = posLessThanEnabled && posLessThanValue !== '';
    const hasPosGreaterThan = posGreaterThanEnabled && posGreaterThanValue !== '';
    
    if (hasPosLessThan || hasPosGreaterThan) {
      result = result.filter(t => {
        const tradePos = calculateTradePosition(t);
        if (tradePos === null) return false;
        
        const lessThanMatch = tradePos < posLessVal;
        const greaterThanMatch = tradePos > posGreaterVal;
        
        if (hasPosLessThan && hasPosGreaterThan) {
          return posFilterMode === 'AND' ? (lessThanMatch && greaterThanMatch) : (lessThanMatch || greaterThanMatch);
        }
        if (hasPosLessThan) return lessThanMatch;
        if (hasPosGreaterThan) return greaterThanMatch;
        return true;
      });
    }
    
    // LTP Diff filters with AND/OR mode (similar to AGG %)
    // Note: ltp_diff is PRE-CALCULATED when ticks are processed (no new objects created)
    // Uses DEBOUNCED values to prevent expensive re-filtering on every keystroke
    const ltpDiffLessVal = parseFloat(debouncedLtpDiffLess) || 0;
    const ltpDiffGreaterVal = parseFloat(debouncedLtpDiffGreater) || 0;
    const hasLtpDiffLessThan = ltpDiffLessThanEnabled && debouncedLtpDiffLess !== '';
    const hasLtpDiffGreaterThan = ltpDiffGreaterThanEnabled && debouncedLtpDiffGreater !== '';
    
    if (hasLtpDiffLessThan || hasLtpDiffGreaterThan) {
      result = result.filter(t => {
        // Use pre-calculated ltp_diff (already on tick object)
        const ltpDiff = t.ltp_diff || 0;
        
        const lessThanMatch = ltpDiff < ltpDiffLessVal;
        const greaterThanMatch = ltpDiff > ltpDiffGreaterVal;
        
        if (hasLtpDiffLessThan && hasLtpDiffGreaterThan) {
          return ltpDiffFilterMode === 'AND' ? (lessThanMatch && greaterThanMatch) : (lessThanMatch || greaterThanMatch);
        }
        if (hasLtpDiffLessThan) return lessThanMatch;
        if (hasLtpDiffGreaterThan) return greaterThanMatch;
        return true;
      });
    }
    
    // Volume Change filter
    if (minVolChg > 0) result = result.filter(t => Math.abs(t.volume_change || 0) >= minVolChg);
    
    // Spread filters - OR logic when both enabled
    const hasSpreadLessThan = spreadLessThanEnabled && spreadLessThanValue > 0;
    const hasSpreadGreaterThan = spreadGreaterThanEnabled && spreadGreaterThanValue > 0;
    
    if (hasSpreadLessThan || hasSpreadGreaterThan) {
      result = result.filter(t => {
        const spread = (t.ask_price || 0) - (t.bid_price || 0);
        if (hasSpreadLessThan && hasSpreadGreaterThan) {
          return spread < spreadLessThanValue || spread > spreadGreaterThanValue;
        }
        if (hasSpreadLessThan) return spread < spreadLessThanValue;
        if (hasSpreadGreaterThan) return spread > spreadGreaterThanValue;
        return true;
      });
    }
    
    // Apply smart filters - process ALL ticks
    if (smartFilters.enabled) {
      const smartMaxValues = calculateMaxValues(result);
      const filtered = applySmartFilters(result, smartFilters, smartMaxValues);
      
      // If filter mode, only show matching rows
      if (smartFilters.mode === 'filter') {
        result = filtered.filter(t => t._smartFilterMatch);
        // Recalculate group boundaries for filtered results
        result = result.map((tick, i, arr) => ({
          ...tick,
          _isFirstInGroup: i === 0 || !arr[i - 1]._smartFilterMatch,
          _isLastInGroup: i === arr.length - 1 || !arr[i + 1]._smartFilterMatch,
        }));
      } else {
        // Use filtered results directly
        result = filtered;
      }
    }
    
    // Apply filter save mode processing (Save/Show All/Hide)
    const finalResult = processFilteredTicks(result);
    
    // Cache result for large datasets
    if (marketFilteredTicks.length > 1000) {
      const filterKey = `${storageKey}_${localFilter}_${minLtq}_${minBidQty}_${minAskQty}_${bidQtyEnabled}_${askQtyEnabled}_${bidAskQtyMode}_${posLessThanEnabled}_${posLessThanValue}_${posGreaterThanEnabled}_${posGreaterThanValue}_${posFilterMode}_${ltpDiffLessThanEnabled}_${debouncedLtpDiffLess}_${debouncedLtpDiffGreater}_${ltpDiffFilterMode}_${minVolChg}_${spreadLessThanEnabled}_${spreadLessThanValue}_${spreadGreaterThanEnabled}_${spreadGreaterThanValue}_${smartFilters.enabled}_${marketFilteredTicks.length}`;
      setCachedFilterResult(filterKey, finalResult);
    }
    
    return finalResult;
  }, [marketFilteredTicks, localFilter, maxVals, minLtq, minBidQty, minAskQty, bidQtyEnabled, askQtyEnabled, bidAskQtyMode, posLessThanEnabled, posLessThanValue, posGreaterThanEnabled, posGreaterThanValue, posFilterMode, ltpDiffLessThanEnabled, debouncedLtpDiffLess, debouncedLtpDiffGreater, ltpDiffFilterMode, minVolChg, spreadLessThanEnabled, spreadLessThanValue, spreadGreaterThanEnabled, spreadGreaterThanValue, smartFilters, processFilteredTicks, storageKey]);
  
  // Download currently visible filtered ticks as CSV
  const downloadFilteredTicks = useCallback(() => {
    if (filteredTicks.length === 0) {
      window.alert('No filtered ticks to download. Apply some filters first!');
      return;
    }
    console.log(`[GridCell Download Filtered] Downloading ${filteredTicks.length} visible filtered ticks for ${symbol}`);
    downloadTicksAsCSV(filteredTicks, symbol, localVisibleCols, localColumnOrder);
  }, [filteredTicks, symbol, localVisibleCols, localColumnOrder]);
  
  // STABLE FILTERED COUNT STATE - Controlled updates to prevent flickering
  const [stableFilteredCount, setStableFilteredCount] = useState(0);
  const filteredCountTimerRef = useRef(null);
  
  useEffect(() => {
    const currentCount = filteredTicks.length;
    
    // During initial loading, update immediately
    if (isInitialLoading || currentCount === 0) {
      setStableFilteredCount(currentCount);
      return;
    }
    
    // Clear any pending update
    if (filteredCountTimerRef.current) {
      clearTimeout(filteredCountTimerRef.current);
    }
    
    // Only update if count is growing or significant change
    setStableFilteredCount(prev => {
      const diff = Math.abs(currentCount - prev);
      // Always update if growing, or if significant change
      if (currentCount > prev || diff > 100 || prev === 0) {
        return currentCount;
      }
      // For decreases, batch updates with a slight delay to prevent flicker
      filteredCountTimerRef.current = setTimeout(() => {
        setStableFilteredCount(currentCount);
      }, 2000);
      return prev;
    });
    
    return () => {
      if (filteredCountTimerRef.current) {
        clearTimeout(filteredCountTimerRef.current);
      }
    };
  }, [filteredTicks.length, isInitialLoading]);
  
  // Memoized buy/sell counts - during initial loading, show actual counts
  const buySellCounts = useMemo(() => {
    const buyCount = filteredTicks.filter(t => t.agg_buy > 0).length;
    const sellCount = filteredTicks.filter(t => t.agg_sell > 0).length;
    return { buyCount, sellCount };
  }, [filteredTicks]);
  
  // STABLE BUY/SELL COUNTS - Controlled updates to prevent flickering
  const [stableBuySellCounts, setStableBuySellCounts] = useState({ buyCount: 0, sellCount: 0 });
  const buySellTimerRef = useRef(null);
  
  useEffect(() => {
    const { buyCount, sellCount } = buySellCounts;
    
    // During initial loading, update immediately
    if (isInitialLoading) {
      setStableBuySellCounts({ buyCount, sellCount });
      return;
    }
    
    // Clear any pending update
    if (buySellTimerRef.current) {
      clearTimeout(buySellTimerRef.current);
    }
    
    // Only update if counts are growing or significant change
    setStableBuySellCounts(prev => {
      const buyDiff = Math.abs(buyCount - prev.buyCount);
      const sellDiff = Math.abs(sellCount - prev.sellCount);
      
      // Always update if growing, or if significant change
      if (buyCount >= prev.buyCount || sellCount >= prev.sellCount || buyDiff > 50 || sellDiff > 50) {
        return { buyCount, sellCount };
      }
      // For decreases, batch updates with a slight delay to prevent flicker
      buySellTimerRef.current = setTimeout(() => {
        setStableBuySellCounts({ buyCount, sellCount });
      }, 2000);
      return prev;
    });
    
    return () => {
      if (buySellTimerRef.current) {
        clearTimeout(buySellTimerRef.current);
      }
    };
  }, [buySellCounts, isInitialLoading]);
  
  // TELEGRAM ALERTS - Send alerts when filters match new ticks
  const prevFilteredCountRef = useRef(0);
  
  useEffect(() => {
    // Skip if Telegram alerts disabled or no filters active
    if (!telegramAlertEnabled) return;
    
    // Check if any filter is active
    const hasActiveFilter = localFilter !== 'all' || minLtq > 0 || minVolChg > 0 ||
      (bidQtyEnabled && minBidQty > 0) || (askQtyEnabled && minAskQty > 0) ||
      (spreadLessThanEnabled && spreadLessThanValue > 0) || (spreadGreaterThanEnabled && spreadGreaterThanValue > 0) ||
      (posLessThanEnabled && posLessThanValue !== '') || (posGreaterThanEnabled && posGreaterThanValue !== '') ||
      (ltpDiffLessThanEnabled && debouncedLtpDiffLess !== '') || (ltpDiffGreaterThanEnabled && debouncedLtpDiffGreater !== '') ||
      smartFilters.enabled;
    
    if (!hasActiveFilter) return;
    
    // Only trigger on new filtered ticks (count increase)
    const currentCount = filteredTicks.length;
    const prevCount = prevFilteredCountRef.current;
    
    if (currentCount > prevCount && currentCount > 0 && prevCount > 0) {
      // New ticks matched the filter - send alert for the latest tick
      const latestTick = filteredTicks[0]; // Most recent tick
      if (latestTick && !telegramAlertSentRef.current.has(latestTick.timestamp_ms)) {
        // Build filter description
        const filterParts = [];
        if (localFilter !== 'all') filterParts.push(`Type: ${localFilter}`);
        if (minLtq > 0) filterParts.push(`LTQ ≥ ${minLtq}`);
        if (bidQtyEnabled && minBidQty > 0) filterParts.push(`Bid ≥ ${minBidQty}`);
        if (askQtyEnabled && minAskQty > 0) filterParts.push(`Ask ≥ ${minAskQty}`);
        if (ltpDiffLessThanEnabled) filterParts.push(`LTP Diff < ${debouncedLtpDiffLess}`);
        if (ltpDiffGreaterThanEnabled) filterParts.push(`LTP Diff > ${debouncedLtpDiffGreater}`);
        if (posLessThanEnabled) filterParts.push(`Agg% < ${posLessThanValue}`);
        if (posGreaterThanEnabled) filterParts.push(`Agg% > ${posGreaterThanValue}`);
        if (minVolChg > 0) filterParts.push(`Vol Chg ≥ ${minVolChg}`);
        if (spreadLessThanEnabled) filterParts.push(`Spread < ${spreadLessThanValue}`);
        if (spreadGreaterThanEnabled) filterParts.push(`Spread > ${spreadGreaterThanValue}`);
        if (smartFilters.enabled) filterParts.push('Smart Filters ON');
        
        const filterValue = filterParts.join(' | ') || 'Custom Filter';
        
        // Mark this tick as sent to avoid duplicates
        telegramAlertSentRef.current.add(latestTick.timestamp_ms);
        
        // Limit stored sent alerts to prevent memory leak
        if (telegramAlertSentRef.current.size > 100) {
          const arr = Array.from(telegramAlertSentRef.current);
          telegramAlertSentRef.current = new Set(arr.slice(-50));
        }
        
        // Send the alert asynchronously
        sendBasicFilterTelegramAlert(
          apiClient,
          symbol,
          token,
          'basic_filter',
          filterValue,
          latestTick,
          storageKey
        ).then(result => {
          if (result.status === 'sent') {
            console.log(`[TG Alert] Sent for ${symbol}: ${filterValue}`);
          }
        }).catch(err => {
          console.error(`[TG Alert] Error for ${symbol}:`, err);
        });
      }
    }
    
    prevFilteredCountRef.current = currentCount;
  }, [telegramAlertEnabled, filteredTicks.length, filteredTicks, symbol, token, storageKey, localFilter, minLtq, minBidQty, minAskQty, bidQtyEnabled, askQtyEnabled, minVolChg, spreadLessThanEnabled, spreadLessThanValue, spreadGreaterThanEnabled, spreadGreaterThanValue, posLessThanEnabled, posLessThanValue, posGreaterThanEnabled, posGreaterThanValue, ltpDiffLessThanEnabled, debouncedLtpDiffLess, ltpDiffGreaterThanEnabled, debouncedLtpDiffGreater, smartFilters.enabled]);

  const parentRef = useRef(null);
  
  // Calculate row height including potential filter header
  const getRowHeight = (index) => {
    const tick = filteredTicks[index];
    const baseHeight = compact ? 24 : 30;
    // Add header height if this is first in a filter group
    if (smartFilters.enabled && tick?._smartFilterMatch && tick?._isFirstInGroup) {
      return baseHeight + (compact ? 18 : 22);
    }
    return baseHeight;
  };
  
  const rowVirtualizer = useVirtualizer({
    count: Math.min(filteredTicks.length, 5000),
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => getRowHeight(index),
    overscan: 15,
  });

  // Handle column resize
  const handleMouseDown = (e, colKey) => {
    e.preventDefault();
    setResizing(colKey);
    const startX = e.clientX;
    const startWidth = colWidths[colKey] || (compact ? 60 : 80);
    
    const handleMouseMove = (moveEvent) => {
      const diff = moveEvent.clientX - startX;
      setColWidths(prev => ({
        ...prev,
        [colKey]: Math.max(10, startWidth + diff) // No restriction
      }));
    };
    
    const handleMouseUp = () => {
      setResizing(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Get column width - either custom or auto-sized minimum
  const getColStyle = (key) => {
    if (colWidths[key]) {
      return { width: colWidths[key], minWidth: 10, flexShrink: 0 }; // No restriction
    }
    // Use minimum width from config, flex-grow to fill space
    const minW = defaultColWidths[key] || (compact ? 30 : 40);
    return { flex: 1, minWidth: minW };
  };

  // Render cell with proper colors - LTP shows REVERSED price change color (Red=UP, Green=DOWN)
  // sameSecond parameter for time column light blue text (consecutive same-second ticks)
  const renderCell = (tick, key, prevTick, sameSecond = false) => {
    const fontSize = compact ? 'text-[10px]' : 'text-[12px]';
    const fontSizeLarge = compact ? 'text-[11px]' : 'text-[13px]';
    switch(key) {
      case 'ltp': {
        const priceUp = prevTick && tick.last_price > prevTick.last_price;
        const priceDown = prevTick && tick.last_price < prevTick.last_price;
        // REVERSED: Red for price UP, Green for price DOWN
        const colorClass = priceUp ? 'text-red-400' : priceDown ? 'text-green-400' : 'text-white';
        return <span className={`font-mono ${fontSizeLarge} font-bold ${colorClass}`}>{formatNumber(tick.last_price)}</span>;
      }
      case 'ltp_diff': {
        // Calculate ltp_diff lazily and cache on tick object (only for visible rows)
        if (tick.ltp_diff === undefined && prevTick) {
          tick.ltp_diff = (tick.last_price || 0) - (prevTick.last_price || 0);
        }
        const ltpDiff = tick.ltp_diff || 0;
        
        if (ltpDiff === 0) return <span className={`text-[#71717A] ${fontSize} font-mono`}>0.00</span>;
        
        const isPositive = ltpDiff > 0;
        const absDiff = Math.abs(ltpDiff);
        const isSignificant = absDiff >= 1;
        const isLarge = absDiff >= 2;
        
        let bgClass = 'bg-transparent';
        let textClass = isPositive ? 'text-green-400' : 'text-red-400';
        let symbol = '';
        
        if (isLarge) {
          bgClass = isPositive ? 'bg-green-600/40' : 'bg-red-600/40';
          textClass = isPositive ? 'text-green-300 font-bold' : 'text-red-300 font-bold';
          symbol = isPositive ? '🔥' : '❄️';
        } else if (isSignificant) {
          bgClass = isPositive ? 'bg-green-600/25' : 'bg-red-600/25';
          textClass = isPositive ? 'text-green-400 font-semibold' : 'text-red-400 font-semibold';
        }
        
        const displayValue = (isPositive ? '+' : '') + ltpDiff.toFixed(2);
        
        if (bgClass !== 'bg-transparent') {
          return (
            <div className={`relative h-5 ${bgClass} rounded-sm overflow-hidden flex items-center justify-center`}>
              <span className={`${textClass} ${fontSize} font-mono`}>
                {symbol}{displayValue}
              </span>
            </div>
          );
        }
        
        return <span className={`${textClass} ${fontSize} font-mono`}>{displayValue}</span>;
      }
      case 'bid_price': return <span className={`text-green-400 font-mono ${fontSize} font-medium`}>{formatNumber(tick.bid_price)}</span>;
      case 'ask_price': return <span className={`text-red-400 font-mono ${fontSize} font-medium`}>{formatNumber(tick.ask_price)}</span>;
      case 'bid_qty': return <DataBar value={tick.bid_qty} max={maxVals.maxBidQty} color="green" />;
      case 'ask_qty': return <DataBar value={tick.ask_qty} max={maxVals.maxAskQty} color="red" />;
      case 'trade_pos': {
        const tradePos = calculateTradePosition(tick);
        if (tradePos === null) return <span className={`text-[#3f3f46] ${fontSize}`}>-</span>;
        
        const isExtreme = tradePos > 100 || tradePos < 0;
        const isBuyerAggressive = tradePos >= 80;
        const isSellerAggressive = tradePos <= 20;
        const isHeavyBuyer = tradePos >= 90;
        const isHeavySeller = tradePos <= 10;
        
        let bgClass = 'bg-[#1a1a1a]';
        let textClass = 'text-white';
        
        if (isHeavyBuyer || tradePos > 100) {
          bgClass = 'bg-green-600/40';
          textClass = 'text-green-300 font-bold';
        } else if (isBuyerAggressive) {
          bgClass = 'bg-green-600/25';
          textClass = 'text-green-400';
        } else if (isHeavySeller || tradePos < 0) {
          bgClass = 'bg-red-600/40';
          textClass = 'text-red-300 font-bold';
        } else if (isSellerAggressive) {
          bgClass = 'bg-red-600/25';
          textClass = 'text-red-400';
        }
        
        return (
          <div className={`relative h-5 ${bgClass} rounded-sm overflow-hidden flex items-center justify-center`}>
            <span className={`${textClass} ${fontSize} font-mono`}>
              {isExtreme && (tradePos > 100 ? '🔥' : '❄️')}{tradePos.toFixed(0)}%
            </span>
          </div>
        );
      }
      case 'spread': return <DataBar value={tick.spread} max={maxVals.maxSpread} color="yellow" />;
      case 'ltq': return <DataBar value={tick.last_traded_quantity} max={maxVals.maxLtq} color="blue" />;
      case 'vol_chg': return <DataBar value={Math.abs(tick.volume_change || 0)} max={maxVals.maxVolChg} color="cyan" />;
      case 'volume': return <DataBar value={tick.volume || tick.volume_traded} max={maxVals.maxVolume} color="purple" />;
      case 'oi': return <DataBar value={tick.oi} max={maxVals.maxOi} color="orange" />;
      case 'oi_change': {
        const oiChg = tick.oi_change || 0;
        return <DataBar 
          value={Math.abs(oiChg)} 
          max={maxVals.maxOiChg} 
          color={oiChg >= 0 ? "green" : "red"} 
          showSign={true}
          originalValue={oiChg}
        />;
      }
      case 'time': {
        // Green text for same-second ticks instead of yellow background
        const timeColor = sameSecond ? 'text-cyan-400 font-bold' : 'text-[#f4f4f5]';
        return <span className={`${timeColor} ${fontSize} font-mono font-semibold`}>{formatTimeIST(tick.timestamp)}</span>;
      }
      default: return '-';
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setLocalFilter('all');
    setMinLtq(0);
    setMinBidQty(0);
    setMinAskQty(0);
    setBidQtyEnabled(false);
    setAskQtyEnabled(false);
    setBidAskQtyMode('AND');
    setPosLessThanEnabled(false);
    setPosLessThanValue('');
    setPosGreaterThanEnabled(false);
    setPosGreaterThanValue('');
    setPosFilterMode('OR');
    setLtpDiffLessThanEnabled(false);
    setLtpDiffLessThanValue('');
    setLtpDiffGreaterThanEnabled(false);
    setLtpDiffGreaterThanValue('');
    setLtpDiffFilterMode('OR');
    setMinVolChg(0);
    setSpreadLessThanEnabled(false);
    setSpreadLessThanValue(0);
    setSpreadGreaterThanEnabled(false);
    setSpreadGreaterThanValue(0);
  };

  // Get exchange badge color
  const getExchangeBadge = () => {
    if (exchange === 'MCX') return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    if (exchange === 'NFO') return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  return (
    <div className="border border-[#333] rounded-xl bg-gradient-to-b from-[#0f0f0f] to-[#0a0a0a] flex flex-col overflow-hidden h-full shadow-xl relative">
      {/* Premium Loading UI - Shows until data is received */}
      {showLoadingOverlay && (
        <div className="absolute inset-0 flex items-center justify-center bg-[#0a0a0a]/95 backdrop-blur-sm z-30">
          <div className="flex flex-col items-center gap-3 p-4">
            <div className="relative">
              <div className="w-12 h-12 rounded-full border-3 border-cyan-500/20 border-t-cyan-500 animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <svg className="w-6 h-6 text-cyan-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
            </div>
            <div className="text-center">
              <p className="text-cyan-400 text-[12px] font-semibold animate-pulse">Loading...</p>
              <p className="text-[#71717A] text-[10px] mt-1">{symbol}</p>
            </div>
          </div>
        </div>
      )}
      
      {/* DELETE BUTTON - Fixed at top-right corner, always visible */}
      {onRemove && (
        <button
          onClick={onRemove}
          className="absolute top-1 right-1 z-20 w-6 h-6 flex items-center justify-center rounded-full bg-red-500/10 hover:bg-red-500/30 text-red-400 hover:text-red-300 transition-colors"
          title="Remove symbol"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      )}
      
      {/* HEADER - Premium Styling with LARGER TEXT */}
      <div className="px-3 py-2 bg-gradient-to-r from-[#1a1a1a] via-[#151515] to-[#1a1a1a] border-b border-[#333]">
        <div className="flex items-center justify-between pr-6">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            {/* Loading indicator - yellow while loading, green when stable */}
            {showLoadingOverlay || !dataStable ? (
              <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse shadow-lg shadow-yellow-500/50 flex-shrink-0" title="Loading data..." />
            ) : (
              <div className="w-2 h-2 bg-green-500 rounded-full shadow-lg shadow-green-500/50 flex-shrink-0" />
            )}
            <span className="text-[13px] font-bold text-white tracking-wide truncate" title={symbol}>
              {symbol}
              {instanceNum && instanceNum > 1 && (
                <span className="ml-1 text-[10px] text-cyan-400 font-normal">({instanceNum})</span>
              )}
            </span>
            <span className={`text-[9px] px-1.5 py-0.5 rounded border font-semibold flex-shrink-0 ${getExchangeBadge()}`}>
              {exchange}
            </span>
          </div>
          <div className="flex items-center gap-1 flex-shrink-0">
            {/* Filter Dropdown */}
            <Select value={localFilter} onValueChange={setLocalFilter}>
              <SelectTrigger className="h-6 w-[65px] text-[9px] bg-[#1a1a1a] border-[#333] text-white font-medium">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#111] border-[#333] text-[10px]">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="buy">Buys</SelectItem>
                <SelectItem value="sell">Sells</SelectItem>
                <SelectItem value="large_ltq">Lg LTQ</SelectItem>
                <SelectItem value="large_vol">Lg Vol</SelectItem>
                <SelectItem value="oi_up">OI↑</SelectItem>
                <SelectItem value="oi_down">OI↓</SelectItem>
              </SelectContent>
            </Select>
            {/* Smart Filters Panel - Per Symbol */}
            <SmartFiltersPanel 
              filters={localSmartFilters} 
              setFilters={setLocalSmartFilters} 
              isCompact={compact}
              currentSymbol={symbol}
            />
            {/* Advanced Filter Toggle */}
            <Button
              variant={showAdvanced ? "default" : "ghost"}
              size="sm"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className={`h-6 px-1.5 text-[8px] ${showAdvanced ? 'bg-blue-600 text-white' : 'text-[#71717A] hover:text-white'}`}
              title="Advanced Filters"
            >
              <Filter className="w-3 h-3 mr-0.5" />
              Adv
            </Button>
            {/* Column Settings Toggle */}
            <Button
              variant={showColumnSettings ? "default" : "ghost"}
              size="sm"
              onClick={() => setShowColumnSettings(!showColumnSettings)}
              className={`h-6 px-1.5 text-[8px] ${showColumnSettings ? 'bg-purple-600 text-white' : 'text-[#71717A] hover:text-white'}`}
              title="Column Settings"
            >
              <Settings2 className="w-3 h-3 mr-0.5" />
              Cols ({localVisibleCols.length})
            </Button>
            {/* Pop-out Buttons - Single + Multi Window */}
            <div className="flex items-center">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openPopOutWindow(token, symbol, exchange)}
                className="h-6 w-6 p-0 text-[#71717A] hover:text-cyan-400"
                title="Pop out to new window"
              >
                <ExternalLink className="w-3 h-3" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openAdditionalPopOutWindow(token, symbol, exchange)}
                className="h-6 w-6 p-0 text-[#71717A] hover:text-green-400"
                title="Open additional window (up to 5)"
              >
                <Plus className="w-3 h-3" />
              </Button>
            </div>
          </div>
        </div>
      </div>
      
      {/* Advanced Filter Panel - Same as Pop-out Window */}
      {showAdvanced && (
        <div className="flex items-center gap-2 px-2 py-1.5 bg-[#111] border-b border-[#333] flex-wrap">
          <span className="text-[9px] text-[#71717A] font-medium">Advanced Filters:</span>
          
          {/* LTQ Filter */}
          <div className="flex items-center gap-1">
            <span className="text-[9px] text-[#a1a1aa]">LTQ≥</span>
            <Input type="number" value={minLtq || ''} onChange={(e) => setMinLtq(parseInt(e.target.value) || 0)} placeholder="0" className="h-5 w-14 text-[10px] bg-[#0a0a0a] border-[#333] px-1 text-white" />
          </div>
          
          {/* Bid/Ask Qty Filters */}
          <div className="flex items-center gap-1 border-l border-[#444] pl-2">
            <span className="text-[9px] text-green-400 font-medium">Bid/Ask:</span>
            <button
              onClick={() => setBidAskQtyMode(prev => prev === 'AND' ? 'OR' : 'AND')}
              className={`h-5 px-1.5 text-[9px] rounded font-bold ${bidAskQtyMode === 'AND' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50' : 'bg-amber-500/20 text-amber-400 border border-amber-500/50'}`}
            >{bidAskQtyMode}</button>
            <button onClick={() => setBidQtyEnabled(!bidQtyEnabled)} className={`h-5 px-1.5 text-[9px] rounded ${bidQtyEnabled ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-[#1a1a1a] text-[#666] border border-[#333]'}`}>Bid≥</button>
            <Input type="number" value={minBidQty || ''} onChange={(e) => setMinBidQty(parseInt(e.target.value) || 0)} placeholder="0" disabled={!bidQtyEnabled} className={`h-5 w-14 text-[10px] bg-[#0a0a0a] border-[#333] px-1 text-white ${!bidQtyEnabled ? 'opacity-40' : ''}`} />
            <button onClick={() => setAskQtyEnabled(!askQtyEnabled)} className={`h-5 px-1.5 text-[9px] rounded ${askQtyEnabled ? 'bg-red-500/20 text-red-400 border border-red-500/50' : 'bg-[#1a1a1a] text-[#666] border border-[#333]'}`}>Ask≥</button>
            <Input type="number" value={minAskQty || ''} onChange={(e) => setMinAskQty(parseInt(e.target.value) || 0)} placeholder="0" disabled={!askQtyEnabled} className={`h-5 w-14 text-[10px] bg-[#0a0a0a] border-[#333] px-1 text-white ${!askQtyEnabled ? 'opacity-40' : ''}`} />
          </div>
          
          {/* Agg % Filters */}
          <div className="flex items-center gap-1 border-l border-[#444] pl-2">
            <span className="text-[9px] text-cyan-400 font-medium">Agg%:</span>
            <button onClick={() => setPosFilterMode(prev => prev === 'AND' ? 'OR' : 'AND')} className={`h-5 px-1 text-[8px] rounded font-bold ${posFilterMode === 'AND' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50' : 'bg-amber-500/20 text-amber-400 border border-amber-500/50'}`}>{posFilterMode}</button>
            <button onClick={() => setPosLessThanEnabled(!posLessThanEnabled)} className={`h-5 px-1.5 text-[9px] rounded ${posLessThanEnabled ? 'bg-red-500/20 text-red-400 border border-red-500/50' : 'bg-[#1a1a1a] text-[#666] border border-[#333]'}`}>&lt;</button>
            <Input type="number" value={posLessThanValue} onChange={(e) => setPosLessThanValue(e.target.value)} placeholder="0" disabled={!posLessThanEnabled} className={`h-5 w-12 text-[10px] bg-[#0a0a0a] border-[#333] px-1 text-white ${!posLessThanEnabled ? 'opacity-40' : ''}`} />
            <button onClick={() => setPosGreaterThanEnabled(!posGreaterThanEnabled)} className={`h-5 px-1.5 text-[9px] rounded ${posGreaterThanEnabled ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-[#1a1a1a] text-[#666] border border-[#333]'}`}>&gt;</button>
            <Input type="number" value={posGreaterThanValue} onChange={(e) => setPosGreaterThanValue(e.target.value)} placeholder="0" disabled={!posGreaterThanEnabled} className={`h-5 w-12 text-[10px] bg-[#0a0a0a] border-[#333] px-1 text-white ${!posGreaterThanEnabled ? 'opacity-40' : ''}`} />
          </div>
          
          {/* LTP Diff Filters */}
          <div className="flex items-center gap-1 border-l border-[#444] pl-2">
            <span className="text-[9px] text-blue-400 font-medium">LTP Diff:</span>
            <button onClick={() => setLtpDiffFilterMode(prev => prev === 'AND' ? 'OR' : 'AND')} className={`h-5 px-1 text-[8px] rounded font-bold ${ltpDiffFilterMode === 'AND' ? 'bg-purple-500/20 text-purple-400 border border-purple-500/50' : 'bg-amber-500/20 text-amber-400 border border-amber-500/50'}`}>{ltpDiffFilterMode}</button>
            <button onClick={() => setLtpDiffLessThanEnabled(!ltpDiffLessThanEnabled)} className={`h-5 px-1.5 text-[9px] rounded ${ltpDiffLessThanEnabled ? 'bg-red-500/20 text-red-400 border border-red-500/50' : 'bg-[#1a1a1a] text-[#666] border border-[#333]'}`}>&lt;</button>
            <Input type="number" value={ltpDiffLessThanValue} onChange={(e) => setLtpDiffLessThanValue(e.target.value)} placeholder="0" disabled={!ltpDiffLessThanEnabled} className={`h-5 w-12 text-[10px] bg-[#0a0a0a] border-[#333] px-1 text-white ${!ltpDiffLessThanEnabled ? 'opacity-40' : ''}`} />
            <button onClick={() => setLtpDiffGreaterThanEnabled(!ltpDiffGreaterThanEnabled)} className={`h-5 px-1.5 text-[9px] rounded ${ltpDiffGreaterThanEnabled ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-[#1a1a1a] text-[#666] border border-[#333]'}`}>&gt;</button>
            <Input type="number" value={ltpDiffGreaterThanValue} onChange={(e) => setLtpDiffGreaterThanValue(e.target.value)} placeholder="0" disabled={!ltpDiffGreaterThanEnabled} className={`h-5 w-12 text-[10px] bg-[#0a0a0a] border-[#333] px-1 text-white ${!ltpDiffGreaterThanEnabled ? 'opacity-40' : ''}`} />
          </div>
          
          {/* Vol Chg Filter */}
          <div className="flex items-center gap-1 border-l border-[#444] pl-2">
            <span className="text-[9px] text-purple-400 font-medium">VolChg≥</span>
            <Input type="number" value={minVolChg || ''} onChange={(e) => setMinVolChg(parseInt(e.target.value) || 0)} placeholder="0" className="h-5 w-14 text-[10px] bg-[#0a0a0a] border-purple-500/30 px-1 text-white" />
          </div>
          
          {/* Spread Filters */}
          <div className="flex items-center gap-1 border-l border-[#444] pl-2">
            <span className="text-[9px] text-amber-400 font-medium">Spread:</span>
            <button onClick={() => setSpreadLessThanEnabled(!spreadLessThanEnabled)} className={`h-5 px-1.5 text-[9px] rounded ${spreadLessThanEnabled ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-[#1a1a1a] text-[#666] border border-[#333]'}`}>&lt;</button>
            <Input type="number" value={spreadLessThanValue || ''} onChange={(e) => setSpreadLessThanValue(parseFloat(e.target.value) || 0)} placeholder="0" disabled={!spreadLessThanEnabled} className={`h-5 w-12 text-[10px] bg-[#0a0a0a] border-[#333] px-1 text-white ${!spreadLessThanEnabled ? 'opacity-40' : ''}`} />
            <button onClick={() => setSpreadGreaterThanEnabled(!spreadGreaterThanEnabled)} className={`h-5 px-1.5 text-[9px] rounded ${spreadGreaterThanEnabled ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-[#1a1a1a] text-[#666] border border-[#333]'}`}>&gt;</button>
            <Input type="number" value={spreadGreaterThanValue || ''} onChange={(e) => setSpreadGreaterThanValue(parseFloat(e.target.value) || 0)} placeholder="0" disabled={!spreadGreaterThanEnabled} className={`h-5 w-12 text-[10px] bg-[#0a0a0a] border-[#333] px-1 text-white ${!spreadGreaterThanEnabled ? 'opacity-40' : ''}`} />
          </div>
          
          {/* Clear Button */}
          <Button variant="ghost" size="sm" onClick={clearFilters} className="h-5 px-1.5 text-[9px] text-red-400 hover:text-red-300 hover:bg-red-500/10 font-medium">
            <X className="w-3 h-3 mr-0.5" />Clear
          </Button>
          
          {/* Telegram Alert Toggle */}
          <div className="flex items-center gap-1 border-l border-[#444] pl-2">
            <button
              onClick={() => setTelegramAlertEnabled(!telegramAlertEnabled)}
              className={`h-5 px-2 text-[9px] rounded flex items-center gap-1 font-medium transition-colors ${
                telegramAlertEnabled 
                  ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50 hover:bg-blue-500/30' 
                  : 'bg-[#1a1a1a] text-[#666] border border-[#333] hover:text-[#888]'
              }`}
              title={telegramAlertEnabled ? "Telegram alerts ON - Filter matches will be sent to Telegram" : "Click to enable Telegram alerts for this table"}
            >
              {telegramAlertEnabled ? <Bell className="w-3 h-3" /> : <BellOff className="w-3 h-3" />}
              <span>TG</span>
            </button>
          </div>
        </div>
      )}
      
      {/* Column Settings Panel - Same as Pop-out Window */}
      {showColumnSettings && (
        <div className="px-3 py-2 bg-[#111] border-b border-[#333]">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-[11px] text-[#71717A] font-semibold">Columns (click to toggle, arrows to reorder):</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setLocalVisibleCols(['time', 'ltp', 'ltp_diff', 'trade_pos', 'bid_qty', 'ask_qty', 'ltq', 'spread']);
                setLocalColumnOrder(['time', 'ltp', 'ltp_diff', 'trade_pos', 'bid_qty', 'ask_qty', 'ltq', 'spread', ...COLUMNS.filter(c => !['time', 'ltp', 'ltp_diff', 'trade_pos', 'bid_qty', 'ask_qty', 'ltq', 'spread'].includes(c.key)).map(c => c.key)]);
              }}
              className="h-5 px-2 text-[10px] text-orange-400 hover:text-orange-300"
            >
              Clear Sort
            </Button>
          </div>
          <div className="flex items-center gap-1 flex-wrap">
            {localColumnOrder.map((colKey, idx) => {
              const col = COLUMNS.find(c => c.key === colKey);
              if (!col) return null;
              const isVisible = localVisibleCols.includes(colKey);
              return (
                <div key={colKey} className="flex items-center bg-[#0a0a0a] border border-[#333] rounded">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveColumnUp(colKey)}
                    disabled={idx === 0}
                    className="h-6 px-1 text-[#52525B] hover:text-white disabled:opacity-30"
                  >
                    <ChevronUp className="w-3 h-3" />
                  </Button>
                  <Button
                    variant={isVisible ? "default" : "ghost"}
                    size="sm"
                    onClick={() => toggleLocalColumn(colKey)}
                    className={`h-6 px-2 text-[10px] rounded-none ${isVisible ? 'bg-purple-600 text-white' : 'text-[#71717A]'}`}
                  >
                    {col.label}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => moveColumnDown(colKey)}
                    disabled={idx === localColumnOrder.length - 1}
                    className="h-6 px-1 text-[#52525B] hover:text-white disabled:opacity-30"
                  >
                    <ChevronDown className="w-3 h-3" />
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}
      
      {/* FILTER SAVE MODE PANEL - Always visible for CSV download access */}
      <div className="flex items-center gap-2 px-2 py-1.5 bg-gradient-to-r from-[#0d1117] to-[#0a0a0a] border-b border-[#333]">
        <span className="text-[9px] text-[#71717A] font-medium">Filter Results:</span>
        <FilterSaveModeToggle
          mode={filterSaveMode}
          setMode={setFilterSaveMode}
          savedCount={savedTicksCount}
          onDownload={downloadSavedTicks}
          onDownloadFiltered={downloadFilteredTicks}
          filteredCount={filteredTicks.length}
          onClear={clearSavedTicks}
          autoDownload={autoDownload}
          setAutoDownload={setAutoDownload}
          alertEnabled={alertEnabled}
          setAlertEnabled={setAlertEnabled}
          compact={compact}
        />
        {isGlobalModeActive && (
          <span className="text-[8px] text-indigo-400 font-bold px-1.5 py-0.5 rounded bg-indigo-900/30 border border-indigo-500/30 flex items-center gap-0.5">
            <Filter className="w-2.5 h-2.5" />
            Global
          </span>
        )}
        {hasLocalOverride && (
          <span className="text-[8px] text-amber-400 font-bold px-1.5 py-0.5 rounded bg-amber-900/30 border border-amber-500/30">
            Local Override
          </span>
        )}
        {filterSaveMode === FILTER_SAVE_MODES.SAVE && (
          <span className="text-[8px] text-green-400 animate-pulse">● Recording</span>
        )}
        {filterSaveMode === FILTER_SAVE_MODES.HIDE && (
          <span className="text-[8px] text-orange-400">● Waiting for new matches</span>
        )}
        {autoDownload && (
          <span className="text-[8px] text-purple-400">● Auto-download at market close</span>
        )}
      </div>
      
      {/* TABLE HEADER - Premium Styling with VISIBLE text */}
      <div className={`flex bg-gradient-to-r from-[#1a1a1a] to-[#151515] border-b border-[#333] ${compact ? 'text-[9px]' : 'text-[11px]'} font-bold select-none`}>
        {orderedCols.map((key, idx) => (
          <div 
            key={key}
            style={getColStyle(key)}
            className={`relative ${compact ? 'px-1 py-1.5' : 'px-2 py-2.5'} text-center whitespace-nowrap overflow-hidden uppercase tracking-wider ${
              key === 'agg_buy' ? 'text-green-400 bg-green-900/30' : 
              key === 'agg_sell' ? 'text-red-400 bg-red-900/30' : 
              'text-[#d4d4d8]'
            }`}
          >
            {COLUMNS.find(c => c.key === key)?.label}
            {/* Resize Handle - Enhanced visibility */}
            {idx < orderedCols.length - 1 && (
              <div
                className={`absolute right-0 top-0 bottom-0 w-3 cursor-col-resize flex items-center justify-center group/resize ${resizing === key ? 'bg-blue-500/30' : 'hover:bg-blue-500/20'}`}
                onMouseDown={(e) => handleMouseDown(e, key)}
              >
                {/* Visible grip line */}
                <div className={`w-0.5 h-3/5 rounded-full transition-all ${resizing === key ? 'bg-blue-500 w-1' : 'bg-[#555] group-hover/resize:bg-blue-400 group-hover/resize:w-0.5'}`} />
              </div>
            )}
          </div>
        ))}
      </div>
      
      {/* Virtualized Rows */}
      <div ref={parentRef} className="flex-1 overflow-auto">
        <div 
          style={{ height: `${rowVirtualizer.getTotalSize()}px`, position: 'relative', willChange: 'contents' }}
          className="contain-paint"
        >
          {rowVirtualizer.getVirtualItems().map(vRow => {
            const tick = filteredTicks[vRow.index];
            const prevTick = vRow.index > 0 ? filteredTicks[vRow.index - 1] : null;
            const sameSecond = prevTick && getSeconds(tick.timestamp) === getSeconds(prevTick.timestamp);
            
            // Smart filter highlight class
            const smartFilterClass = smartFilters.enabled ? getSmartFilterRowClass(tick, smartFilters) : '';
            
            // Check if this is first row in a filter group - show header (only if labels enabled)
            const showFilterHeader = smartFilters.enabled && tick._smartFilterMatch && tick._isFirstInGroup && smartFilters.showLabels;
            const filterHeader = showFilterHeader ? getFilterHeaderRow(tick, true, compact, smartFilters.showLabels) : null;
            
            // Calculate heights
            const headerHeight = showFilterHeader ? (compact ? 18 : 22) : 0;
            const rowHeight = compact ? 24 : 30;
            
            return (
              <div
                key={vRow.index}
                className="absolute left-0 right-0"
                style={{ 
                  top: `${vRow.start}px`,
                  height: rowHeight + headerHeight,
                }}
              >
                {/* Filter Header Row - shows above matched group */}
                {filterHeader}
                
                {/* Data Row - optimized for performance */}
                <div
                  className={`flex items-center ${compact ? 'text-[10px]' : 'text-[12px]'} border-b border-[#222] hover:bg-[#1a1a1a] ${smartFilterClass}`}
                  style={{ 
                    height: `${rowHeight}px`,
                    contain: 'layout style paint'
                  }}
                >
                  {orderedCols.map(key => {
                    let cellBg = '';
                    // Remove yellow background for time - now using light blue text instead
                    if (key === 'agg_buy' && tick.agg_buy > 0) cellBg = 'bg-green-900/40';
                    else if (key === 'agg_sell' && tick.agg_sell > 0) cellBg = 'bg-red-900/40';
                    
                    // Don't truncate LTP, bid_price, ask_price columns
                    const noTruncate = ['ltp', 'bid_price', 'ask_price', 'time'].includes(key);
                    
                    return (
                      <div 
                        key={key} 
                        style={getColStyle(key)}
                        className={`${compact ? 'px-1' : 'px-2'} ${noTruncate ? 'whitespace-nowrap overflow-hidden' : 'truncate'} ${cellBg}`}
                      >
                        {renderCell(tick, key, prevTick, sameSecond)}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      
      {/* FOOTER - Premium Styling with VISIBLE stats and Load More */}
      <div className={`${compact ? 'h-7 px-2' : 'h-9 px-3'} bg-gradient-to-r from-[#1a1a1a] via-[#151515] to-[#1a1a1a] border-t border-[#333] flex items-center justify-between`}>
        <div className="flex items-center gap-2">
          <span className={`${compact ? 'text-[11px]' : 'text-[13px]'} font-bold text-white`}>{stableFilteredCount}</span>
          <span className={`${compact ? 'text-[9px]' : 'text-[11px]'} text-[#a1a1aa] font-medium`}>/ {stableTotalCount} ticks</span>
          {/* Load More Button */}
          {onLoadMore && hasMoreHistory && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onLoadMore(token)}
              disabled={isLoadingMore}
              className={`${compact ? 'h-5 px-1.5 text-[8px]' : 'h-6 px-2 text-[9px]'} text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/20`}
            >
              {isLoadingMore ? (
                <RefreshCw className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} animate-spin`} />
              ) : (
                <>
                  <Download className={`${compact ? 'w-2.5 h-2.5' : 'w-3 h-3'} mr-1`} />
                  +5K
                </>
              )}
            </Button>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {/* Saved ticks indicator */}
          {savedTicksCount > 0 && (
            <span className={`${compact ? 'text-[8px] px-1.5 py-0.5' : 'text-[10px] px-2 py-1'} rounded bg-purple-900/40 text-purple-400 font-bold`}>
              💾 {savedTicksCount}
            </span>
          )}
          <span className={`${compact ? 'text-[8px] px-1.5 py-0.5' : 'text-[10px] px-2 py-1'} rounded bg-green-900/40 text-green-400 font-bold`}>
            B: {stableBuySellCounts.buyCount}
          </span>
          <span className={`${compact ? 'text-[8px] px-1.5 py-0.5' : 'text-[10px] px-2 py-1'} rounded bg-red-900/40 text-red-400 font-bold`}>
            S: {stableBuySellCounts.sellCount}
          </span>
        </div>
      </div>
    </div>
  );
});

// Main TickHistory Component
export const TickHistory = ({ 
  instruments, 
  subscribedTokens, 
  myWatchlistTokens = [],
  onClose, 
  tickData = {}, 
  marketStatus = {},
  subscribeInstruments,
  unsubscribeInstruments
}) => {
  // Access background tick history from App context
  const appContext = useContext(AppContext);
  const backgroundTickHistoryRef = appContext?.backgroundTickHistoryRef;
  const backgroundStats = appContext?.backgroundStats;
  
  // Track if grid settings have been loaded from database
  const [gridSettingsLoaded, setGridSettingsLoaded] = useState(false);
  const gridSettingsSaveTimeoutRef = useRef(null);
  
  // Load grid settings from database on mount (per-user settings)
  useEffect(() => {
    const loadGridSettings = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        if (!token) {
          setGridSettingsLoaded(true);
          return;
        }
        
        const response = await apiClient.get('/user/grid-settings');
        if (response.data.success && response.data.settings) {
          const settings = response.data.settings;
          
          // Apply loaded settings
          if (settings.gridCellPositions && Object.keys(settings.gridCellPositions).length > 0) {
            setGridCellPositions(settings.gridCellPositions);
          }
          if (settings.gridVisibleCols && settings.gridVisibleCols.length > 0) {
            setGridVisibleCols(settings.gridVisibleCols);
          }
          if (settings.gridColumnOrder && settings.gridColumnOrder.length > 0) {
            setGridColumnOrder(settings.gridColumnOrder);
          }
          if (typeof settings.freePositionMode === 'boolean') {
            setFreePositionMode(settings.freePositionMode);
          }
          if (settings.gridSymbols && settings.gridSymbols.length > 0) {
            setGridSymbols(settings.gridSymbols);
          }
          
          // Restore per-symbol filter settings to localStorage
          if (settings.symbolFilters) {
            Object.entries(settings.symbolFilters).forEach(([key, value]) => {
              if (value) {
                localStorage.setItem(`gridcell_filters_${key}`, JSON.stringify(value));
              }
            });
            console.log('[GridSettings] Restored symbol filters from database');
          }
          
          // Restore per-symbol smart filters to localStorage
          if (settings.symbolSmartFilters) {
            Object.entries(settings.symbolSmartFilters).forEach(([key, value]) => {
              if (value) {
                localStorage.setItem(`smartfilters_${key}`, JSON.stringify(value));
              }
            });
            console.log('[GridSettings] Restored smart filters from database');
          }
          
          console.log('[GridSettings] Loaded from database');
        }
      } catch (err) {
        console.warn('[GridSettings] Failed to load from database, using localStorage:', err.message);
      } finally {
        setGridSettingsLoaded(true);
      }
    };
    
    loadGridSettings();
  }, []);
  
  // Save grid settings to database (debounced to prevent excessive API calls)
  const saveGridSettingsToDb = useCallback(async (settings) => {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;
      
      // Collect all per-symbol filter settings from localStorage
      const symbolFilters = {};
      const symbolSmartFilters = {};
      
      // Get filters for each symbol in the grid
      if (settings.gridSymbols) {
        settings.gridSymbols.forEach(sym => {
          // Try both gridId and token as storage keys
          const keys = [sym.id, sym.token, `${sym.token}`];
          keys.forEach(key => {
            // Regular filters
            const filterKey = `gridcell_filters_${key}`;
            const filterData = localStorage.getItem(filterKey);
            if (filterData) {
              try {
                symbolFilters[key] = JSON.parse(filterData);
              } catch {}
            }
            
            // Smart filters
            const smartKey = `smartfilters_${key}`;
            const smartData = localStorage.getItem(smartKey);
            if (smartData) {
              try {
                symbolSmartFilters[key] = JSON.parse(smartData);
              } catch {}
            }
          });
        });
      }
      
      // Include filter settings in the save
      const fullSettings = {
        ...settings,
        symbolFilters,
        symbolSmartFilters
      };
      
      await apiClient.post('/user/grid-settings', { settings: fullSettings });
      console.log('[GridSettings] Saved to database with filters');
    } catch (err) {
      console.warn('[GridSettings] Failed to save to database:', err.message);
    }
  }, []);
  
  // Debounced save - triggers 2 seconds after last change
  const debouncedSaveGridSettings = useCallback((settings) => {
    if (gridSettingsSaveTimeoutRef.current) {
      clearTimeout(gridSettingsSaveTimeoutRef.current);
    }
    gridSettingsSaveTimeoutRef.current = setTimeout(() => {
      saveGridSettingsToDb(settings);
    }, 2000);
  }, [saveGridSettingsToDb]);
  
  // Watchlist source selection - 'watchlist' or 'my_watchlist'
  const [watchlistSource, setWatchlistSource] = useState(() => {
    try { return localStorage.getItem('ticklog_watchlist_source') || 'watchlist'; } catch { return 'watchlist'; }
  });
  
  // Save watchlist source preference
  useEffect(() => {
    localStorage.setItem('ticklog_watchlist_source', watchlistSource);
  }, [watchlistSource]);
  
  // Combined tokens based on source selection
  const sourceTokens = useMemo(() => {
    if (watchlistSource === 'my_watchlist') {
      return myWatchlistTokens || [];
    }
    return subscribedTokens || [];
  }, [watchlistSource, subscribedTokens, myWatchlistTokens]);
  
  // Filter tokens to only show those that have tick data OR are in instruments
  const activeTokens = useMemo(() => {
    const tokensWithData = Object.keys(tickData).map(t => parseInt(t));
    const marketStatus = getCurrentMarketStatus();
    
    // When market is closed, show all tokens from watchlist that have instrument info
    // This allows users to see historical data even when no live data is available
    if (!marketStatus.isOpen) {
      return sourceTokens.filter(t => {
        const hasInstrumentInfo = instruments && instruments[t];
        const symbolFromInstruments = instruments?.[t]?.tradingsymbol;
        const hasProperSymbol = symbolFromInstruments && typeof symbolFromInstruments === 'string' && symbolFromInstruments.length > 0;
        return hasInstrumentInfo && hasProperSymbol;
      });
    }
    
    // Market open - show tokens that are in source AND either have tick data OR have instrument info
    // FILTER OUT tokens without proper symbol names (Token_XXXXXX)
    return sourceTokens.filter(t => {
      const hasTickData = tokensWithData.includes(t);
      const hasInstrumentInfo = instruments && instruments[t];
      // Check if the token has a proper symbol name from instruments
      const symbolFromInstruments = instruments?.[t]?.tradingsymbol;
      // Also check tick data for symbol name
      const tickDataForToken = tickData[t];
      const symbolFromTicks = tickDataForToken && tickDataForToken.length > 0 
        ? tickDataForToken[0]?.tradingsymbol 
        : null;
      // Use symbol from instruments first, then from tick data
      const symbolName = symbolFromInstruments || symbolFromTicks;
      const hasProperSymbol = symbolName && typeof symbolName === 'string' && symbolName.length > 0;
      // Only include if has proper symbol name
      return (hasTickData || hasInstrumentInfo) && hasProperSymbol;
    });
  }, [sourceTokens, tickData, instruments]);
  
  // Sync available symbols to localStorage for pop-out windows
  useEffect(() => {
    if (activeTokens.length > 0 && instruments && Object.keys(instruments).length > 0) {
      const symbolsForPopout = activeTokens.map(token => {
        const inst = getInstrumentByToken(instruments, token);
        return {
          token: token,
          symbol: inst?.tradingsymbol || getSymbolName(instruments, token),
          exchange: inst?.exchange || 'NSE'
        };
      }).filter(s => s.symbol && s.symbol.length > 0);
      
      if (symbolsForPopout.length > 0) {
        localStorage.setItem('popout_available_symbols', JSON.stringify(symbolsForPopout));
      }
    }
  }, [activeTokens, instruments]);
  
  // Force update helper for re-rendering after loading more ticks
  const [, forceUpdate] = useState(0);
  
  // View state
  const [viewMode, setViewMode] = useState("single"); // single, multi, grid
  const [selectedToken, setSelectedToken] = useState(null);
  
  // Smart Filters state
  const [smartFilters, setSmartFilters] = useState(DEFAULT_SMART_FILTERS);
  
  // Multi and Grid symbols - persisted to localStorage
  const [multiSymbols, setMultiSymbols] = useState(() => {
    try { 
      const saved = JSON.parse(localStorage.getItem('ticklog_multi_symbols'));
      return Array.isArray(saved) ? saved : [];
    } catch { return []; }
  });
  // Grid symbols - now supports multi-instance (same symbol up to 5 times)
  // Format: array of objects {id: 'token_instance', token: number, instance: number}
  const [gridSymbols, setGridSymbols] = useState(() => {
    try { 
      const saved = JSON.parse(localStorage.getItem('ticklog_grid_symbols'));
      if (!Array.isArray(saved)) return [];
      // Migration: Convert old format (array of numbers) to new format (array of objects)
      if (saved.length > 0 && typeof saved[0] === 'number') {
        return saved.map((token, idx) => ({
          id: `${token}_1`,
          token: token,
          instance: 1
        }));
      }
      return saved;
    } catch { return []; }
  });
  const [gridColumns, setGridColumns] = useState(2);
  
  // Column settings with ordering - SEPARATE for Single, Multi, Grid
  const defaultCols = ['ltp', 'ltp_diff', 'bid_price', 'ask_price', 'bid_qty', 'ask_qty', 'trade_pos', 'spread', 'ltq', 'vol_chg', 'time'];
  const defaultOrder = COLUMNS.map(c => c.key);
  
  // Single view columns
  // Valid column keys for filtering out removed columns from localStorage
  const validColumnKeys = COLUMNS.map(c => c.key);
  
  const [visibleCols, setVisibleCols] = useState(() => {
    try { 
      const saved = JSON.parse(localStorage.getItem('ticklog_cols')) || defaultCols;
      return saved.filter(col => validColumnKeys.includes(col));
    } catch { return defaultCols; }
  });
  const [columnOrder, setColumnOrder] = useState(() => {
    try { 
      const saved = JSON.parse(localStorage.getItem('ticklog_order')) || defaultOrder;
      return saved.filter(col => validColumnKeys.includes(col));
    } catch { return defaultOrder; }
  });
  
  // Multi view columns (separate settings)
  const [multiVisibleCols, setMultiVisibleCols] = useState(() => {
    try { 
      const saved = JSON.parse(localStorage.getItem('ticklog_multi_cols')) || defaultCols;
      return saved.filter(col => validColumnKeys.includes(col));
    } catch { return defaultCols; }
  });
  const [multiColumnOrder, setMultiColumnOrder] = useState(() => {
    try { 
      const saved = JSON.parse(localStorage.getItem('ticklog_multi_order')) || defaultOrder;
      return saved.filter(col => validColumnKeys.includes(col));
    } catch { return defaultOrder; }
  });
  
  // Grid view columns (separate settings)
  const [gridVisibleCols, setGridVisibleCols] = useState(() => {
    try { 
      const saved = JSON.parse(localStorage.getItem('ticklog_grid_cols')) || defaultCols;
      return saved.filter(col => validColumnKeys.includes(col));
    } catch { return defaultCols; }
  });
  const [gridColumnOrder, setGridColumnOrder] = useState(() => {
    try { 
      const saved = JSON.parse(localStorage.getItem('ticklog_grid_order')) || defaultOrder;
      return saved.filter(col => validColumnKeys.includes(col));
    } catch { return defaultOrder; }
  });
  
  // Custom Layout Mode - allows resize and drag-drop reordering
  const [customLayoutMode, setCustomLayoutMode] = useState(false);
  const [gridCellSizes, setGridCellSizes] = useState(() => {
    try {
      const saved = localStorage.getItem('ticklog_grid_cell_sizes');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  
  // Cell positions for absolute positioning (x, y coordinates)
  const [gridCellPositions, setGridCellPositions] = useState(() => {
    try {
      const saved = localStorage.getItem('ticklog_grid_cell_positions');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  
  // Free position mode - allows placing tables anywhere
  const [freePositionMode, setFreePositionMode] = useState(() => {
    try {
      return localStorage.getItem('ticklog_free_position_mode') === 'true';
    } catch { return false; }
  });

  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  // Grid gap control - user adjustable spacing between tables
  const [gridGap, setGridGap] = useState(() => {
    try {
      return parseInt(localStorage.getItem('ticklog_grid_gap')) || 0;
    } catch { return 0; }
  });

  // Save column settings to localStorage
  useEffect(() => {
    localStorage.setItem('ticklog_cols', JSON.stringify(visibleCols));
    localStorage.setItem('ticklog_order', JSON.stringify(columnOrder));
  }, [visibleCols, columnOrder]);
  
  useEffect(() => {
    localStorage.setItem('ticklog_multi_cols', JSON.stringify(multiVisibleCols));
    localStorage.setItem('ticklog_multi_order', JSON.stringify(multiColumnOrder));
  }, [multiVisibleCols, multiColumnOrder]);
  
  useEffect(() => {
    localStorage.setItem('ticklog_grid_cols', JSON.stringify(gridVisibleCols));
    localStorage.setItem('ticklog_grid_order', JSON.stringify(gridColumnOrder));
    // Also save to database (debounced)
    if (gridSettingsLoaded) {
      debouncedSaveGridSettings({
        gridCellPositions,
        gridVisibleCols,
        gridColumnOrder,
        freePositionMode,
        gridSymbols
      });
    }
  }, [gridVisibleCols, gridColumnOrder, gridSettingsLoaded, debouncedSaveGridSettings, gridCellPositions, freePositionMode, gridSymbols]);
  
  // ==================== GLOBAL FILTER SAVE MODE (Multi & Grid Views) ====================
  // This controls ALL tables at once - individual tables can still override
  const [globalFilterSaveMode, setGlobalFilterSaveMode] = useState(() => {
    try {
      return localStorage.getItem('global_filter_save_mode') || FILTER_SAVE_MODES.OFF;
    } catch { return FILTER_SAVE_MODES.OFF; }
  });
  
  const [globalAutoDownload, setGlobalAutoDownload] = useState(() => {
    try {
      return localStorage.getItem('global_auto_download') === 'true';
    } catch { return false; }
  });
  
  // Global Alert state
  const [globalAlertEnabled, setGlobalAlertEnabled] = useState(() => {
    try {
      return localStorage.getItem('global_alert_enabled') === 'true';
    } catch { return false; }
  });
  
  // Save global alert setting
  useEffect(() => {
    localStorage.setItem('global_alert_enabled', globalAlertEnabled.toString());
  }, [globalAlertEnabled]);
  
  // Global saved ticks - aggregated from all tables for download
  // Use ref to avoid re-renders, only update count state
  const globalSavedTicksRef = useRef([]);
  const [globalSavedTicksCount, setGlobalSavedTicksCount] = useState(() => {
    try {
      const saved = localStorage.getItem('global_saved_ticks');
      if (saved) {
        const parsed = JSON.parse(saved);
        globalSavedTicksRef.current = parsed;
        return parsed.length;
      }
    } catch {}
    return 0;
  });
  const globalSavedTickIdsRef = useRef(new Set(globalSavedTicksRef.current.map(t => getTickUniqueId(t))));
  const globalHideTimestampRef = useRef(null);
  
  // State for global filter dropdown visibility
  const [showGlobalFilterDropdown, setShowGlobalFilterDropdown] = useState(false);
  const globalFilterDropdownRef = useRef(null);
  
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (globalFilterDropdownRef.current && !globalFilterDropdownRef.current.contains(event.target)) {
        setShowGlobalFilterDropdown(false);
      }
    };
    
    if (showGlobalFilterDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showGlobalFilterDropdown]);
  
  // Save global filter mode to localStorage
  useEffect(() => {
    localStorage.setItem('global_filter_save_mode', globalFilterSaveMode);
    if (globalFilterSaveMode === FILTER_SAVE_MODES.HIDE) {
      globalHideTimestampRef.current = Date.now();
    }
  }, [globalFilterSaveMode]);
  
  // Save global auto-download setting
  useEffect(() => {
    localStorage.setItem('global_auto_download', globalAutoDownload.toString());
  }, [globalAutoDownload]);
  
  // Save global ticks to localStorage (debounced) - use ref
  const saveGlobalTicksTimeoutRef = useRef(null);
  const saveGlobalTicksToStorage = useCallback(() => {
    if (saveGlobalTicksTimeoutRef.current) {
      clearTimeout(saveGlobalTicksTimeoutRef.current);
    }
    saveGlobalTicksTimeoutRef.current = setTimeout(() => {
      try {
        const ticksToSave = globalSavedTicksRef.current.slice(0, 10000);
        localStorage.setItem('global_saved_ticks', JSON.stringify(ticksToSave));
      } catch (e) {
        console.warn('Could not save global ticks to localStorage:', e);
      }
    }, 2000); // Debounce 2 seconds
  }, []);
  
  // Function to add ticks to global saved (called from GridCell via callback)
  const addToGlobalSavedTicks = useCallback((ticks, symbol) => {
    if (globalFilterSaveMode !== FILTER_SAVE_MODES.SAVE) return;
    
    let newTicksAdded = false;
    ticks.forEach(tick => {
      const tickId = getTickUniqueId(tick);
      if (!globalSavedTickIdsRef.current.has(tickId)) {
        globalSavedTickIdsRef.current.add(tickId);
        globalSavedTicksRef.current.unshift({ ...tick, _symbol: symbol, _savedAt: Date.now() });
        newTicksAdded = true;
      }
    });
    
    if (newTicksAdded) {
      // Trim to max limit
      if (globalSavedTicksRef.current.length > MAX_SAVED_TICKS) {
        const removed = globalSavedTicksRef.current.splice(MAX_SAVED_TICKS);
        removed.forEach(t => globalSavedTickIdsRef.current.delete(getTickUniqueId(t)));
      }
      // Update count (throttled to prevent too many renders)
      setGlobalSavedTicksCount(globalSavedTicksRef.current.length);
      // Save to localStorage (debounced)
      saveGlobalTicksToStorage();
    }
  }, [globalFilterSaveMode, saveGlobalTicksToStorage]);
  
  // Download all global saved ticks as Excel with multiple tabs (one per symbol)
  const downloadGlobalSavedTicks = useCallback(() => {
    console.log('[Global Excel] Attempting download...', {
      savedTicksCount: globalSavedTicksRef.current.length
    });
    
    if (globalSavedTicksRef.current.length === 0) {
      console.log('[Global Excel] No ticks - showing alert');
      window.alert('📋 No saved ticks to download!\n\n' +
            '👉 To save filtered ticks globally:\n' +
            '1. Apply filters on your tables (LTQ, Spread, etc.)\n' +
            '2. Click "Save All" in Filtered Result bar\n' +
            '3. All filtered ticks from all tables will be saved\n' +
            '4. Then click CSV/Excel to download all at once');
      return;
    }
    
    try {
      // Group ticks by symbol
      const ticksBySymbol = {};
      globalSavedTicksRef.current.forEach(tick => {
        const symbol = tick._symbol || tick.trading_symbol || 'Unknown';
        if (!ticksBySymbol[symbol]) {
          ticksBySymbol[symbol] = [];
        }
        ticksBySymbol[symbol].push(tick);
      });
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      const headers = ['Timestamp', 'LTP', 'LTP Diff', 'Bid', 'Ask', 'Bid Qty', 'Ask Qty', 'AGG%', 'Spread', 'LTQ', 'Vol Chg', 'Volume', 'OI'];
      
      // Create a sheet for each symbol
      Object.entries(ticksBySymbol).forEach(([symbol, ticks]) => {
        const rows = ticks.map(tick => [
          tick.timestamp || new Date(tick.timestamp_ms || tick.exchange_timestamp).toISOString(),
          tick.last_price || 0,
          tick.ltp_diff || 0,
          tick.bid_price || 0,
          tick.ask_price || 0,
          tick.bid_qty || 0,
          tick.ask_qty || 0,
          calculateTradePosition(tick)?.toFixed(2) || '',
          tick.spread || ((tick.ask_price || 0) - (tick.bid_price || 0)),
          tick.last_traded_quantity || 0,
          tick.volume_change || 0,
          tick.volume || tick.volume_traded || 0,
          tick.oi || 0
        ]);
        
        // Create worksheet data with headers
        const wsData = [headers, ...rows];
        const ws = XLSX.utils.aoa_to_sheet(wsData);
        
        // Set column widths
        ws['!cols'] = headers.map(() => ({ wch: 15 }));
        
        // Sanitize sheet name (Excel has 31 char limit and doesn't allow certain chars)
        let sheetName = symbol.substring(0, 31).replace(/[:\\/\\?\\*\\[\\]]/g, '_');
        
        // Add worksheet to workbook
        XLSX.utils.book_append_sheet(wb, ws, sheetName);
      });
      
      // Also create a "Summary" sheet with all data
      const allRows = globalSavedTicksRef.current.map(tick => [
        tick._symbol || tick.trading_symbol || 'Unknown',
        tick.timestamp || new Date(tick.timestamp_ms || tick.exchange_timestamp).toISOString(),
        tick.last_price || 0,
        tick.ltp_diff || 0,
        tick.bid_price || 0,
        tick.ask_price || 0,
        tick.bid_qty || 0,
        tick.ask_qty || 0,
        calculateTradePosition(tick)?.toFixed(2) || '',
        tick.spread || ((tick.ask_price || 0) - (tick.bid_price || 0)),
        tick.last_traded_quantity || 0,
        tick.volume_change || 0,
        tick.volume || tick.volume_traded || 0,
        tick.oi || 0
      ]);
      
      const summaryHeaders = ['Symbol', ...headers];
      const summaryWs = XLSX.utils.aoa_to_sheet([summaryHeaders, ...allRows]);
      summaryWs['!cols'] = summaryHeaders.map(() => ({ wch: 15 }));
      XLSX.utils.book_append_sheet(wb, summaryWs, 'ALL_DATA');
      
      // Generate filename
      const date = new Date().toISOString().split('T')[0];
      const time = new Date().toTimeString().split(' ')[0].replace(/:/g, '-');
      const filename = `FilteredTicks_${date}_${time}.xlsx`;
      
      // Write to buffer and download using file-saver
      const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      saveAs(blob, filename);
      
      console.log('[Global Excel] Download complete:', {
        symbols: Object.keys(ticksBySymbol).length,
        totalTicks: globalSavedTicksRef.current.length
      });
      
      // Show success alert
      window.alert(`✅ Excel Download started!\n\nFile: ${filename}\nSheets: ${Object.keys(ticksBySymbol).length + 1} (one per symbol + ALL_DATA)\nTotal Ticks: ${globalSavedTicksRef.current.length}\n\nCheck your browser's Downloads folder.`);
      
    } catch (error) {
      console.error('[Global Excel] Error creating Excel file:', error);
      window.alert('❌ Error creating Excel file. Falling back to CSV...');
      
      // Fallback to CSV
      const headers = ['Symbol', 'Timestamp', 'LTP', 'LTP Diff', 'Bid', 'Ask', 'Bid Qty', 'Ask Qty', 'AGG%', 'Spread', 'LTQ', 'Vol Chg', 'Volume', 'OI'];
      const rows = globalSavedTicksRef.current.map(tick => [
        tick._symbol || tick.trading_symbol || 'Unknown',
        tick.timestamp || new Date(tick.timestamp_ms || tick.exchange_timestamp).toISOString(),
        tick.last_price || 0,
        tick.ltp_diff || 0,
        tick.bid_price || 0,
        tick.ask_price || 0,
        tick.bid_qty || 0,
        tick.ask_qty || 0,
        calculateTradePosition(tick)?.toFixed(2) || '',
        tick.spread || ((tick.ask_price || 0) - (tick.bid_price || 0)),
        tick.last_traded_quantity || 0,
        tick.volume_change || 0,
        tick.volume || tick.volume_traded || 0,
        tick.oi || 0
      ]);
      
      const csvContent = [
        headers.join(','),
        ...rows.map(row => row.map(cell => {
          const str = String(cell);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        }).join(','))
      ].join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const date = new Date().toISOString().split('T')[0];
      const time = new Date().toTimeString().split(' ')[0].replace(/:/g, '-');
      const filename = `ALL_SYMBOLS_filtered_${date}_${time}.csv`;
      saveAs(blob, filename);
      
      window.alert(`✅ CSV Download started!\n\nFile: ${filename}\nTicks: ${globalSavedTicksRef.current.length}\n\nCheck your browser's Downloads folder.`);
    }
  }, []);
  
  // Clear all global saved ticks
  const clearGlobalSavedTicks = useCallback(() => {
    globalSavedTicksRef.current = [];
    globalSavedTickIdsRef.current.clear();
    globalHideTimestampRef.current = null;
    setGlobalSavedTicksCount(0);
    try {
      localStorage.removeItem('global_saved_ticks');
    } catch {}
  }, []);
  
  // Auto-download at market close for global
  useEffect(() => {
    if (!globalAutoDownload || globalSavedTicksRef.current.length === 0) return;
    
    const checkMarketClose = () => {
      const now = new Date();
      const istOffset = 5.5 * 60;
      const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
      const istMinutes = utcMinutes + istOffset;
      const istHour = Math.floor((istMinutes % (24 * 60)) / 60);
      const istMinute = Math.floor(istMinutes % 60);
      
      const today = new Date().toISOString().split('T')[0];
      const alreadyDownloaded = localStorage.getItem('global_lastAutoDownload') === today;
      
      if (alreadyDownloaded) return;
      
      // Check NSE close time (3:31 PM) - download all
      if (istHour === 15 && istMinute === 31) {
        console.log('[Global] Auto-downloading CSV at NSE market close');
        downloadGlobalSavedTicks();
        localStorage.setItem('global_lastAutoDownload', today);
        clearGlobalSavedTicks();
      }
    };
    
    const interval = setInterval(checkMarketClose, 60000);
    checkMarketClose();
    
    return () => clearInterval(interval);
  }, [globalAutoDownload, downloadGlobalSavedTicks, clearGlobalSavedTicks]);
  
  // Save Multi and Grid symbols to localStorage when they change
  useEffect(() => {
    // Always save to localStorage (even empty array to clear previous)
    localStorage.setItem('ticklog_multi_symbols', JSON.stringify(multiSymbols));
  }, [multiSymbols]);
  
  useEffect(() => {
    // Always save to localStorage (even empty array to clear previous)
    localStorage.setItem('ticklog_grid_symbols', JSON.stringify(gridSymbols));
  }, [gridSymbols]);
  
  // Sync column settings between Multi and Grid views
  // When multiVisibleCols changes, update gridVisibleCols to match
  const prevMultiColsRef = useRef(multiVisibleCols);
  useEffect(() => {
    // Only sync if multiVisibleCols actually changed (not initial render)
    if (JSON.stringify(prevMultiColsRef.current) !== JSON.stringify(multiVisibleCols)) {
      prevMultiColsRef.current = multiVisibleCols;
      setGridVisibleCols(multiVisibleCols);
      setGridColumnOrder(multiColumnOrder);
    }
  }, [multiVisibleCols, multiColumnOrder]);
  
  // When gridVisibleCols changes, update multiVisibleCols to match
  const prevGridColsRef = useRef(gridVisibleCols);
  useEffect(() => {
    // Only sync if gridVisibleCols actually changed (not initial render)
    if (JSON.stringify(prevGridColsRef.current) !== JSON.stringify(gridVisibleCols)) {
      prevGridColsRef.current = gridVisibleCols;
      setMultiVisibleCols(gridVisibleCols);
      setMultiColumnOrder(gridColumnOrder);
    }
  }, [gridVisibleCols, gridColumnOrder]);
  
  // Single view filter with advanced options
  const [singleFilter, setSingleFilter] = useState('all');
  const [singleMinLtq, setSingleMinLtq] = useState(0);
  const [singleMinBidQty, setSingleMinBidQty] = useState(0);
  const [singleMinAskQty, setSingleMinAskQty] = useState(0);
  
  // Bid/Ask Qty filter toggles and mode for Single view
  const [singleBidQtyEnabled, setSingleBidQtyEnabled] = useState(false);
  const [singleAskQtyEnabled, setSingleAskQtyEnabled] = useState(false);
  const [singleBidAskQtyMode, setSingleBidAskQtyMode] = useState('AND'); // 'AND' or 'OR'
  
  // Position % filters for Single view with AND/OR mode
  const [singlePosLessThanEnabled, setSinglePosLessThanEnabled] = useState(false);
  const [singlePosLessThanValue, setSinglePosLessThanValue] = useState('');
  const [singlePosGreaterThanEnabled, setSinglePosGreaterThanEnabled] = useState(false);
  const [singlePosGreaterThanValue, setSinglePosGreaterThanValue] = useState('');
  const [singlePosFilterMode, setSinglePosFilterMode] = useState('OR'); // 'AND' or 'OR'
  
  // LTP Diff filters for Single view with AND/OR mode (similar to AGG %)
  const [singleLtpDiffLessThanEnabled, setSingleLtpDiffLessThanEnabled] = useState(false);
  const [singleLtpDiffLessThanValue, setSingleLtpDiffLessThanValue] = useState('');
  const [singleLtpDiffGreaterThanEnabled, setSingleLtpDiffGreaterThanEnabled] = useState(false);
  const [singleLtpDiffGreaterThanValue, setSingleLtpDiffGreaterThanValue] = useState('');
  const [singleLtpDiffFilterMode, setSingleLtpDiffFilterMode] = useState('OR'); // 'AND' or 'OR'
  
  // Debounced LTP Diff values for Single view
  const [debouncedSingleLtpDiffLess, setDebouncedSingleLtpDiffLess] = useState('');
  const [debouncedSingleLtpDiffGreater, setDebouncedSingleLtpDiffGreater] = useState('');
  const singleLtpDiffDebounceRef = useRef(null);
  
  useEffect(() => {
    if (singleLtpDiffDebounceRef.current) clearTimeout(singleLtpDiffDebounceRef.current);
    singleLtpDiffDebounceRef.current = setTimeout(() => {
      setDebouncedSingleLtpDiffLess(singleLtpDiffLessThanValue);
      setDebouncedSingleLtpDiffGreater(singleLtpDiffGreaterThanValue);
    }, 300);
    return () => { if (singleLtpDiffDebounceRef.current) clearTimeout(singleLtpDiffDebounceRef.current); };
  }, [singleLtpDiffLessThanValue, singleLtpDiffGreaterThanValue]);
  
  // Volume Change filter
  const [singleMinVolChg, setSingleMinVolChg] = useState(0);
  
  // Spread filters with toggle switches
  const [spreadLessThanEnabled, setSpreadLessThanEnabled] = useState(false);
  const [spreadLessThanValue, setSpreadLessThanValue] = useState(0);
  const [spreadGreaterThanEnabled, setSpreadGreaterThanEnabled] = useState(false);
  const [spreadGreaterThanValue, setSpreadGreaterThanValue] = useState(0);
  
  // Performance Settings - User adjustable to reduce laptop heating
  // Performance settings - DEFAULT to backend_1min for auto-refresh
  const [performancePreset, setPerformancePreset] = useState(() => {
    try { 
      const saved = localStorage.getItem('ticklog_perf_preset');
      // Validate that saved preset still exists
      if (saved && PERFORMANCE_PRESETS[saved]) {
        return saved;
      }
      return 'instant'; // Default to instant mode for real-time updates
    } catch { return 'instant'; }
  });
  const [updateInterval, setUpdateInterval] = useState(() => {
    try { return parseInt(localStorage.getItem('ticklog_interval')) || 60000; } catch { return 60000; }
  });
  const [maxTicksPerSymbol, setMaxTicksPerSymbol] = useState(() => {
    try { return parseInt(localStorage.getItem('ticklog_max_ticks')) || 5000; } catch { return 5000; }
  });
  const [showPerfSettings, setShowPerfSettings] = useState(false);
  
  // Determine if current mode is 'backend' (fetch from DB with auto-refresh)
  const isBackendMode = useMemo(() => {
    const preset = PERFORMANCE_PRESETS[performancePreset];
    return preset?.mode === 'backend';
  }, [performancePreset]);
  
  // Last backend fetch timestamp for each token
  const lastBackendFetchRef = useRef({});
  
  // Multi-view table width customization
  const [multiTableWidth, setMultiTableWidth] = useState(() => {
    try { return parseInt(localStorage.getItem('ticklog_multi_width')) || 0; } catch { return 0; } // 0 = auto
  });
  
  // Compact mode - auto-fit columns to show more symbols on screen
  const [compactMode, setCompactMode] = useState(() => {
    try { return localStorage.getItem('ticklog_compact_mode') === 'true'; } catch { return false; }
  });
  
  // LIGHT MODE - Show only selected columns for maximum performance
  // Allows tracking 20+ tables simultaneously with minimal CPU usage
  // DEFAULT TO FALSE so users have the option to enable it
  const [lightweightMode, setLightweightMode] = useState(() => {
    try { 
      const saved = localStorage.getItem('ticklog_lightweight_mode');
      return saved === null ? false : saved === 'true'; // Default to false if not set
    } catch { return false; }
  });
  
  // Lightweight mode column selection - default to LTQ + Time (user's example)
  const [lightweightCols, setLightweightCols] = useState(() => {
    try { 
      const saved = JSON.parse(localStorage.getItem('ticklog_lightweight_cols'));
      return Array.isArray(saved) && saved.length > 0 ? saved : ['ltq', 'time']; 
    } catch { return ['ltq', 'time']; }
  });

  // PERF MODE V2 toggle - controls deep buffer (200k) used for filters/alerts.
  // Default ON for smoother day-long sessions. Toggle OFF to fall back to old behavior.
  const [perfModeV2, setPerfModeV2] = useState(() => {
    try { return localStorage.getItem('perf_mode_v2') !== 'off'; }
    catch { return true; }
  });
  useEffect(() => {
    try { localStorage.setItem('perf_mode_v2', perfModeV2 ? 'on' : 'off'); } catch {}
  }, [perfModeV2]);
  
  // Save performance settings
  useEffect(() => {
    localStorage.setItem('ticklog_perf_preset', performancePreset);
    localStorage.setItem('ticklog_interval', updateInterval.toString());
    localStorage.setItem('ticklog_max_ticks', maxTicksPerSymbol.toString());
    localStorage.setItem('ticklog_multi_width', multiTableWidth.toString());
    localStorage.setItem('ticklog_compact_mode', compactMode.toString());
    localStorage.setItem('ticklog_lightweight_mode', lightweightMode.toString());
    localStorage.setItem('ticklog_lightweight_cols', JSON.stringify(lightweightCols));
  }, [performancePreset, updateInterval, maxTicksPerSymbol, multiTableWidth, compactMode, lightweightMode, lightweightCols]);
  
  // Apply performance preset
  const applyPreset = (presetKey) => {
    setPerformancePreset(presetKey);
    const preset = PERFORMANCE_PRESETS[presetKey];
    if (preset) {
      setUpdateInterval(preset.interval);
      setMaxTicksPerSymbol(preset.maxTicks);
    }
  };
  
  // Yesterday's max values for bar % reference (Option E - Hybrid)
  // This prevents 100% bars at market start
  const [yesterdayMaxValues, setYesterdayMaxValues] = useState({});
  const yesterdayMaxLoadedRef = useRef(false);
  
  // Fetch yesterday's max values on mount
  useEffect(() => {
    const fetchYesterdayMax = async () => {
      if (yesterdayMaxLoadedRef.current) return;
      
      try {
        const response = await apiClient.get('/daily-max-values');
        if (response.data.success && response.data.max_values) {
          setYesterdayMaxValues(response.data.max_values);
          yesterdayMaxLoadedRef.current = true;
          console.log('[TickHistory] Loaded yesterday max values for', Object.keys(response.data.max_values).length, 'symbols');
        }
      } catch (err) {
        console.warn('[TickHistory] Could not load yesterday max values:', err.message);
      }
    };
    
    fetchYesterdayMax();
  }, []);
  
  // Helper to get yesterday's max for a specific token
  const getYesterdayMax = useCallback((token) => {
    return yesterdayMaxValues[String(token)] || null;
  }, [yesterdayMaxValues]);
  
  // Helper to get today's date string in IST
  const getTodayIST = () => {
    const now = new Date();
    const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
    const istDate = new Date(now.getTime() + istOffset);
    return istDate.toISOString().split('T')[0]; // YYYY-MM-DD
  };
  
  // Tick history - persists until midnight using localStorage
  // Initialize from localStorage if same day, clear if new day
  const [tickHistory, setTickHistory] = useState(() => {
    const now = new Date();
    const dayOfWeek = now.getDay();
    
    // Weekend - don't load any cached data
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      console.log('[TickHistory] Weekend detected - starting with empty tick history');
      return {};
    }
    
    try {
      const savedDate = localStorage.getItem('ticklog_history_date');
      const todayIST = getTodayIST();
      
      // If saved date is today, load the cached history
      if (savedDate === todayIST) {
        const savedHistory = localStorage.getItem('ticklog_history_data');
        if (savedHistory) {
          const parsed = JSON.parse(savedHistory);
          console.log('[TickHistory] Loaded today\'s history from localStorage');
          return parsed;
        }
      } else {
        // Different day - clear old data
        console.log('[TickHistory] New day detected - clearing old history');
        localStorage.removeItem('ticklog_history_data');
        localStorage.removeItem('ticklog_history_date');
      }
    } catch (e) {
      console.error('[TickHistory] Error loading history:', e);
    }
    
    return {};
  });
  
  // Save tick history to localStorage (persists until midnight)
  // Only save last 500 ticks per symbol
  useEffect(() => {
    const saveHistory = () => {
      try {
        const todayIST = getTodayIST();
        const historyToSave = {};
        
        // Keep only last 500 ticks per symbol
        Object.keys(tickHistory).forEach(token => {
          const ticks = tickHistory[token];
          if (Array.isArray(ticks) && ticks.length > 0) {
            // Filter to only market hours ticks and limit to 500
            // Use isTickWithinMarketHours helper to handle null timestamp_ms
            const validTicks = ticks
              .filter(t => isTickWithinMarketHours(t, 'NSE'))
              .slice(0, 500);
            if (validTicks.length > 0) {
              historyToSave[token] = validTicks;
            }
          }
        });
        
        if (Object.keys(historyToSave).length > 0) {
          localStorage.setItem('ticklog_history_data', JSON.stringify(historyToSave));
          localStorage.setItem('ticklog_history_date', todayIST);
        }
      } catch (e) {
        console.error('[TickHistory] Error saving history:', e);
      }
    };
    
    // Debounce save - every 5 seconds
    const timer = setTimeout(saveHistory, 5000);
    return () => clearTimeout(timer);
  }, [tickHistory]);
  
  // Clear history at midnight (IST) - check every minute
  useEffect(() => {
    const checkMidnight = () => {
      const now = new Date();
      const istOffset = 5.5 * 60; // minutes
      const utcMinutes = now.getUTCHours() * 60 + now.getUTCMinutes();
      const istMinutes = (utcMinutes + istOffset) % (24 * 60);
      
      // If it's between 00:00 and 00:01 IST, clear history
      if (istMinutes >= 0 && istMinutes < 1) {
        console.log('[TickHistory] Midnight IST - clearing history');
        localStorage.removeItem('ticklog_history_data');
        localStorage.removeItem('ticklog_history_date');
        setTickHistory({});
      }
    };
    
    const interval = setInterval(checkMidnight, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);
  
  const tickHistoryRef = useRef(tickHistory);
  
  // CRITICAL: Sync tickHistoryRef with backgroundTickHistoryRef on mount
  // This ensures we don't lose ticks when modal reopens
  useEffect(() => {
    if (backgroundTickHistoryRef?.current) {
      const bgHistory = backgroundTickHistoryRef.current;
      if (Object.keys(bgHistory).length > 0) {
        // Merge background history with existing local history
        const mergedHistory = { ...tickHistoryRef.current };
        let hasNewData = false;
        
        for (const [tokenStr, bgTicks] of Object.entries(bgHistory)) {
          if (!bgTicks || bgTicks.length === 0) continue;
          const token = tokenStr;
          
          const existingTicks = mergedHistory[token] || [];
          const existingTickIds = new Set(existingTicks.map(t => getTickUniqueId(t)));
          
          // Add ticks we don't have (filter dummy ticks)
          const newTicks = bgTicks.filter(t => {
            if (!t.timestamp_ms) return false;
            if (!t.bid_price || t.bid_price === 0 || !t.ask_price || t.ask_price === 0) return false;
            return !existingTickIds.has(getTickUniqueId(t));
          });
          
          if (newTicks.length > 0) {
            mergedHistory[token] = [...newTicks, ...existingTicks]
              .sort((a, b) => (b.timestamp_ms || 0) - (a.timestamp_ms || 0));
            hasNewData = true;
          }
        }
        
        if (hasNewData) {
          tickHistoryRef.current = mergedHistory;
          setTickHistory({ ...mergedHistory });
          console.log('[TickHistory] Synced background history on mount');
        }
      }
    }
  }, []); // Run once on mount
  
  // Keep tickHistoryRef in sync with tickHistory state
  useEffect(() => {
    tickHistoryRef.current = tickHistory;
  }, [tickHistory]);
  
  // Transition for smooth updates without flickering
  const [isPending, startTransition] = useTransition();
  
  // Track last update timestamp to prevent too frequent state updates
  const lastStateUpdateRef = useRef(0);
  // MIN_STATE_UPDATE_INTERVAL is now DYNAMIC based on performance preset
  // This allows faster presets (10ms, 50ms) to actually update faster
  
  // Track already alerted ticks to avoid duplicate Telegram alerts
  const alertedTicksRef = useRef(new Set());
  const lastAlertTimeRef = useRef({});
  const ALERT_COOLDOWN_MS = 60000; // 1 minute cooldown per symbol
  
  // Initial loading state - allow data to stabilize before showing updates
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const initialLoadTimeRef = useRef(Date.now());
  const INITIAL_STABILIZATION_MS = 1000; // Reduced to 1 second for faster initial display
  
  // After initial stabilization, set loading to false
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialLoading(false);
      console.log('[TickHistory] Initial stabilization complete, enabling smooth updates');
    }, INITIAL_STABILIZATION_MS);
    return () => clearTimeout(timer);
  }, []);
  
  // Stable tick history for rendering - only update when stabilized
  const stableTickHistory = useMemo(() => {
    // During initial loading, return current ref directly to avoid flicker
    if (isInitialLoading) {
      return tickHistoryRef.current;
    }
    return tickHistory;
  }, [tickHistory, isInitialLoading]);
  
  // SIMPLE BACKEND POLLING - Fetch ALL ticks from backend every 1 second
  // This bypasses all complex frontend sync logic and gets ticks directly from source
  // In INSTANT MODE: Updates UI every poll
  // In BACKEND MODE: Still fetches data but UI updates are throttled separately
  useEffect(() => {
    // Get tokens to poll based on view mode
    const getTokensToPoll = () => {
      if (viewMode === 'single') return selectedToken ? [selectedToken] : [];
      if (viewMode === 'multi') return multiSymbols.filter(t => t);
      if (viewMode === 'grid') return gridSymbols.map(s => s.token).filter(t => t);
      return [];
    };
    
    // Poll backend for ticks - OPTIMIZED: Use batch API and dedupe tokens
    const pollBackendTicks = async () => {
      const tokens = getTokensToPoll();
      if (tokens.length === 0) return;
      
      // OPTIMIZATION: Deduplicate tokens (same symbol in multiple grid cells)
      const uniqueTokens = [...new Set(tokens)];
      
      let hasChanges = false;
      const updatedHistory = { ...tickHistoryRef.current };
      
      try {
        // Use batch endpoint for efficiency - ONE API call for all tokens
        const response = await apiClient.post('/ticks/live/batch', {
          tokens: uniqueTokens,
          limit: 500
        });
        
        const results = response.data.results || {};
        
        for (const token of uniqueTokens) {
          const backendTicks = results[token]?.ticks || [];
          
          if (backendTicks.length > 0) {
            const existingTicks = updatedHistory[token] || [];
            const existingTickIds = new Set(existingTicks.map(t => getTickUniqueId(t)));
            
            // Get ALL new ticks from backend
            const newTicks = backendTicks.filter(t => {
              const tickId = getTickUniqueId(t);
              return !existingTickIds.has(tickId);
            });
            
            if (newTicks.length > 0) {
              // Merge with existing, sort newest first
              updatedHistory[token] = [...newTicks, ...existingTicks]
                .sort((a, b) => (b.timestamp_ms || 0) - (a.timestamp_ms || 0))
                .slice(0, maxTicksPerSymbol);
              hasChanges = true;
            }
          }
        }
      } catch (batchErr) {
        // Fallback to individual calls if batch fails
        console.warn('Batch API failed, falling back to individual calls:', batchErr);
        for (const token of uniqueTokens) {
          try {
            const response = await apiClient.get(`/ticks/live/${token}?limit=500`);
            const backendTicks = response.data.ticks || [];
            
            if (backendTicks.length > 0) {
              const existingTicks = updatedHistory[token] || [];
              const existingTickIds = new Set(existingTicks.map(t => getTickUniqueId(t)));
              
              const newTicks = backendTicks.filter(t => {
                const tickId = getTickUniqueId(t);
                return !existingTickIds.has(tickId);
              });
              
              if (newTicks.length > 0) {
                updatedHistory[token] = [...newTicks, ...existingTicks]
                  .sort((a, b) => (b.timestamp_ms || 0) - (a.timestamp_ms || 0))
                  .slice(0, maxTicksPerSymbol);
                hasChanges = true;
              }
            }
          } catch (err) {
            // If backend poll fails, fall back to backgroundTickHistoryRef
            const bgTicks = backgroundTickHistoryRef?.current?.[token] || [];
            if (bgTicks.length > 0) {
              const existingTicks = updatedHistory[token] || [];
              const existingTickIds = new Set(existingTicks.map(t => getTickUniqueId(t)));
              
              const newTicks = bgTicks.filter(t => !existingTickIds.has(getTickUniqueId(t)));
              if (newTicks.length > 0) {
                updatedHistory[token] = [...newTicks, ...existingTicks]
                  .sort((a, b) => (b.timestamp_ms || 0) - (a.timestamp_ms || 0))
                  .slice(0, maxTicksPerSymbol);
                hasChanges = true;
              }
            }
          }
        }
      }
      
      // Always update the ref with latest data
      if (hasChanges) {
        tickHistoryRef.current = updatedHistory;
        lastStateUpdateRef.current = Date.now();
        
        // In INSTANT MODE: Update UI immediately
        // In BACKEND MODE: Only update ref, UI is updated by throttled interval
        if (!isBackendMode && !isInitialLoading) {
          startTransition(() => {
            setTickHistory({ ...updatedHistory });
          });
        }
      }
    };
    
    // Poll less frequently to reduce CPU load
    // In backend mode: use the configured updateInterval
    // In instant mode: poll every 5 seconds as a fallback (WebSocket handles real-time)
    const pollFrequency = isBackendMode ? Math.max(updateInterval, 5000) : 5000;
    pollBackendTicks();
    const pollInterval = setInterval(pollBackendTicks, pollFrequency);
    
    return () => clearInterval(pollInterval);
  }, [viewMode, selectedToken, multiSymbols, gridSymbols, maxTicksPerSymbol, isInitialLoading, backgroundTickHistoryRef, isBackendMode, updateInterval]);
  
  // Track when tab was last active for fetching missed ticks
  const lastVisibleTimestampRef = useRef(Date.now());
  const [isLoadingMissedTicks, setIsLoadingMissedTicks] = useState(false);
  
  // P0 FIX: Fetch ALL missed ticks from backend when tab becomes visible again
  // Uses the new /ticks/missed endpoint for efficient batch fetching
  // ONLY ACTIVE IN INSTANT MODE - Backend mode fetches at its own interval
  useEffect(() => {
    // Skip if in backend mode - it handles its own data fetching
    if (isBackendMode) return;
    
    const handleVisibilityChange = async () => {
      if (document.visibilityState === 'visible') {
        // Tab became visible - check if we missed any ticks
        const timeSinceLastVisible = Date.now() - lastVisibleTimestampRef.current;
        
        // If tab was hidden for more than 500ms, fetch missed ticks
        if (timeSinceLastVisible > 500) {
          console.log(`[TickHistory] Tab was hidden for ${(timeSinceLastVisible/1000).toFixed(1)}s - fetching ALL missed ticks`);
          setIsLoadingMissedTicks(true);
          
          try {
            // Get all tokens we need to fetch
            const tokensToFetch = viewMode === 'single' 
              ? (selectedToken ? [selectedToken] : activeTokens.slice(0, 5)) 
              : viewMode === 'multi' 
                ? multiSymbols 
                : gridSymbols.map(s => s.token); // Extract tokens from grid objects
            
            if (tokensToFetch.length > 0) {
              // Use the new batch missed ticks API
              const response = await apiClient.post('/ticks/missed', {
                tokens: tokensToFetch,
                since_timestamp_ms: lastVisibleTimestampRef.current,
                limit: 5000
              });
              
              if (response.data.total_missed > 0) {
                let totalMerged = 0;
                
                // Merge ALL missed ticks into local history - WITH market hours filter
                for (const [tokenStr, data] of Object.entries(response.data.results || {})) {
                  const token = parseInt(tokenStr);
                  const missedTicks = data.ticks || [];
                  
                  if (missedTicks.length > 0) {
                    const currentHistory = tickHistoryRef.current[token] || [];
                    // CRITICAL FIX: Use full tick ID for deduplication
                    const existingTickIds = new Set(currentHistory.map(t => getTickUniqueId(t)));
                    
                    // Get instrument for exchange detection
                    const instrument = getInstrumentByToken(instruments, token);
                    const exchange = instrument?.exchange || 'NSE';
                    
                    // Filter by deduplication AND market hours
                    const newTicks = missedTicks.filter(t => {
                      if (existingTickIds.has(getTickUniqueId(t))) return false;
                      const ts = getTickTimestamp(t);
                      if (!ts) return false;
                      return isWithinMarketHours(ts, exchange);
                    });
                    
                    if (newTicks.length > 0) {
                      // PERF V2: keep deeper buffer for filters
                      const cap = isPerfV2Enabled() ? PERF_V2_BUFFER_MAX : maxTicksPerSymbol;
                      tickHistoryRef.current[token] = [...newTicks, ...currentHistory]
                        .sort((a, b) => (b.timestamp_ms || 0) - (a.timestamp_ms || 0))
                        .slice(0, cap);

                      totalMerged += newTicks.length;
                    }
                  }
                }
                
                if (totalMerged > 0) {
                  console.log(`[TickHistory] Recovered ${totalMerged} missed ticks`);
                  // Update state to trigger re-render
                  startTransition(() => {
                    setTickHistory({ ...tickHistoryRef.current });
                  });
                }
              }
            }
          } catch (err) {
            console.error('[TickHistory] Error fetching missed ticks:', err);
            // Fallback: try individual token fetch
            try {
              const tokensToFetch = selectedToken ? [selectedToken] : [];
              for (const token of tokensToFetch) {
                const response = await apiClient.get(`/history/ticks/${token}?limit=500&latest_first=true`);
                const backendTicks = response.data.ticks || [];
                if (backendTicks.length > 0) {
                  const currentHistory = tickHistoryRef.current[token] || [];
                  // CRITICAL FIX: Use full tick ID for deduplication
                  const existingTickIds = new Set(currentHistory.map(t => getTickUniqueId(t)));
                  
                  // Get instrument for exchange detection
                  const instrument = getInstrumentByToken(instruments, token);
                  const exchange = instrument?.exchange || 'NSE';
                  
                  // Filter by deduplication AND market hours
                  const newTicks = backendTicks.filter(t => {
                    if (existingTickIds.has(getTickUniqueId(t))) return false;
                    const ts = getTickTimestamp(t);
                    if (!ts) return false;
                    return isWithinMarketHours(ts, exchange);
                  });
                  
                  if (newTicks.length > 0) {
                    // PERF V2: keep deeper buffer for filters
                    const cap = isPerfV2Enabled() ? PERF_V2_BUFFER_MAX : maxTicksPerSymbol;
                    tickHistoryRef.current[token] = [...newTicks, ...currentHistory]
                      .sort((a, b) => (b.timestamp_ms || 0) - (a.timestamp_ms || 0))
                      .slice(0, cap);
                  }
                }
              }
              startTransition(() => {
                setTickHistory({ ...tickHistoryRef.current });
              });
            } catch (fallbackErr) {
              console.error('[TickHistory] Fallback fetch also failed:', fallbackErr);
            }
          } finally {
            setIsLoadingMissedTicks(false);
          }
        }
        
        lastVisibleTimestampRef.current = Date.now();
      } else {
        // Tab becoming hidden - record precise timestamp
        lastVisibleTimestampRef.current = Date.now();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [selectedToken, viewMode, multiSymbols, gridSymbols, maxTicksPerSymbol, activeTokens, isBackendMode]);
  
  // Save state
  const [lastSaveIndex, setLastSaveIndex] = useState({});
  const [saving, setSaving] = useState(false);
  const [lastSaveTime, setLastSaveTime] = useState(null);
  
  // Auto-select first token for Single view
  useEffect(() => {
    if (!selectedToken && activeTokens.length > 0) {
      setSelectedToken(activeTokens[0]);
    }
  }, [activeTokens, selectedToken]);
  
  // NO auto-populate for Multi/Grid - user manually adds/removes symbols
  // This ensures deleted symbols stay deleted
  
  // SIMPLE & RELIABLE tick updates - avoids over-optimization that breaks functionality
  // Uses requestAnimationFrame for smooth updates without blocking UI
  const rafIdRef = useRef(null);
  const lastFetchTimestampRef = useRef({}); // Track last fetched timestamp per token for slow intervals
  
  // BACKEND MODE: Instead of fetching old data from MongoDB, 
  // just throttle the UI updates while still receiving live WebSocket data
  // This keeps data current but reduces CPU by updating UI less often
  const fetchBackendTicks = useCallback(async () => {
    // In backend mode, we DON'T fetch from database
    // We just trigger a UI update with the current memory data
    // WebSocket is still feeding tickHistoryRef.current in the background
    console.log('[Backend Mode] Throttled UI update triggered');
    
    // Force UI update with current memory data
    const currentData = { ...tickHistoryRef.current };
    if (Object.keys(currentData).length > 0) {
      console.log(`[Backend Mode] Updating UI with ${Object.keys(currentData).length} symbols from memory`);
      setTickHistory(currentData);
    }
  }, []);
  
  // BACKEND MODE useEffect - just throttles UI updates, WebSocket still receives data
  useEffect(() => {
    if (!isBackendMode) return;
    
    console.log(`[TickHistory] ========== BACKEND MODE ACTIVE ==========`);
    console.log(`[TickHistory] UI will update every ${updateInterval}ms (${updateInterval/1000}s)`);
    console.log(`[TickHistory] WebSocket still receives live ticks in background`);
    
    // Initial UI update
    fetchBackendTicks();
    
    // Set up interval for throttled UI updates
    const backendInterval = setInterval(() => {
      console.log(`[Backend Mode] Interval triggered - updating UI...`);
      fetchBackendTicks();
    }, updateInterval);
    
    return () => {
      console.log('[Backend Mode] Cleaning up interval');
      clearInterval(backendInterval);
    };
  }, [isBackendMode, updateInterval, fetchBackendTicks]);
  
  // INSTANT MODE useEffect - only runs when NOT in backend mode
  useEffect(() => {
    // Skip if in backend mode - backend useEffect handles everything
    if (isBackendMode) {
      console.log('[TickHistory] Backend mode is active');
      return;
    }
    
    console.log(`[TickHistory] ========== INSTANT MODE ACTIVE ==========`);
    console.log(`[TickHistory] Processing WebSocket ticks every ${updateInterval}ms`);
    
    // Process incoming tick data - NO market hours filter
    // Ticks are already filtered at WebSocket level in App.js
    const processTicks = () => {
      let hasNewData = false;
      
      // In Light Mode with eco preset, only process visible tokens
      const tokensToProcess = lightweightMode ? 
        (viewMode === 'single' ? (selectedToken ? [selectedToken] : []) :
         viewMode === 'multi' ? multiSymbols : gridSymbols.map(s => s.token)) :
        Object.keys(tickData).map(t => parseInt(t));
      
      tokensToProcess.forEach(token => {
        const instrument = getInstrumentByToken(instruments, token);
        
        // Build a Set of existing tick unique IDs for O(1) duplicate checking
        const localHistory = tickHistoryRef.current[token] || [];
        const existingTickIds = new Set(localHistory.map(t => getTickUniqueId(t)));
        
        // Get exchange for market hours filtering
        const exchange = instrument?.exchange || 'NSE';
        
        let ticksToAdd = [];
        
        // Process ticks from background history - filter by market hours
        if (backgroundTickHistoryRef?.current?.[token]) {
          const appHistory = backgroundTickHistoryRef.current[token];
          
          // Filter duplicates AND market hours
          const newValidTicks = appHistory.filter(t => {
            const ts = getTickTimestamp(t);
            if (!ts) return false;
            const tickId = getTickUniqueId(t);
            if (existingTickIds.has(tickId)) return false;
            // Filter by market hours
            return isWithinMarketHours(ts, exchange);
          });
          
          ticksToAdd.push(...newValidTicks);
        }
        
        // Also process current tick from tickData - with market hours filter
        const tick = tickData[token];
        if (tick) {
          const ts = getTickTimestamp(tick);
          if (ts) {
            const tickId = getTickUniqueId(tick);
            // Check market hours before adding
            if (!existingTickIds.has(tickId) && 
                !ticksToAdd.some(t => getTickUniqueId(t) === tickId) &&
                isWithinMarketHours(ts, exchange)) {
              ticksToAdd.push(tick);
            }
          }
        }
        
        // If we have new ticks, merge them with existing history
        if (ticksToAdd.length > 0) {
          const combined = [...ticksToAdd, ...localHistory];
          const deduped = deduplicateAndSortTicks(combined);
          // PERF V2: keep larger buffer for filters/alerts; render slices down separately
          const cap = isPerfV2Enabled() && !lightweightMode ? PERF_V2_BUFFER_MAX : (lightweightMode ? Math.min(maxTicksPerSymbol, 1000) : maxTicksPerSymbol);
          const limited = deduped.slice(0, cap);
          
          tickHistoryRef.current[token] = limited;
          hasNewData = true;
          
          // Check for Smart Filter matches and send Telegram alerts
          if (smartFilters.enabled && smartFilters.telegramAlerts && ticksToAdd.length > 0) {
            const symbol = instrument?.tradingsymbol || `Token_${token}`;
            const now = Date.now();
            const lastAlertTime = lastAlertTimeRef.current[symbol] || 0;
            
            // Only check for alerts if not on cooldown for this symbol
            if (now - lastAlertTime >= ALERT_COOLDOWN_MS) {
              // Calculate max values from existing history
              const smartMaxValues = calculateMaxValues(limited.slice(0, 500));
              
              // Check each new tick for filter matches
              for (const newTick of ticksToAdd) {
                const tickId = getTickUniqueId(newTick);
                
                // Skip if already alerted
                if (alertedTicksRef.current.has(tickId)) continue;
                
                // Apply smart filters to check for match
                const [processedTick] = applySmartFilters([newTick], smartFilters, smartMaxValues);
                
                if (processedTick._smartFilterMatch && processedTick._matchedFilters?.length > 0) {
                  // Found a matching tick - send alert
                  alertedTicksRef.current.add(tickId);
                  lastAlertTimeRef.current[symbol] = now;
                  
                  // Send telegram alert asynchronously (don't await to avoid blocking)
                  sendSmartFilterTelegramAlert(
                    apiClient,
                    symbol,
                    token,
                    processedTick._matchedFilters,
                    newTick
                  ).then(result => {
                    if (result.status === 'sent') {
                      console.log(`[SmartFilter] Telegram alert sent for ${symbol}:`, processedTick._matchedFilters);
                    }
                  }).catch(err => {
                    console.error('[SmartFilter] Alert error:', err);
                  });
                  
                  // Only one alert per symbol per cooldown period
                  break;
                }
              }
            }
          }
        }
      });
      
      return hasNewData;
    };
    
    // For slow intervals (>= 500ms), fetch from backend buffer
    // NO market hours check - backend will provide valid ticks
    const fetchMissedTicksForSlowInterval = async () => {
      if (lightweightMode || updateInterval < 500) return false;
      
      const tokensToFetch = viewMode === 'single' ? (selectedToken ? [selectedToken] : []) : 
                           viewMode === 'multi' ? multiSymbols : gridSymbols.map(s => s.token);
      
      let hasNewData = false;
      
      for (const token of tokensToFetch) {
        if (!token) continue;
        
        try {
          const response = await apiClient.get(`/ticks/live/${token}`);
          const liveTicks = response.data.ticks || [];
          
          if (liveTicks.length > 0) {
            const currentHistory = tickHistoryRef.current[token] || [];
            const existingTickIds = new Set(currentHistory.map(t => getTickUniqueId(t)));
            
            // Get instrument for exchange detection
            const instrument = getInstrumentByToken(instruments, token);
            const exchange = instrument?.exchange || 'NSE';
            
            // Get ticks we don't have - WITH market hours filter
            const newTicks = liveTicks.filter(t => {
              const ts = getTickTimestamp(t);
              if (!ts) return false;
              const tickId = getTickUniqueId(t);
              if (existingTickIds.has(tickId)) return false;
              // Filter by market hours
              return isWithinMarketHours(ts, exchange);
            });
            
            if (newTicks.length > 0) {
              const combined = [...newTicks, ...currentHistory];
              const deduped = deduplicateAndSortTicks(combined);
              // PERF V2: deep filter/alert buffer (200k), render uses maxTicksPerSymbol separately
              const cap = isPerfV2Enabled() ? PERF_V2_BUFFER_MAX : maxTicksPerSymbol;
              tickHistoryRef.current[token] = deduped.slice(0, cap);
              hasNewData = true;
            }
          }
        } catch (err) {
          // Silently ignore
        }
      }
      
      return hasNewData;
    };
    
    // Update at user-configured interval
    const scheduleUpdate = async () => {
      // Skip updates during initial stabilization period
      if (isInitialLoading) return;
      
      // Skip processing when ALL markets are closed to save CPU
      const now = new Date();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const day = now.getDay();
      const timeInMinutes = hours * 60 + minutes;
      
      // Market hours check - process if ANY market is open:
      // NSE/NFO/BSE/BFO: 9:15 AM (555 min) to 3:30 PM (930 min)
      // MCX: 9:00 AM (540 min) to 11:54 PM (1434 min)
      const isWeekend = day === 0 || day === 6;
      const isNseOpen = timeInMinutes >= 555 && timeInMinutes <= 930;
      const isMcxOpen = timeInMinutes >= 540 && timeInMinutes <= 1434;
      
      if (isWeekend || (!isNseOpen && !isMcxOpen)) {
        // All markets closed - skip processing to save CPU
        return;
      }
      
      rafIdRef.current = requestAnimationFrame(async () => {
        // Process new ticks from tickData and backgroundTickHistoryRef
        let hasNewData = processTicks();
        
        // For slow intervals, also fetch from backend
        if (updateInterval >= 500) {
          const backendHasNew = await fetchMissedTicksForSlowInterval();
          hasNewData = hasNewData || backendHasNew;
        }
        
        // For fast presets, update display immediately
        if (updateInterval <= 50 && hasNewData) {
          const nowMs = Date.now();
          if ((nowMs - lastStateUpdateRef.current) >= 50) {
            lastStateUpdateRef.current = nowMs;
            startTransition(() => {
              setTickHistory({ ...tickHistoryRef.current });
            });
          }
        }
      });
    };
    
    // Use the interval from performance preset
    // INSTANT mode: 20ms for true real-time updates
    // BATCH mode: 200ms for low CPU usage
    // Light Mode: minimum 100ms regardless of preset
    const preset = PERFORMANCE_PRESETS[performancePreset];
    const presetMinInterval = preset?.minInterval || 100;
    
    // In light mode, always use at least 100ms; otherwise use the preset's minInterval
    const effectiveInterval = lightweightMode 
      ? Math.max(updateInterval, 100) 
      : Math.max(updateInterval, presetMinInterval);
    
    console.log(`[TickHistory] Performance: preset=${performancePreset}, requested=${updateInterval}ms, effective=${effectiveInterval}ms`);
    const interval = setInterval(scheduleUpdate, effectiveInterval);
    
    // Save to sessionStorage on unmount (skip in Light Mode to reduce overhead)
    return () => {
      clearInterval(interval);
      if (rafIdRef.current) {
        cancelAnimationFrame(rafIdRef.current);
      }
      // Only save if not in Light Mode (reduces memory usage)
      if (!lightweightMode) {
        const historyToSave = tickHistoryRef.current;
        try {
          sessionStorage.setItem('tickHistory', JSON.stringify(historyToSave));
        } catch (e) {
          console.warn('Could not save tick history to sessionStorage', e);
        }
      }
    };
  }, [tickData, instruments, updateInterval, maxTicksPerSymbol, isInitialLoading, lightweightMode, viewMode, selectedToken, multiSymbols, gridSymbols, smartFilters, isBackendMode, fetchBackendTicks, performancePreset]);
  
  // Auto-save check (3:30 PM and 11:55 PM IST)
  useEffect(() => {
    const checkAutoSave = () => {
      const now = new Date();
      const istHour = (now.getUTCHours() + 5.5) % 24;
      const istDecimal = istHour + (now.getUTCMinutes() / 60);
      
      // Check if within 1 minute of auto-save times
      const shouldSave = (
        (Math.abs(istDecimal - AUTO_SAVE_TIMES.afternoon) < 0.017) || // 3:30 PM
        (Math.abs(istDecimal - AUTO_SAVE_TIMES.night) < 0.017) // 11:55 PM
      );
      
      if (shouldSave && !saving) {
        console.log("[AutoSave] Triggering auto-save at", now.toISOString());
        handleManualSave();
      }
    };
    
    const interval = setInterval(checkAutoSave, 60000); // Check every minute
    return () => clearInterval(interval);
  }, [saving]);
  
  // ==================== PERSISTENT TICK STORAGE ====================
  // Track last stored index for each token to avoid duplicate storage
  const [lastStoredIndex, setLastStoredIndex] = useState({});
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [historicalTicksLoaded, setHistoricalTicksLoaded] = useState(new Set());
  
  // Track load more state per token
  const [loadMoreState, setLoadMoreState] = useState({}); // { token: { loading: false, hasMore: true, skip: 0 } }
  
  // Auto-load historical data when selecting a token in Single view
  // OR when symbols are added to Multi/Grid views
  useEffect(() => {
    const loadHistoryForToken = async (token, forceReload = false) => {
      if (!token) return;
      if (!forceReload && historicalTicksLoaded.has(token)) return; // Already loaded from DB
      
      console.log(`[TickHistory] Loading historical ticks for token ${token}...`);
      
      // Get exchange for market hours filtering
      const instrument = getInstrumentByToken(instruments, token);
      const exchange = getExchangeFromInstrument(instrument);
      
      // Check market status - if closed, load last 500 ticks without market hours filter
      const marketStatus = getCurrentMarketStatus();
      
      setIsLoadingHistory(true);
      try {
        const result = await loadHistoricalTicks(token, null, 500, 0, exchange);
        console.log(`[TickHistory] Received ${result.ticks?.length || 0} ticks for token ${token}, last_trading_day=${result.last_trading_day}`);
        
        if (result.ticks && result.ticks.length > 0) {
          // SAFETY: Filter ticks by market hours on frontend too
          // Backend should already filter, but double-check for safety
          const validHistoricalTicks = result.ticks.filter(t => {
            const ts = getTickTimestamp(t);
            if (!ts) return false;
            return isWithinMarketHours(ts, exchange);
          });
          
          console.log(`[TickHistory] Valid ticks after market hours filter: ${validHistoricalTicks.length}`);
          
          // Merge with existing ticks using deduplication
          setTickHistory(prev => {
            const existingTicks = forceReload ? [] : (prev[token] || []);
            const combined = [...existingTicks, ...validHistoricalTicks];
            // Deduplicate and sort (newest first)
            const deduped = deduplicateAndSortTicks(combined);
            // Limit to 5000 when market closed, otherwise use maxTicksPerSymbol
            const limited = marketStatus.isOpen ? deduped.slice(0, maxTicksPerSymbol) : deduped.slice(0, 5000);
            
            // Update ref immediately for consistent state
            tickHistoryRef.current[token] = limited;
            
            return { ...prev, [token]: limited };
          });
          
          setHistoricalTicksLoaded(prev => new Set([...prev, token]));
          setLoadMoreState(prev => ({
            ...prev,
            [token]: { loading: false, hasMore: result.has_more && marketStatus.isOpen, skip: result.ticks.length }
          }));
        }
      } catch (err) {
        console.error('Error auto-loading history:', err);
      }
      setIsLoadingHistory(false);
    };
    
    // Single view - load for selected token
    if (selectedToken && viewMode === 'single') {
      loadHistoryForToken(selectedToken);
    }
    
    // Multi view - load for ALL multi symbols
    if (viewMode === 'multi' && multiSymbols.length > 0) {
      // Load historical data for each symbol that hasn't been loaded yet
      // Use a small delay between requests to avoid overwhelming the server
      const loadAllMultiSymbols = async () => {
        for (const token of multiSymbols) {
          if (!historicalTicksLoaded.has(token)) {
            await loadHistoryForToken(token);
            // Small delay between requests
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      };
      loadAllMultiSymbols();
    }
    
    // Grid view - load for ALL grid symbols
    if (viewMode === 'grid' && gridSymbols.length > 0) {
      // Load historical data for each symbol that hasn't been loaded yet
      const loadAllGridSymbols = async () => {
        // Extract unique tokens from grid symbols (avoid duplicate fetches)
        const uniqueTokens = [...new Set(gridSymbols.map(s => s.token))];
        for (const token of uniqueTokens) {
          if (!historicalTicksLoaded.has(token)) {
            await loadHistoryForToken(token);
            // Small delay between requests
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        }
      };
      loadAllGridSymbols();
    }
  }, [selectedToken, viewMode, historicalTicksLoaded, multiSymbols, gridSymbols, instruments]);
  
  // Pre-market refresh: Clear historical data 5 minutes before market opens
  // This ensures fresh data when market starts
  const preMarketRefreshDoneRef = useRef(false);
  useEffect(() => {
    const checkPreMarketRefresh = () => {
      const marketStatus = getCurrentMarketStatus();
      
      // If we're in the pre-market refresh zone (5 min before open) and haven't refreshed yet
      if (marketStatus.isPreMarketRefreshZone && !preMarketRefreshDoneRef.current) {
        console.log('[TickHistory] Pre-market refresh triggered - clearing historical data');
        
        // Clear all tick history to start fresh
        tickHistoryRef.current = {};
        setTickHistory({});
        setHistoricalTicksLoaded(new Set());
        setLoadMoreState({});
        
        // Mark that we've done the pre-market refresh
        preMarketRefreshDoneRef.current = true;
      }
      
      // Reset the flag when market is open (so it can trigger again next day)
      if (marketStatus.isOpen) {
        preMarketRefreshDoneRef.current = false;
      }
    };
    
    // Check every minute
    const interval = setInterval(checkPreMarketRefresh, 60000);
    
    // Also check immediately on mount
    checkPreMarketRefresh();
    
    return () => clearInterval(interval);
  }, []);
  
  // NOTE: Auto-save to backend is now handled by App.js (background tick collection)
  // This component only handles local UI state for the Tick Log modal
  
  // Broadcast tick updates to pop-out windows
  useEffect(() => {
    const tokens = Object.keys(tickHistory).map(Number);
    tokens.forEach(token => {
      if (tickHistory[token]?.length > 0) {
        broadcastTickData(token, tickHistory[token]);
      }
    });
  }, [tickHistory]);
  
  // Load more historical ticks for a specific token
  const handleLoadMore = useCallback(async (token) => {
    const state = loadMoreState[token] || { loading: false, hasMore: true, skip: tickHistory[token]?.length || 0 };
    if (state.loading || !state.hasMore) return;
    
    // Get exchange for market hours filtering
    const instrument = getInstrumentByToken(instruments, token);
    const exchange = getExchangeFromInstrument(instrument);
    
    setLoadMoreState(prev => ({ ...prev, [token]: { ...state, loading: true } }));
    
    try {
      const result = await loadHistoricalTicks(token, null, 5000, state.skip);
      if (result.ticks && result.ticks.length > 0) {
        // Filter loaded ticks by market hours using helper function
        // This properly handles ticks with null timestamp_ms by using timestamp string fallback
        const validTicks = result.ticks.filter(t => {
          const ts = getTickTimestamp(t);
          if (!ts) return false;
          return isWithinMarketHours(ts, exchange);
        });
        
        // Even if validTicks is empty, we still loaded ticks - update skip
        // This ensures +5K button progresses through data
        const newSkip = state.skip + result.ticks.length;
        
        if (validTicks.length > 0) {
          // Combine with existing, deduplicate, and sort
          setTickHistory(prev => {
            const existing = prev[token] || [];
            const combined = [...existing, ...validTicks];
            return { ...prev, [token]: deduplicateAndSortTicks(combined) };
          });
          tickHistoryRef.current[token] = deduplicateAndSortTicks([
            ...(tickHistoryRef.current[token] || []),
            ...validTicks
          ]);
        }
        
        setLoadMoreState(prev => ({
          ...prev,
          [token]: {
            loading: false,
            hasMore: result.has_more,
            skip: newSkip
          }
        }));
      } else {
        setLoadMoreState(prev => ({
          ...prev,
          [token]: { loading: false, hasMore: false, skip: state.skip }
        }));
      }
    } catch (err) {
      console.error('Error loading more ticks:', err);
      setLoadMoreState(prev => ({
        ...prev,
        [token]: { ...state, loading: false }
      }));
    }
  }, [loadMoreState, tickHistory, instruments]);
  
  // Manual Save function - saves from last save point
  const handleManualSave = useCallback(async () => {
    if (saving) return;
    setSaving(true);
    
    try {
      const tokensToSave = viewMode === 'single' && selectedToken ? [selectedToken] : 
                          viewMode === 'multi' ? multiSymbols :
                          activeTokens.slice(0, MAX_SYMBOLS);
      
      const saveData = {};
      tokensToSave.forEach(token => {
        const history = tickHistoryRef.current[token] || [];
        const lastIdx = lastSaveIndex[token] || 0;
        const newTicks = history.slice(0, history.length - lastIdx); // Get new ticks since last save
        if (newTicks.length > 0) {
          saveData[token] = {
            symbol: getSymbolName(instruments, token),
            ticks: newTicks,
            count: newTicks.length
          };
        }
      });
      
      if (Object.keys(saveData).length > 0) {
        await apiClient.post('/ticks/save-history', { data: saveData });
        
        // Update last save index
        const newLastSaveIndex = { ...lastSaveIndex };
        Object.keys(saveData).forEach(token => {
          newLastSaveIndex[token] = (tickHistoryRef.current[token] || []).length;
        });
        setLastSaveIndex(newLastSaveIndex);
        setLastSaveTime(new Date());
        console.log(`[Save] Saved ${Object.keys(saveData).length} symbols`);
      }
    } catch (err) {
      console.error("[Save] Failed:", err);
    } finally {
      setSaving(false);
    }
  }, [viewMode, selectedToken, multiSymbols, subscribedTokens, lastSaveIndex, instruments, saving]);
  
  // Remove symbol from multi view - marks as user modified
  const removeMultiSymbol = useCallback((token) => {
    setMultiSymbols(prev => prev.filter(t => t !== token));
    setMultiUserModified(true); // User has manually modified, never auto-populate again
  }, []);
  
  // Add symbol to multi view - marks as user modified
  const addMultiSymbol = useCallback((token) => {
    if (multiSymbols.length >= MAX_SYMBOLS) return;
    if (!multiSymbols.includes(token)) {
      setMultiSymbols(prev => [...prev, token]);
      setMultiUserModified(true); // User has manually modified
    }
  }, [multiSymbols]);
  
  // Grid symbols management - now supports multi-instance (same symbol up to 5 times)
  const addGridSymbol = useCallback((token) => {
    if (gridSymbols.length >= MAX_SYMBOLS) return;
    
    // Count how many instances of this token already exist
    const existingInstances = gridSymbols.filter(s => s.token === token);
    
    if (existingInstances.length >= MAX_INSTANCES_PER_SYMBOL) {
      // Already have max instances of this symbol
      return;
    }
    
    // Find next available instance number
    const usedInstances = existingInstances.map(s => s.instance);
    let nextInstance = 1;
    while (usedInstances.includes(nextInstance)) {
      nextInstance++;
    }
    
    // Add new grid item with unique ID
    const newGridItem = {
      id: `${token}_${nextInstance}`,
      token: token,
      instance: nextInstance
    };
    
    setGridSymbols(prev => [...prev, newGridItem]);
    setGridUserModified(true);
  }, [gridSymbols]);
  
  const removeGridSymbol = useCallback((gridId) => {
    setGridSymbols(prev => prev.filter(s => s.id !== gridId));
    setGridUserModified(true);
  }, []);
  
  // Save gridSymbols to database when they change
  useEffect(() => {
    if (gridSettingsLoaded && gridSymbols.length >= 0) {
      debouncedSaveGridSettings({
        gridCellPositions,
        gridVisibleCols,
        gridColumnOrder,
        freePositionMode,
        gridSymbols
      });
    }
  }, [gridSymbols, gridSettingsLoaded, debouncedSaveGridSettings, gridCellPositions, gridVisibleCols, gridColumnOrder, freePositionMode]);
  
  // Initialize Multi/Grid with subscribed tokens ONLY on first open
  // Once user interacts (adds/removes), never auto-populate again
  // Use localStorage to persist this state across sessions
  const [multiUserModified, setMultiUserModified] = useState(() => {
    try { return localStorage.getItem('ticklog_multi_modified') === 'true'; } catch { return false; }
  });
  const [gridUserModified, setGridUserModified] = useState(() => {
    try { return localStorage.getItem('ticklog_grid_modified') === 'true'; } catch { return false; }
  });
  
  
  // Save grid gap to localStorage
  useEffect(() => {
    localStorage.setItem('ticklog_grid_gap', gridGap.toString());
  }, [gridGap]);
  
  // Save cell positions to localStorage AND database
  useEffect(() => {
    if (Object.keys(gridCellPositions).length > 0) {
      localStorage.setItem('ticklog_grid_cell_positions', JSON.stringify(gridCellPositions));
      // Also save to database (debounced)
      if (gridSettingsLoaded) {
        debouncedSaveGridSettings({
          gridCellPositions,
          gridVisibleCols,
          gridColumnOrder,
          freePositionMode,
          gridSymbols
        });
      }
    }
  }, [gridCellPositions, gridSettingsLoaded, debouncedSaveGridSettings, gridVisibleCols, gridColumnOrder, freePositionMode, gridSymbols]);
  
  // Save free position mode to localStorage AND database
  useEffect(() => {
    localStorage.setItem('ticklog_free_position_mode', freePositionMode.toString());
    // Also save to database (debounced)
    if (gridSettingsLoaded) {
      debouncedSaveGridSettings({
        gridCellPositions,
        gridVisibleCols,
        gridColumnOrder,
        freePositionMode,
        gridSymbols
      });
    }
  }, [freePositionMode, gridSettingsLoaded, debouncedSaveGridSettings, gridCellPositions, gridVisibleCols, gridColumnOrder, gridSymbols]);
  
  // Save cell sizes to localStorage
  useEffect(() => {
    if (Object.keys(gridCellSizes).length > 0) {
      localStorage.setItem('ticklog_grid_cell_sizes', JSON.stringify(gridCellSizes));
    }
  }, [gridCellSizes]);
  
  // Handle cell size change
  const handleCellSizeChange = useCallback((gridId, newSize) => {
    setGridCellSizes(prev => ({
      ...prev,
      [gridId]: newSize
    }));
  }, []);
  
  // Handle drag start
  const handleDragStart = useCallback((e, gridItem) => {
    if (!customLayoutMode) return;
    setDraggedItem(gridItem);
    e.dataTransfer.effectAllowed = 'move';
  }, [customLayoutMode]);
  
  // Handle drag over
  const handleDragOver = useCallback((e, gridItem) => {
    if (!customLayoutMode || !draggedItem) return;
    e.preventDefault();
    if (gridItem.id !== draggedItem.id) {
      setDragOverItem(gridItem);
    }
  }, [customLayoutMode, draggedItem]);
  
  // Handle drop - reorder grid symbols
  const handleDrop = useCallback((e, targetItem) => {
    if (!customLayoutMode || !draggedItem || draggedItem.id === targetItem.id) return;
    e.preventDefault();
    
    setGridSymbols(prev => {
      const newSymbols = [...prev];
      const draggedIndex = newSymbols.findIndex(s => s.id === draggedItem.id);
      const targetIndex = newSymbols.findIndex(s => s.id === targetItem.id);
      
      // Remove dragged item and insert at target position
      const [removed] = newSymbols.splice(draggedIndex, 1);
      newSymbols.splice(targetIndex, 0, removed);
      
      return newSymbols;
    });
    
    setDraggedItem(null);
    setDragOverItem(null);
  }, [customLayoutMode, draggedItem]);
  
  // Handle drag end
  const handleDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDragOverItem(null);
  }, []);
  
  // Saved Grid Configurations - store multiple named configs
  const [savedGridConfigs, setSavedGridConfigs] = useState({});
  const [adminGridLayouts, setAdminGridLayouts] = useState([]);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const [saveConfigName, setSaveConfigName] = useState('');
  const [showLoadMenu, setShowLoadMenu] = useState(false);
  const [gridRefreshKey, setGridRefreshKey] = useState(0); // Key to force re-render GridCells when loading config
  const [layoutsLoading, setLayoutsLoading] = useState(true);
  const [saveAsAdminLayout, setSaveAsAdminLayout] = useState(false);
  
  // Check if current user is admin
  const isAdmin = appContext?.currentUser?.is_admin || false;
  
  // Load saved grid configurations from DATABASE on mount
  useEffect(() => {
    const loadSavedLayouts = async () => {
      try {
        const token = localStorage.getItem('auth_token');
        
        // Always load admin layouts (available to all users)
        try {
          const adminResponse = await apiClient.get('/admin/grid-layouts');
          if (adminResponse.data.success && adminResponse.data.layouts) {
            setAdminGridLayouts(adminResponse.data.layouts);
            console.log('[GridLayouts] Loaded admin layouts:', adminResponse.data.layouts.length);
          }
        } catch (adminErr) {
          console.warn('[GridLayouts] Failed to load admin layouts:', adminErr.message);
        }
        
        if (!token) {
          // Fall back to localStorage if not logged in
          try {
            const saved = localStorage.getItem('ticklog_saved_grid_configs');
            setSavedGridConfigs(saved ? JSON.parse(saved) : {});
          } catch { setSavedGridConfigs({}); }
          setLayoutsLoading(false);
          return;
        }
        
        const response = await apiClient.get('/user/grid-layouts');
        if (response.data.success && response.data.layouts) {
          // Convert array to object keyed by name
          const layoutsObj = {};
          response.data.layouts.forEach(layout => {
            layoutsObj[layout.name] = layout.layout;
          });
          setSavedGridConfigs(layoutsObj);
          console.log('[GridLayouts] Loaded user layouts:', Object.keys(layoutsObj).length);
        }
      } catch (err) {
        console.warn('[GridLayouts] Failed to load from database, using localStorage:', err.message);
        try {
          const saved = localStorage.getItem('ticklog_saved_grid_configs');
          setSavedGridConfigs(saved ? JSON.parse(saved) : {});
        } catch { setSavedGridConfigs({}); }
      } finally {
        setLayoutsLoading(false);
      }
    };
    
    loadSavedLayouts();
  }, []);
  
  // Also save to localStorage as backup
  useEffect(() => {
    if (!layoutsLoading) {
      localStorage.setItem('ticklog_saved_grid_configs', JSON.stringify(savedGridConfigs));
    }
  }, [savedGridConfigs, layoutsLoading]);
  
  // Save current grid configuration with custom name - saves to DATABASE
  const saveGridConfig = useCallback(async (name, asAdminLayout = false) => {
    if (!name.trim()) return;
    
    // Collect all cell settings from localStorage
    const cellSettings = {};
    gridSymbols.forEach(gridItem => {
      const storageKey = gridItem.id;
      const cols = localStorage.getItem(`gridcell_cols_${storageKey}`);
      const colOrder = localStorage.getItem(`gridcell_order_${storageKey}`);
      const filters = localStorage.getItem(`smartfilters_${storageKey}`);
      const widths = localStorage.getItem(`ticklog_colwidths_${storageKey}`);
      const basicFilters = localStorage.getItem(`gridcell_filters_${storageKey}`);
      
      cellSettings[storageKey] = {
        columns: cols ? JSON.parse(cols) : [],
        columnOrder: colOrder ? JSON.parse(colOrder) : [],
        smartFilters: filters ? JSON.parse(filters) : {},
        colWidths: widths ? JSON.parse(widths) : {},
        basicFilters: basicFilters ? JSON.parse(basicFilters) : {},
        cellSize: gridCellSizes[storageKey] || null
      };
      
      console.log('[GridConfig] Saving cell settings for:', storageKey, cellSettings[storageKey]);
    });
    
    const config = {
      name: name.trim(),
      savedAt: new Date().toISOString(),
      gridSymbols: gridSymbols,
      gridCellPositions: gridCellPositions,
      freePositionMode: freePositionMode,
      gridVisibleCols: gridVisibleCols,
      gridColumnOrder: gridColumnOrder,
      cellSettings: cellSettings,
      cellSizes: gridCellSizes
    };
    
    console.log('[GridConfig] Saving full config:', config, asAdminLayout ? '(as Admin)' : '(as User)');
    
    // Save to database
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        if (asAdminLayout) {
          // Save as admin layout (global, visible to all users)
          await apiClient.post('/admin/grid-layouts/save-current', {
            name: name.trim(),
            description: '',
            layout: config
          });
          console.log('[GridConfig] Saved as ADMIN layout');
          
          // Refresh admin layouts
          const adminResponse = await apiClient.get('/admin/grid-layouts');
          if (adminResponse.data.success) {
            setAdminGridLayouts(adminResponse.data.layouts);
          }
        } else {
          // Save as user layout (private)
          await apiClient.post('/user/grid-layouts', {
            name: name.trim(),
            layout: config
          });
          console.log('[GridConfig] Saved as user layout');
          
          // Also update local state
          setSavedGridConfigs(prev => ({
            ...prev,
            [name.trim()]: config
          }));
        }
      }
    } catch (err) {
      console.warn('[GridConfig] Failed to save to database:', err.message);
      // Still update local state for user layouts
      if (!asAdminLayout) {
        setSavedGridConfigs(prev => ({
          ...prev,
          [name.trim()]: config
        }));
      }
    }
    
    setShowSaveDialog(false);
    setSaveConfigName('');
    setSaveAsAdminLayout(false);
  }, [gridSymbols, gridCellSizes, gridCellPositions, freePositionMode, gridVisibleCols, gridColumnOrder]);
  
  // Load a saved grid configuration (works for both user and admin layouts)
  const loadGridConfig = useCallback((configName, isAdminLayout = false) => {
    let config;
    
    if (isAdminLayout) {
      // Find admin layout by name
      const adminLayout = adminGridLayouts.find(l => l.name === configName);
      config = adminLayout?.layout;
    } else {
      config = savedGridConfigs[configName];
    }
    
    if (!config) return;
    
    console.log('[GridConfig] Loading config:', configName, isAdminLayout ? '(Admin)' : '(User)', config);
    
    // Restore cell settings to localStorage FIRST (before updating gridSymbols)
    if (config.cellSettings) {
      Object.entries(config.cellSettings).forEach(([storageKey, settings]) => {
        console.log('[GridConfig] Restoring settings for:', storageKey, settings);
        if (settings.columns && settings.columns.length > 0) {
          localStorage.setItem(`gridcell_cols_${storageKey}`, JSON.stringify(settings.columns));
        }
        if (settings.columnOrder && settings.columnOrder.length > 0) {
          localStorage.setItem(`gridcell_order_${storageKey}`, JSON.stringify(settings.columnOrder));
        }
        if (settings.smartFilters && Object.keys(settings.smartFilters).length > 0) {
          localStorage.setItem(`smartfilters_${storageKey}`, JSON.stringify(settings.smartFilters));
        }
        if (settings.colWidths && Object.keys(settings.colWidths).length > 0) {
          localStorage.setItem(`ticklog_colwidths_${storageKey}`, JSON.stringify(settings.colWidths));
        }
        if (settings.basicFilters && Object.keys(settings.basicFilters).length > 0) {
          localStorage.setItem(`gridcell_filters_${storageKey}`, JSON.stringify(settings.basicFilters));
        }
      });
    }
    
    // Restore cell sizes
    if (config.cellSizes) {
      setGridCellSizes(config.cellSizes);
      localStorage.setItem('ticklog_grid_cell_sizes', JSON.stringify(config.cellSizes));
    }
    
    // Restore cell positions
    if (config.gridCellPositions && Object.keys(config.gridCellPositions).length > 0) {
      setGridCellPositions(config.gridCellPositions);
      localStorage.setItem('ticklog_grid_cell_positions', JSON.stringify(config.gridCellPositions));
    }
    
    // Restore free position mode
    if (typeof config.freePositionMode === 'boolean') {
      setFreePositionMode(config.freePositionMode);
      localStorage.setItem('ticklog_free_position_mode', config.freePositionMode.toString());
    }
    
    // Restore grid visible columns
    if (config.gridVisibleCols && config.gridVisibleCols.length > 0) {
      setGridVisibleCols(config.gridVisibleCols);
      localStorage.setItem('ticklog_grid_cols', JSON.stringify(config.gridVisibleCols));
    }
    
    // Restore grid column order
    if (config.gridColumnOrder && config.gridColumnOrder.length > 0) {
      setGridColumnOrder(config.gridColumnOrder);
      localStorage.setItem('ticklog_grid_order', JSON.stringify(config.gridColumnOrder));
    }
    
    // Close the load menu
    setShowLoadMenu(false);
    
    // Use setTimeout to ensure localStorage writes are complete before re-rendering
    setTimeout(() => {
      // Restore grid symbols
      setGridSymbols(config.gridSymbols || []);
      setGridUserModified(true);
      
      // Increment refresh key to force GridCell components to re-render and re-read localStorage
      setGridRefreshKey(prev => prev + 1);
    }, 50);
  }, [savedGridConfigs, adminGridLayouts]);
  
  // Delete a saved configuration - deletes from DATABASE
  const deleteGridConfig = useCallback(async (configName) => {
    // Delete from database
    try {
      const token = localStorage.getItem('auth_token');
      if (token) {
        await apiClient.delete(`/user/grid-layouts/${encodeURIComponent(configName)}`);
        console.log('[GridConfig] Deleted from database');
      }
    } catch (err) {
      console.warn('[GridConfig] Failed to delete from database:', err.message);
    }
    
    // Also update local state
    setSavedGridConfigs(prev => {
      const newConfigs = { ...prev };
      delete newConfigs[configName];
      return newConfigs;
    });
  }, []);
  
  // Delete an admin readymade layout (admin only)
  const deleteAdminGridLayout = useCallback(async (layoutName) => {
    try {
      await apiClient.delete(`/admin/grid-layouts/${encodeURIComponent(layoutName)}`);
      console.log('[GridConfig] Admin layout deleted:', layoutName);
      // Remove from local state
      setAdminGridLayouts(prev => prev.filter(l => l.name !== layoutName));
    } catch (err) {
      console.warn('[GridConfig] Failed to delete admin layout:', err.message);
    }
  }, []);

  
  // Persist user modification flags
  useEffect(() => {
    localStorage.setItem('ticklog_multi_modified', multiUserModified.toString());
    localStorage.setItem('ticklog_grid_modified', gridUserModified.toString());
  }, [multiUserModified, gridUserModified]);
  
  // Only auto-populate if user has NEVER modified the view
  // Also filter saved symbols to only include tokens still in watchlist
  useEffect(() => {
    if (viewMode === "multi") {
      // Filter multiSymbols to only include tokens in activeTokens (not subscribedTokens)
      const validSymbols = multiSymbols.filter(t => activeTokens.includes(t));
      if (validSymbols.length !== multiSymbols.length) {
        setMultiSymbols(validSymbols);
      }
      // Auto-populate only if never modified and empty
      if (!multiUserModified && validSymbols.length === 0 && activeTokens.length > 0) {
        setMultiSymbols(activeTokens.slice(0, Math.min(3, MAX_SYMBOLS)));
      }
    }
  }, [viewMode, activeTokens, multiUserModified]);
  
  useEffect(() => {
    if (viewMode === "grid") {
      // Filter gridSymbols to only include tokens in activeTokens
      const validSymbols = gridSymbols.filter(s => activeTokens.includes(s.token));
      if (validSymbols.length !== gridSymbols.length) {
        setGridSymbols(validSymbols);
      }
      // Auto-populate only if never modified and empty
      if (!gridUserModified && validSymbols.length === 0 && activeTokens.length > 0) {
        // Create grid items from activeTokens
        const initialGrid = activeTokens.slice(0, MAX_SYMBOLS).map(token => ({
          id: `${token}_1`,
          token: token,
          instance: 1
        }));
        setGridSymbols(initialGrid);
      }
    }
  }, [viewMode, activeTokens, gridUserModified]);
  
  // Helper function to filter ticks by market hours for display
  // ALWAYS filter - no ticks outside market hours should be shown
  const filterTicksByMarketHours = useCallback((ticks, token) => {
    if (!ticks || ticks.length === 0) return ticks;
    
    // Get exchange for market hours filtering
    const instrument = getInstrumentByToken(instruments, token);
    const exchange = instrument?.exchange || 'NSE';
    
    // Filter ticks by market hours - always apply this filter
    return ticks.filter(t => {
      // Use getTickTimestamp to handle various timestamp formats
      const ts = getTickTimestamp(t);
      if (!ts) return false;
      return isWithinMarketHours(ts, exchange);
    });
  }, [instruments]);
  
  // Calculate max values for single view
  // In Light Mode, skip expensive calculations
  const currentTicks = useMemo(() => {
    const rawTicks = stableTickHistory[selectedToken] || [];
    // Filter by market hours before display
    const marketHoursFiltered = filterTicksByMarketHours(rawTicks, selectedToken);
    console.log(`[TickHistory] currentTicks: selectedToken=${selectedToken}, rawTicks=${rawTicks.length}, afterFilter=${marketHoursFiltered.length}`);
    // In Light Mode, limit displayed ticks for performance
    return lightweightMode ? marketHoursFiltered.slice(0, 5000) : marketHoursFiltered;
  }, [stableTickHistory, selectedToken, lightweightMode, filterTicksByMarketHours]);
  
  const maxVals = useMemo(() => {
    // Skip calculations in Light Mode - use default values
    if (lightweightMode) {
      return {
        maxLtq: 100, maxBidQty: 100, maxAskQty: 100, maxVolChg: 100,
        maxOiChg: 100, maxSpread: 10, maxVolume: 100000, maxOi: 100000
      };
    }
    const sample = currentTicks.slice(0, 1000);
    return {
      maxLtq: Math.max(1, ...sample.map(t => t.last_traded_quantity || 0)),
      maxBidQty: Math.max(1, ...sample.map(t => t.bid_qty || 0)),
      maxAskQty: Math.max(1, ...sample.map(t => t.ask_qty || 0)),
      maxVolChg: Math.max(1, ...sample.map(t => Math.abs(t.volume_change || 0))),
      maxOiChg: Math.max(1, ...sample.map(t => Math.abs(t.oi_change || 0))),
      maxSpread: Math.max(1, ...sample.map(t => t.spread || 0)),
      maxVolume: Math.max(1, ...sample.map(t => t.volume || t.volume_traded || 0)),
      maxOi: Math.max(1, ...sample.map(t => t.oi || 0)),
    };
  }, [currentTicks, lightweightMode]);
  
  const isReceivingData = marketStatus?.isReceivingData || false;
  
  return (
    <div className="fixed inset-0 z-50 bg-[#0A0A0A] flex flex-col" data-testid="tick-history-modal">
      
      {/* Header */}
      <div className="flex-shrink-0 border-b border-[#27272A] bg-[#0A0A0A]">
        <div className="h-12 flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Activity className="w-5 h-5 text-blue-500" />
            <h2 className="text-base font-bold text-white">Tick Log</h2>
            
            {/* Live indicator - Shows current mode and setting */}
            <div className={`flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium ${
              isBackendMode 
                ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                : 'bg-green-500/20 text-green-400 border border-green-500/30'
            }`}>
              <div className={`w-1.5 h-1.5 rounded-full ${
                isBackendMode 
                  ? 'bg-cyan-400' 
                  : 'bg-green-400 animate-pulse'
              }`} />
              {isBackendMode 
                ? `🔄 REFRESH ${updateInterval >= 60000 ? `${updateInterval/60000}min` : `${updateInterval/1000}s`}`
                : `⚡ INSTANT`
              }
            </div>
            
            {/* Background Collection Status - always running */}
            <div className="flex items-center gap-1 px-2 py-0.5 rounded text-[11px] font-medium bg-purple-500/20 text-purple-400 border border-purple-500/30" title="Ticks are being collected and saved in background even when this modal is closed">
              <div className="w-1.5 h-1.5 rounded-full bg-purple-400 animate-pulse" />
              BG SAVE
              {backgroundStats?.ticksCollected > 0 && (
                <span className="ml-1 text-[10px] opacity-70">({(backgroundStats.ticksCollected / 1000).toFixed(0)}K)</span>
              )}
            </div>
            
            {/* Performance Settings Button */}
            <Button
              variant={showPerfSettings ? "default" : "ghost"}
              size="sm"
              onClick={() => setShowPerfSettings(!showPerfSettings)}
              className={`h-7 px-2 ${showPerfSettings ? 'bg-orange-600 text-white' : 'text-[#71717A] hover:text-white'}`}
              title="Performance Settings - Reduce laptop heating"
            >
              <Settings2 className="w-3.5 h-3.5 mr-1" />
              <span className="text-[10px]">Perf</span>
            </Button>
            
            {/* Market Status Indicator */}
            {(() => {
              const status = getCurrentMarketStatus();
              const isHistoricalMode = !status.isOpen;
              const bgClass = status.isOpen 
                ? 'bg-green-600/20 text-green-400 border border-green-600/30'
                : status.isPreMarketRefreshZone
                  ? 'bg-yellow-600/20 text-yellow-400 border border-yellow-600/30'
                  : 'bg-orange-600/20 text-orange-400 border border-orange-600/30';
              const dotClass = status.isOpen 
                ? 'bg-green-400 animate-pulse' 
                : status.isPreMarketRefreshZone
                  ? 'bg-yellow-400 animate-pulse'
                  : 'bg-orange-400';
              return (
                <div className={`flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium ${bgClass}`} title={status.message}>
                  <span className={`w-1.5 h-1.5 rounded-full ${dotClass}`}></span>
                  <span>{status.message}</span>
                </div>
              );
            })()}
            
            {/* View Mode Toggle */}
            <div className="flex items-center gap-1 ml-2 bg-[#111] rounded-md p-0.5">
              <Button variant={viewMode === "single" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("single")} className={`h-7 px-3 text-[11px] transition-none ${viewMode === "single" ? "bg-blue-600 text-white hover:bg-blue-600" : "hover:bg-[#222]"}`}>Single</Button>
              <Button variant={viewMode === "multi" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("multi")} className={`h-7 px-3 text-[11px] transition-none ${viewMode === "multi" ? "bg-blue-600 text-white hover:bg-blue-600" : "hover:bg-[#222]"}`}>Multi ({multiSymbols.length})</Button>
              <Button variant={viewMode === "grid" ? "default" : "ghost"} size="sm" onClick={() => setViewMode("grid")} className={`h-7 px-3 text-[11px] transition-none ${viewMode === "grid" ? "bg-blue-600 text-white hover:bg-blue-600" : "hover:bg-[#222]"}`}><Grid3X3 className="w-3 h-3 mr-1" />Grid</Button>
            </div>
            
            {/* Watchlist Source Toggle */}
            <div className="flex items-center gap-1 bg-[#111] rounded-md p-0.5 border border-[#27272A]">
              <Button 
                variant={watchlistSource === "watchlist" ? "default" : "ghost"} 
                size="sm" 
                onClick={() => setWatchlistSource("watchlist")} 
                className={`h-6 px-2 text-[10px] transition-none ${watchlistSource === "watchlist" ? "bg-cyan-600 text-white hover:bg-cyan-600" : "hover:bg-[#222]"}`}
              >
                Watchlist ({subscribedTokens?.length || 0})
              </Button>
              <Button 
                variant={watchlistSource === "my_watchlist" ? "default" : "ghost"} 
                size="sm" 
                onClick={() => setWatchlistSource("my_watchlist")} 
                className={`h-6 px-2 text-[10px] transition-none ${watchlistSource === "my_watchlist" ? "bg-purple-600 text-white hover:bg-purple-600" : "hover:bg-[#222]"}`}
              >
                My Watchlist ({myWatchlistTokens?.length || 0})
              </Button>
            </div>
            
            {/* Symbol Selector (Single) */}
            {viewMode === "single" && (
              <Select value={selectedToken?.toString() || ""} onValueChange={(val) => setSelectedToken(parseInt(val))}>
                <SelectTrigger className="w-48 bg-[#111] border-[#27272A] text-white h-7 text-[11px]">
                  <SelectValue placeholder="Select symbol" />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-[#27272A] max-h-80">
                  {activeTokens.map(token => (
                    <SelectItem key={token} value={token.toString()}>
                      {getSymbolName(instruments, token)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            
            {/* Add Symbol (Multi) - Uses Popover to stay open like Grid view */}
            {viewMode === "multi" && multiSymbols.length < MAX_SYMBOLS && (
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="h-7 w-44 bg-[#111] border-[#27272A] text-white text-[11px] justify-between">
                    + Add Symbol
                    <ChevronDown className="w-3 h-3 ml-2 opacity-50" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-52 p-0 bg-[#111] border-[#333]" align="start">
                  <div className="max-h-[300px] overflow-auto">
                    {activeTokens.filter(t => !multiSymbols.includes(t)).map(token => (
                      <button
                        key={token}
                        onClick={() => addMultiSymbol(token)}
                        className="w-full px-3 py-2 text-left text-[11px] text-white hover:bg-[#1a1a1a] border-b border-[#222] transition-colors"
                      >
                        {getSymbolName(instruments, token)}
                      </button>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            )}
            
            {/* Grid Columns */}
            {viewMode === "grid" && (
              <div className="flex items-center gap-1 ml-2">
                <span className="text-[10px] text-[#71717A]">Cols:</span>
                {[2, 3, 4, 5, 6, 8, 10].map(col => (
                  <Button key={col} variant={gridColumns === col ? "default" : "ghost"} size="sm" onClick={() => setGridColumns(col)} className={`h-5 w-5 p-0 text-[10px] transition-none ${gridColumns === col ? "bg-blue-600 text-white hover:bg-blue-600" : "hover:bg-[#222]"}`}>{col}</Button>
                ))}
              </div>
            )}
          </div>
          
          <div className="flex items-center gap-2">
            {/* Global Filter & Column Settings HIDDEN - Each table has its own dedicated toggle */}
            
            {/* Manual Save Button */}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleManualSave}
              disabled={saving}
              className="h-7 px-3 text-[11px] text-green-400 border-green-400/30 hover:bg-green-600/20"
            >
              {saving ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Save className="w-3 h-3 mr-1" />}
              {saving ? 'Saving...' : 'Save'}
            </Button>
            
            {/* Last Save Time */}
            {lastSaveTime && (
              <span className="text-[10px] text-[#52525B]">
                Last: {lastSaveTime.toLocaleTimeString()}
              </span>
            )}
            
            {/* GLOBAL FILTER DROPDOWN TOGGLE - Top Right Corner */}
            <div className="relative" ref={globalFilterDropdownRef}>
              <button
                type="button"
                onClick={() => setShowGlobalFilterDropdown(!showGlobalFilterDropdown)}
                className={`h-8 px-3 rounded-lg flex items-center gap-2 text-[11px] font-medium transition-all border ${
                  globalFilterSaveMode !== FILTER_SAVE_MODES.OFF || globalSavedTicksCount > 0
                    ? 'bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-400 border-amber-500/50 shadow-lg shadow-amber-500/20'
                    : 'bg-[#1a1a1a] text-[#888] border-[#333] hover:border-amber-500/30 hover:text-amber-400'
                }`}
                title="Filtered Result - Save & Download filtered ticks"
              >
                <Filter className="w-4 h-4" />
                <span className="bg-gradient-to-r from-amber-400 to-yellow-400 bg-clip-text text-transparent font-semibold">Filtered Result</span>
                {globalSavedTicksCount > 0 && (
                  <span className="px-1.5 py-0.5 text-[9px] rounded bg-gradient-to-r from-amber-500/30 to-yellow-500/30 text-amber-300 font-bold">
                    {globalSavedTicksCount.toLocaleString()}
                  </span>
                )}
                <ChevronDown className={`w-3 h-3 transition-transform ${showGlobalFilterDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Dropdown Panel - Premium Design */}
              {showGlobalFilterDropdown && (
                <div className="absolute right-0 top-full mt-2 w-[340px] bg-gradient-to-br from-[#0f0f1a] via-[#1a1a2e] to-[#0d0d15] rounded-2xl border border-amber-500/20 shadow-2xl shadow-amber-500/10 z-50 overflow-hidden backdrop-blur-xl">
                  {/* Premium Header */}
                  <div className="px-5 py-4 bg-gradient-to-r from-amber-500/10 via-yellow-500/10 to-amber-500/10 border-b border-amber-500/20">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-400 to-yellow-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
                        <Filter className="w-4 h-4 text-white" />
                      </div>
                      <div>
                        <h3 className="text-[13px] font-bold bg-gradient-to-r from-amber-400 via-yellow-400 to-amber-400 bg-clip-text text-transparent">
                          Filtered Result
                        </h3>
                        <p className="text-[9px] text-amber-300/60">Premium tick capture & export</p>
                      </div>
                      {globalFilterSaveMode === FILTER_SAVE_MODES.SAVE && (
                        <span className="ml-auto flex items-center gap-1 text-[9px] text-emerald-400 animate-pulse">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-400"></span>
                          Recording
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Mode Toggles - Premium Cards */}
                  <div className="p-4 space-y-3">
                    <p className="text-[9px] text-amber-400/80 uppercase tracking-wider font-bold mb-2">Capture Mode</p>
                    <div className="flex gap-2">
                      {/* Save All */}
                      <button
                        type="button"
                        onClick={() => setGlobalFilterSaveMode(globalFilterSaveMode === FILTER_SAVE_MODES.SAVE ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.SAVE)}
                        className={`flex-1 h-10 px-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-bold transition-all ${
                          globalFilterSaveMode === FILTER_SAVE_MODES.SAVE
                            ? 'bg-gradient-to-br from-emerald-500/40 to-green-600/40 text-emerald-300 border-2 border-emerald-400 shadow-lg shadow-emerald-500/30 scale-105'
                            : 'bg-gradient-to-br from-emerald-500/15 to-green-600/15 text-emerald-400 border border-emerald-500/50 hover:from-emerald-500/25 hover:to-green-600/25'
                        }`}
                      >
                        <Archive className="w-4 h-4" />
                        <span>Save All</span>
                      </button>
                      
                      {/* Show All */}
                      <button
                        type="button"
                        onClick={() => setGlobalFilterSaveMode(globalFilterSaveMode === FILTER_SAVE_MODES.SHOW_ALL ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.SHOW_ALL)}
                        className={`flex-1 h-10 px-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-bold transition-all ${
                          globalFilterSaveMode === FILTER_SAVE_MODES.SHOW_ALL
                            ? 'bg-gradient-to-br from-blue-500/40 to-cyan-600/40 text-cyan-300 border-2 border-cyan-400 shadow-lg shadow-cyan-500/30 scale-105'
                            : 'bg-gradient-to-br from-blue-500/15 to-cyan-600/15 text-cyan-400 border border-cyan-500/50 hover:from-blue-500/25 hover:to-cyan-600/25'
                        }`}
                      >
                        <Eye className="w-4 h-4" />
                        <span>Show All</span>
                      </button>
                      
                      {/* Hide All */}
                      <button
                        type="button"
                        onClick={() => setGlobalFilterSaveMode(globalFilterSaveMode === FILTER_SAVE_MODES.HIDE ? FILTER_SAVE_MODES.OFF : FILTER_SAVE_MODES.HIDE)}
                        className={`flex-1 h-10 px-3 rounded-xl flex items-center justify-center gap-2 text-[10px] font-bold transition-all ${
                          globalFilterSaveMode === FILTER_SAVE_MODES.HIDE
                            ? 'bg-gradient-to-br from-orange-500/40 to-red-600/40 text-orange-300 border-2 border-orange-400 shadow-lg shadow-orange-500/30 scale-105'
                            : 'bg-gradient-to-br from-orange-500/15 to-red-600/15 text-orange-400 border border-orange-500/50 hover:from-orange-500/25 hover:to-red-600/25'
                        }`}
                      >
                        <EyeOff className="w-4 h-4" />
                        <span>Hide</span>
                      </button>
                    </div>
                    
                    {/* Global Alert Toggle */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-yellow-500/5 to-amber-500/5 border border-yellow-500/20">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-yellow-500/30 to-amber-600/30 flex items-center justify-center">
                          <Bell className="w-3 h-3 text-yellow-400" />
                        </div>
                        <div>
                          <span className="text-[10px] text-yellow-300 font-medium block">Alert Notifications</span>
                          <span className="text-[8px] text-yellow-400/60">Sound + notification on new ticks</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setGlobalAlertEnabled(!globalAlertEnabled);
                          // Request notification permission
                          if (!globalAlertEnabled && 'Notification' in window && Notification.permission === 'default') {
                            Notification.requestPermission();
                          }
                        }}
                        className={`h-7 px-4 rounded-lg text-[9px] font-bold transition-all ${
                          globalAlertEnabled
                            ? 'bg-gradient-to-r from-yellow-500/40 to-amber-500/40 text-yellow-200 border-2 border-yellow-400 shadow-lg shadow-yellow-500/30 animate-pulse'
                            : 'bg-gradient-to-r from-yellow-500/15 to-amber-500/15 text-yellow-400 border border-yellow-500/40 hover:from-yellow-500/25 hover:to-amber-500/25'
                        }`}
                      >
                        {globalAlertEnabled ? '🔔 ON' : 'OFF'}
                      </button>
                      {/* Test Sound Button */}
                      <button
                        type="button"
                        onClick={() => playAlertSound('chime')}
                        className="h-7 px-2 rounded-lg text-[9px] font-medium bg-[#1a1a1a] text-yellow-400 border border-yellow-500/30 hover:bg-yellow-500/20 transition-all"
                        title="Test alert sound"
                      >
                        <Volume2 className="w-3 h-3" />
                      </button>
                    </div>
                    
                    {/* Saved Count & Download - Premium Style */}
                    <div className="mt-4 p-3 rounded-xl bg-gradient-to-r from-violet-500/5 to-purple-500/5 border border-violet-500/20">
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-violet-500/30 to-purple-600/30 flex items-center justify-center">
                            <Save className="w-3 h-3 text-violet-400" />
                          </div>
                          <span className="text-[10px] text-violet-300 font-medium">Saved Ticks</span>
                        </div>
                        <span className="text-[14px] font-bold bg-gradient-to-r from-violet-400 to-purple-400 bg-clip-text text-transparent">
                          {globalSavedTicksCount.toLocaleString()}
                        </span>
                      </div>
                      
                      <div className="flex gap-2">
                        {/* Download Excel - Premium */}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            console.log('[Global Filter Dropdown] Excel clicked');
                            downloadGlobalSavedTicks();
                          }}
                          className={`flex-1 h-9 rounded-xl flex items-center justify-center gap-2 text-[10px] font-bold transition-all ${
                            globalSavedTicksCount > 0
                              ? 'bg-gradient-to-r from-cyan-500/30 to-blue-500/30 text-cyan-300 border border-cyan-400/60 hover:from-cyan-500/40 hover:to-blue-500/40 shadow-lg shadow-cyan-500/20'
                              : 'bg-gradient-to-r from-cyan-500/10 to-blue-500/10 text-cyan-500 border border-cyan-500/30 hover:from-cyan-500/20 hover:to-blue-500/20'
                          }`}
                        >
                          <Download className="w-4 h-4" />
                          <span>Download Excel</span>
                        </button>
                        
                        {/* Clear */}
                        {globalSavedTicksCount > 0 && (
                          <button
                            type="button"
                            onClick={() => clearGlobalSavedTicks()}
                            className="h-9 px-3 rounded-xl text-red-400 border border-red-500/30 hover:bg-red-500/20 transition-all"
                            title="Clear all saved ticks"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Auto Download Toggle - Premium */}
                    <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-purple-500/5 to-pink-500/5 border border-purple-500/20">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-lg bg-gradient-to-br from-purple-500/30 to-pink-600/30 flex items-center justify-center">
                          <Clock className="w-3 h-3 text-purple-400" />
                        </div>
                        <div>
                          <span className="text-[10px] text-purple-300 font-medium block">Auto-Download</span>
                          <span className="text-[8px] text-purple-400/60">At market close (3:31 PM)</span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setGlobalAutoDownload(!globalAutoDownload)}
                        className={`h-7 px-4 rounded-lg text-[9px] font-bold transition-all ${
                          globalAutoDownload
                            ? 'bg-gradient-to-r from-purple-500/40 to-pink-500/40 text-purple-200 border-2 border-purple-400 shadow-lg shadow-purple-500/30'
                            : 'bg-gradient-to-r from-purple-500/15 to-pink-500/15 text-purple-400 border border-purple-500/40 hover:from-purple-500/25 hover:to-pink-500/25'
                        }`}
                      >
                        {globalAutoDownload ? '✓ ON' : 'OFF'}
                      </button>
                    </div>
                  </div>
                  
                  {/* Footer - Premium Tip */}
                  <div className="px-5 py-3 bg-gradient-to-r from-amber-500/10 via-transparent to-amber-500/10 border-t border-amber-500/20">
                    <p className="text-[9px] text-amber-300/80 text-center flex items-center justify-center gap-2">
                      <span className="text-amber-400">💡</span>
                      <span>Apply filters on tables, then click <span className="text-emerald-400 font-bold">Save All</span> to capture</span>
                    </p>
                  </div>
                </div>
              )}
            </div>
            
            <Button variant="ghost" size="sm" onClick={onClose} className="h-6 w-6 p-0">
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
        
        {/* Performance Settings Panel */}
        {showPerfSettings && (
          <div className="h-auto flex flex-wrap items-center gap-4 px-4 py-2 bg-[#0f0f0f] border-t border-[#27272A]">
            <span className="text-[11px] text-orange-400 font-bold">⚡ Performance Settings:</span>
            
            {/* Simplified Mode Selector - Only 3 modes */}
            <div className="flex items-center gap-1">
              <span className="text-[11px] text-[#71717A]">Mode:</span>
              <Select value={performancePreset} onValueChange={applyPreset}>
                <SelectTrigger className="h-7 w-48 text-[11px] bg-[#0a0a0a] border-[#27272A] text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#111] border-[#27272A]">
                  {/* Instant Mode - True Real-time */}
                  <SelectItem value="instant" className="text-[11px] text-green-400">
                    ⚡ Instant (Real-time)
                  </SelectItem>
                  
                  {/* Batch Mode - Batched updates */}
                  <SelectItem value="batch" className="text-[11px] text-yellow-400">
                    📦 Batch (Low CPU)
                  </SelectItem>
                  
                  {/* Divider */}
                  <div className="px-2 py-1 text-[9px] text-cyan-400/70 border-t border-[#27272A] mt-1">
                    Auto-refresh modes ↓
                  </div>
                  
                  {/* Backend Modes - Auto Refresh */}
                  <SelectItem value="backend_30s" className="text-[11px] text-cyan-400">
                    🔄 Refresh 30s
                  </SelectItem>
                  <SelectItem value="backend_1min" className="text-[11px] text-cyan-400">
                    🔄 Refresh 1min
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Mode explanation with CPU indicator */}
            <div className={`text-[10px] px-2 py-1 rounded flex items-center gap-2 ${
              isBackendMode 
                ? 'bg-cyan-900/30 text-cyan-300' 
                : performancePreset === 'batch' 
                  ? 'bg-yellow-900/30 text-yellow-300'
                  : 'bg-green-900/30 text-green-300'
            }`}>
              {isBackendMode 
                ? <>🔄 Database polling every {updateInterval/1000}s - <span className="text-cyan-400 font-bold">LOW CPU</span></> 
                : performancePreset === 'batch'
                  ? <>📦 Batched updates (200ms) - <span className="text-yellow-400 font-bold">LOW CPU</span></>
                  : <>⚡ Real-time updates (20ms) - <span className="text-red-400 font-bold">HIGH CPU</span></>
              }
            </div>
            
            {/* Multi-view Table Width (only for Multi view) */}
            {viewMode === 'multi' && (
              <div className="flex items-center gap-1 border-l border-[#333] pl-4">
                <span className="text-[11px] text-cyan-400">Table Width:</span>
                <Select value={multiTableWidth.toString()} onValueChange={(v) => setMultiTableWidth(parseInt(v))}>
                  <SelectTrigger className="h-7 w-28 text-[11px] bg-[#0a0a0a] border-[#27272A] text-white">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#111] border-[#27272A]">
                    <SelectItem value="0">Auto</SelectItem>
                    <SelectItem value="400">400px</SelectItem>
                    <SelectItem value="500">500px</SelectItem>
                    <SelectItem value="600">600px</SelectItem>
                    <SelectItem value="700">700px</SelectItem>
                    <SelectItem value="800">800px</SelectItem>
                    <SelectItem value="900">900px</SelectItem>
                    <SelectItem value="1000">1000px</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {/* Compact Mode Toggle - For ALL views - Only visible when Light Mode is OFF */}
            {!lightweightMode && (
              <div className="flex items-center gap-2 border-l border-[#333] pl-4">
                <span className="text-[11px] text-purple-400">Compact:</span>
                <Button 
                  variant={compactMode ? "default" : "outline"}
                  size="sm"
                  onClick={() => setCompactMode(!compactMode)}
                  className={`h-7 px-3 text-[10px] ${compactMode ? 'bg-purple-600 text-white' : 'bg-[#0a0a0a] border-[#333] text-[#71717A] hover:text-white'}`}
                  data-testid="compact-mode-toggle"
                >
                  {compactMode ? 'ON' : 'OFF'}
                </Button>
                <span className="text-[9px] text-[#52525B]">Smaller cells</span>
              </div>
            )}
            
            {/* LIGHT MODE - For ALL views - Includes compact styling */}
            <div className="flex items-center gap-2 border-l border-[#333] pl-4">
              <span className="text-[11px] text-yellow-400 font-semibold">Light Mode:</span>
              <Button 
                variant={lightweightMode ? "default" : "outline"}
                size="sm"
                onClick={() => setLightweightMode(!lightweightMode)}
                className={`h-7 px-3 text-[10px] font-bold ${lightweightMode ? 'bg-yellow-600 text-black hover:bg-yellow-500' : 'bg-[#0a0a0a] border-yellow-500/30 text-yellow-400 hover:bg-yellow-900/20'}`}
                data-testid="lightweight-mode-toggle"
              >
                {lightweightMode ? 'ON' : 'OFF'}
              </Button>
              {lightweightMode && (
                <LightweightColumnSelector 
                  lightweightCols={lightweightCols}
                  setLightweightCols={setLightweightCols}
                />
              )}
              <span className="text-[9px] text-[#52525B]">{lightweightMode ? `${lightweightCols.length} cols + compact` : '20+ tables'}</span>
            </div>

            {/* PERF MODE V2 Toggle - keeps deep 200k buffer for filters/alerts, light render */}
            <div className="flex items-center gap-2 border-l border-[#333] pl-4">
              <span className="text-[11px] text-cyan-400 font-semibold" title="Performance Mode V2: deep 200k tick buffer for filters/alerts, light render. Helps full-day sessions stay smooth. Toggle OFF to fall back to old behavior. Reload required after toggling.">
                Perf V2:
              </span>
              <Button
                variant={perfModeV2 ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setPerfModeV2(!perfModeV2);
                  toast.info(`Perf V2 ${!perfModeV2 ? 'ENABLED' : 'DISABLED'} - reload the page to apply`);
                }}
                className={`h-7 px-3 text-[10px] font-bold ${perfModeV2 ? 'bg-cyan-600 text-white hover:bg-cyan-500' : 'bg-[#0a0a0a] border-cyan-500/30 text-cyan-400 hover:bg-cyan-900/20'}`}
                data-testid="perf-mode-v2-toggle"
                title="Toggle deep buffer mode for filters/alerts. Requires reload."
              >
                {perfModeV2 ? 'ON' : 'OFF'}
              </Button>
              <span className="text-[9px] text-[#52525B]">{perfModeV2 ? '200k buffer, light render' : 'classic'}</span>
            </div>

            {/* Normal Mode Column Settings - Only show when Light Mode is OFF */}
            {!lightweightMode && (
              <div className="flex items-center gap-2 border-l border-[#333] pl-4">
                <span className="text-[11px] text-blue-400">Columns:</span>
                <ColumnSettings 
                  visibleCols={viewMode === 'multi' ? multiVisibleCols : viewMode === 'grid' ? gridVisibleCols : visibleCols} 
                  setVisibleCols={viewMode === 'multi' ? setMultiVisibleCols : viewMode === 'grid' ? setGridVisibleCols : setVisibleCols} 
                  columnOrder={viewMode === 'multi' ? multiColumnOrder : viewMode === 'grid' ? gridColumnOrder : columnOrder}
                  setColumnOrder={viewMode === 'multi' ? setMultiColumnOrder : viewMode === 'grid' ? setGridColumnOrder : setColumnOrder}
                />
              </div>
            )}
            
            <span className="text-[10px] text-[#52525B] ml-auto">
              💡 Lower values = Less CPU usage, less laptop heating
            </span>
          </div>
        )}
      </div>
      
      {/* Content */}
      {viewMode === "single" ? (
        /* SINGLE VIEW - Now uses exact same GridCell component as Multi/Grid for identical tick behavior */
        <div className="flex-1 overflow-hidden flex flex-col relative p-2">
          {/* Premium Loading UI */}
          {isLoadingHistory && selectedToken && (stableTickHistory[selectedToken] || []).length === 0 && (
            <PremiumLoadingUI message="Loading tick history..." />
          )}
          
          {selectedToken ? (
            <GridCell
              token={selectedToken}
              gridId={`single_${selectedToken}`}
              symbol={getSymbolName(instruments, selectedToken)}
              ticks={filterTicksByMarketHours(stableTickHistory[selectedToken] || [], selectedToken).slice(0, lightweightMode ? Math.min(maxTicksPerSymbol, 1000) : maxTicksPerSymbol)}
              columns={lightweightMode ? lightweightCols : visibleCols}
              columnOrder={lightweightMode ? lightweightCols : columnOrder}
              exchange={getExchangeFromInstrument(getInstrumentByToken(instruments, selectedToken))}
              onRemove={null}
              compact={compactMode || lightweightMode}
              onLoadMore={handleLoadMore}
              isLoadingMore={loadMoreState[selectedToken]?.loading || false}
              hasMoreHistory={loadMoreState[selectedToken]?.hasMore !== false}
              isInitialLoading={isInitialLoading}
              smartFilters={smartFilters}
              globalAlertEnabled={globalAlertEnabled}
            />
          ) : (
            <div className="h-full flex items-center justify-center">
              <p className="text-[#71717A] text-[12px]">Select a symbol</p>
            </div>
          )}
        </div>
      ) : viewMode === "multi" ? (
        /* MULTI VIEW - Side by side with horizontal scroll - AUTO or CUSTOM WIDTH */
        <div className="flex-1 overflow-x-auto overflow-y-hidden p-2">
          {multiSymbols.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-[#71717A] text-[12px]">Add symbols using dropdown above (max {MAX_SYMBOLS})</p>
            </div>
          ) : (
            <div className="flex gap-3 h-full" style={{ minWidth: `${multiSymbols.length * ((multiTableWidth || calculateTableWidth(lightweightMode ? lightweightCols : multiVisibleCols, compactMode || lightweightMode)) + 12)}px` }}>
              {multiSymbols.map(token => (
                <div key={token} className="flex-shrink-0 h-full" style={{ width: `${multiTableWidth || calculateTableWidth(lightweightMode ? lightweightCols : multiVisibleCols, compactMode || lightweightMode)}px` }}>
                  <GridCell
                    token={token}
                    symbol={getSymbolName(instruments, token)}
                    ticks={filterTicksByMarketHours(stableTickHistory[token] || [], token).slice(0, lightweightMode ? Math.min(maxTicksPerSymbol, 1000) : maxTicksPerSymbol)}
                    columns={lightweightMode ? lightweightCols : multiVisibleCols}
                    columnOrder={lightweightMode ? lightweightCols : multiColumnOrder}
                    exchange={getExchangeFromInstrument(getInstrumentByToken(instruments, token))}
                    onRemove={() => removeMultiSymbol(token)}
                    compact={compactMode || lightweightMode}
                    onLoadMore={handleLoadMore}
                    isLoadingMore={loadMoreState[token]?.loading || false}
                    hasMoreHistory={loadMoreState[token]?.hasMore !== false}
                    isInitialLoading={isInitialLoading || (isLoadingHistory && !historicalTicksLoaded.has(token))}
                    smartFilters={smartFilters}
                    globalFilterMode={globalFilterSaveMode}
                    globalHideTimestamp={globalHideTimestampRef.current}
                    onAddToGlobalSaved={addToGlobalSavedTicks}
                    globalAlertEnabled={globalAlertEnabled}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        /* GRID VIEW - With Add/Delete Symbol functionality (supports multi-instance) */
        <div className="flex-1 overflow-auto p-2">
          {/* Add Symbol Bar for Grid View - Uses Popover to stay open */}
          <div className="flex items-center gap-2 mb-2 px-2">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="h-7 w-52 bg-[#111] border-[#333] text-white text-[11px] justify-between">
                  + Add Symbol to Grid
                  <ChevronDown className="w-3 h-3 ml-2 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-52 p-0 bg-[#111] border-[#333]" align="start">
                <div className="max-h-[300px] overflow-auto">
                  {activeTokens.map(token => {
                    const instanceCount = gridSymbols.filter(s => s.token === token).length;
                    const canAddMore = instanceCount < MAX_INSTANCES_PER_SYMBOL && gridSymbols.length < MAX_SYMBOLS;
                    return (
                      <button
                        key={token}
                        onClick={() => canAddMore && addGridSymbol(token)}
                        disabled={!canAddMore}
                        className={`w-full px-3 py-2 text-left text-[11px] border-b border-[#222] transition-colors ${
                          canAddMore 
                            ? 'text-white hover:bg-[#1a1a1a] cursor-pointer' 
                            : 'text-[#555] cursor-not-allowed'
                        }`}
                      >
                        {getSymbolName(instruments, token)}
                        {instanceCount > 0 && (
                          <span className={`ml-2 text-[9px] ${instanceCount >= MAX_INSTANCES_PER_SYMBOL ? 'text-red-400' : 'text-cyan-400'}`}>
                            ({instanceCount}/{MAX_INSTANCES_PER_SYMBOL})
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </PopoverContent>
            </Popover>
            <span className="text-[10px] text-[#71717A]">
              {gridSymbols.length} / {MAX_SYMBOLS} cells
            </span>
            <span className="text-[9px] text-cyan-500">
              (Same symbol up to {MAX_INSTANCES_PER_SYMBOL}x)
            </span>
            
            {/* Save/Load Grid Config Buttons */}
            <div className="flex items-center gap-1 ml-auto">
              {/* AI Stock Scan: open in new window (real-time auto-refresh via polling) */}
              {viewMode === 'grid' && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const w = window.open(
                      '/algo-setup-popout',
                      'algo-setup-popout',
                      'width=1400,height=900,resizable=yes,scrollbars=yes'
                    );
                    if (!w) {
                      toast.error('Pop-up blocked. Please allow pop-ups for this site.');
                    }
                  }}
                  className="h-7 px-2 bg-cyan-900/30 border-cyan-600/50 text-cyan-400 hover:bg-cyan-900/50 text-[10px]"
                  data-testid="grid-ai-stock-scan-popout"
                  title="Open AI Stock Scan in a new window (real-time)"
                >
                  <Table2 className="w-3 h-3 mr-1" />
                  AI Stock Scan
                </Button>
              )}
              {/* Save Button */}
              <Popover open={showSaveDialog} onOpenChange={setShowSaveDialog}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 px-2 bg-green-900/30 border-green-600/50 text-green-400 hover:bg-green-900/50 text-[10px]">
                    <Save className="w-3 h-3 mr-1" />
                    Save
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-64 p-3 bg-[#111] border-[#333]" align="end">
                  <div className="space-y-2">
                    <h4 className="text-[11px] font-bold text-white">Save Grid Configuration</h4>
                    <p className="text-[9px] text-[#71717A]">Saves symbols, columns & filters for each table</p>
                    <Input
                      value={saveConfigName}
                      onChange={(e) => setSaveConfigName(e.target.value)}
                      placeholder="Enter config name..."
                      className="h-7 bg-[#0a0a0a] border-[#333] text-[11px] text-white"
                      onKeyDown={(e) => e.key === 'Enter' && saveGridConfig(saveConfigName)}
                    />
                    {isAdmin && (
                      <label className="flex items-center gap-2 cursor-pointer select-none">
                        <input
                          type="checkbox"
                          checked={saveAsAdminLayout}
                          onChange={(e) => setSaveAsAdminLayout(e.target.checked)}
                          className="accent-amber-500 w-3.5 h-3.5"
                          data-testid="save-as-admin-layout-checkbox"
                        />
                        <span className="text-[10px] text-amber-400 font-medium">Save as Readymade Layout (visible to all users)</span>
                      </label>
                    )}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        onClick={() => saveGridConfig(saveConfigName, saveAsAdminLayout)}
                        disabled={!saveConfigName.trim()}
                        className={`flex-1 h-7 text-[10px] ${saveAsAdminLayout ? 'bg-amber-600 hover:bg-amber-700' : 'bg-green-600 hover:bg-green-700'}`}
                        data-testid="save-layout-button"
                      >
                        {saveAsAdminLayout ? 'Save as Readymade' : 'Save'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setShowSaveDialog(false); setSaveConfigName(''); setSaveAsAdminLayout(false); }}
                        className="h-7 text-[10px] text-[#71717A]"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                </PopoverContent>
              </Popover>
              
              {/* Load Button */}
              <Popover open={showLoadMenu} onOpenChange={setShowLoadMenu}>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-7 px-2 bg-cyan-900/30 border-cyan-600/50 text-cyan-400 hover:bg-cyan-900/50 text-[10px]">
                    <Download className="w-3 h-3 mr-1" />
                    Load
                    {(Object.keys(savedGridConfigs).length + adminGridLayouts.length) > 0 && (
                      <span className="ml-1 text-[8px] bg-cyan-600 px-1 rounded">{Object.keys(savedGridConfigs).length + adminGridLayouts.length}</span>
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-72 p-0 bg-[#111] border-[#333]" align="end">
                  <div className="p-2 border-b border-[#333]">
                    <h4 className="text-[11px] font-bold text-white">Load Grid Configuration</h4>
                    <p className="text-[9px] text-[#71717A]">Click to load a saved grid setup</p>
                  </div>
                  <div className="max-h-[300px] overflow-auto">
                    {/* Readymade Layouts Section (Admin-created, visible to all) */}
                    {adminGridLayouts.length > 0 && (
                      <>
                        <div className="px-3 py-1.5 bg-amber-900/20 border-b border-amber-600/20">
                          <span className="text-[10px] font-bold text-amber-400 uppercase tracking-wider">Readymade Layouts</span>
                        </div>
                        {adminGridLayouts.map((adminLayout) => (
                          <div key={`admin-${adminLayout.name}`} className="flex items-center justify-between px-3 py-2 border-b border-[#222] hover:bg-amber-900/10 group" data-testid={`admin-layout-${adminLayout.name}`}>
                            <button
                              onClick={() => loadGridConfig(adminLayout.name, true)}
                              className="flex-1 text-left"
                              data-testid={`load-admin-layout-${adminLayout.name}`}
                            >
                              <div className="text-[11px] text-amber-300 font-medium flex items-center gap-1.5">
                                <span className="text-[8px] bg-amber-600/30 text-amber-400 px-1 rounded">PRO</span>
                                {adminLayout.name}
                              </div>
                              <div className="text-[9px] text-[#666]">
                                {adminLayout.layout?.gridSymbols?.length || 0} cells
                                {adminLayout.description ? ` • ${adminLayout.description}` : ''}
                              </div>
                            </button>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); deleteAdminGridLayout(adminLayout.name); }}
                                className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30 opacity-0 group-hover:opacity-100"
                                data-testid={`delete-admin-layout-${adminLayout.name}`}
                              >
                                <X className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        ))}
                      </>
                    )}
                    
                    {/* My Layouts Section (User's private layouts) */}
                    <div className="px-3 py-1.5 bg-cyan-900/20 border-b border-cyan-600/20">
                      <span className="text-[10px] font-bold text-cyan-400 uppercase tracking-wider">My Layouts</span>
                    </div>
                    {Object.keys(savedGridConfigs).length === 0 ? (
                      <div className="p-4 text-center text-[11px] text-[#666]">
                        No saved configurations yet
                      </div>
                    ) : (
                      Object.entries(savedGridConfigs).map(([name, config]) => (
                        <div key={`user-${name}`} className="flex items-center justify-between px-3 py-2 border-b border-[#222] hover:bg-[#1a1a1a] group" data-testid={`user-layout-${name}`}>
                          <button
                            onClick={() => loadGridConfig(name)}
                            className="flex-1 text-left"
                            data-testid={`load-user-layout-${name}`}
                          >
                            <div className="text-[11px] text-white font-medium">{name}</div>
                            <div className="text-[9px] text-[#666]">
                              {config.gridSymbols?.length || 0} cells • Saved {new Date(config.savedAt).toLocaleDateString()}
                            </div>
                          </button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => { e.stopPropagation(); deleteGridConfig(name); }}
                            className="h-6 w-6 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30 opacity-0 group-hover:opacity-100"
                            data-testid={`delete-user-layout-${name}`}
                          >
                            <X className="w-3 h-3" />
                          </Button>
                        </div>
                      ))
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              
              {/* Custom Layout Toggle */}
              <Button
                variant={customLayoutMode ? "default" : "outline"}
                size="sm"
                onClick={() => setCustomLayoutMode(!customLayoutMode)}
                data-testid="custom-layout-toggle"
                className={`h-7 px-2 text-[10px] ${
                  customLayoutMode 
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-0 shadow-lg shadow-purple-500/30' 
                    : 'bg-purple-900/30 border-purple-600/50 text-purple-400 hover:bg-purple-900/50'
                }`}
                title="Enable custom layout mode to resize and reorder tables"
              >
                <Grid3X3 className="w-3 h-3 mr-1" />
                {customLayoutMode ? 'Custom ON' : 'Custom'}
              </Button>
            </div>
          </div>
          
          {/* Custom Layout Mode Instructions & Controls */}
          {customLayoutMode && (
            <div className="mx-2 mb-2 p-2 bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/30 rounded-lg">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-[10px] text-purple-300">
                  <span className="font-bold">Custom Layout Mode:</span> 
                  {freePositionMode 
                    ? ' Drag tables anywhere on screen • Drag edges/corners to resize • No overlap'
                    : ' Drag tables to reorder • Drag edges/corners to resize'
                  }
                </p>
                <div className="flex items-center gap-3">
                  {/* Free Position Mode Toggle */}
                  <button
                    onClick={() => setFreePositionMode(!freePositionMode)}
                    className={`px-2 py-1 text-[9px] rounded font-medium transition-all ${
                      freePositionMode 
                        ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg shadow-green-500/30' 
                        : 'bg-green-900/30 text-green-400 hover:bg-green-900/50 border border-green-600/50'
                    }`}
                    title={freePositionMode ? 'Free positioning ON - drag tables anywhere' : 'Enable free positioning to place tables anywhere'}
                  >
                    {freePositionMode ? '📌 Free Position ON' : '📌 Free Position'}
                  </button>
                  
                  {/* Gap Control - always visible */}
                  <div className="flex items-center gap-2 bg-black/30 px-2 py-1 rounded">
                    <span className="text-[9px] text-cyan-400 font-medium">Gap:</span>
                    <input
                      type="range"
                      min="0"
                      max="50"
                      value={gridGap}
                      onChange={(e) => setGridGap(parseInt(e.target.value))}
                      className="w-20 h-1 accent-cyan-500 cursor-pointer"
                      title={`Table spacing: ${gridGap}px`}
                    />
                    <span className="text-[9px] text-cyan-300 font-mono w-6">{gridGap}px</span>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => setGridGap(0)} className={`px-1.5 py-0.5 text-[8px] rounded ${gridGap === 0 ? 'bg-cyan-500 text-black' : 'bg-cyan-900/50 text-cyan-400 hover:bg-cyan-800/50'}`}>0</button>
                    <button onClick={() => setGridGap(4)} className={`px-1.5 py-0.5 text-[8px] rounded ${gridGap === 4 ? 'bg-cyan-500 text-black' : 'bg-cyan-900/50 text-cyan-400 hover:bg-cyan-800/50'}`}>4</button>
                    <button onClick={() => setGridGap(8)} className={`px-1.5 py-0.5 text-[8px] rounded ${gridGap === 8 ? 'bg-cyan-500 text-black' : 'bg-cyan-900/50 text-cyan-400 hover:bg-cyan-800/50'}`}>8</button>
                    <button onClick={() => setGridGap(16)} className={`px-1.5 py-0.5 text-[8px] rounded ${gridGap === 16 ? 'bg-cyan-500 text-black' : 'bg-cyan-900/50 text-cyan-400 hover:bg-cyan-800/50'}`}>16</button>
                  </div>
                  
                  {/* Auto Arrange & Reset buttons - only in free position mode */}
                  {freePositionMode && (
                    <>
                      <button
                        onClick={() => {
                          // Auto arrange all tables with exact gap spacing
                          const newPositions = {};
                          let currentX = 0;
                          let currentY = 0;
                          let rowMaxHeight = 0;
                          const containerWidth = 1800; // Approximate container width
                          
                          gridSymbols.forEach((item, index) => {
                            const size = gridCellSizes[item.id] || { width: 450, height: 350 };
                            
                            // Check if this table fits in current row
                            if (currentX + size.width > containerWidth && currentX > 0) {
                              // Move to next row
                              currentX = 0;
                              currentY += rowMaxHeight + gridGap;
                              rowMaxHeight = 0;
                            }
                            
                            newPositions[item.id] = { x: currentX, y: currentY };
                            
                            // Update row tracking
                            rowMaxHeight = Math.max(rowMaxHeight, size.height);
                            currentX += size.width + gridGap;
                          });
                          
                          setGridCellPositions(newPositions);
                        }}
                        className="px-2 py-1 text-[9px] rounded bg-blue-900/30 text-blue-400 hover:bg-blue-900/50 border border-blue-600/50"
                        title="Auto arrange all tables with exact gap spacing"
                      >
                        ⚡ Auto Arrange
                      </button>
                      <button
                        onClick={() => setGridCellPositions({})}
                        className="px-2 py-1 text-[9px] rounded bg-red-900/30 text-red-400 hover:bg-red-900/50 border border-red-600/50"
                        title="Reset all table positions"
                      >
                        Reset
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
          
          {gridSymbols.length === 0 ? (
            <div className="h-full flex items-center justify-center">
              <p className="text-[#71717A] text-[12px]">Add symbols using dropdown above (max {MAX_SYMBOLS})</p>
            </div>
          ) : customLayoutMode ? (
            /* Custom Layout Mode - No restrictions, full screen usage */
            <div 
              className={`overflow-auto h-full ${freePositionMode && draggedItem ? 'bg-purple-900/10' : ''}`}
              ref={(el) => {
                // Store container ref for free position mode
                if (el) el._gridContainer = true;
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                e.dataTransfer.dropEffect = 'move';
              }}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                
                if (freePositionMode && draggedItem) {
                  // In free position mode, drop at exact mouse position
                  const rect = e.currentTarget.getBoundingClientRect();
                  let newX = e.clientX - rect.left + e.currentTarget.scrollLeft;
                  let newY = e.clientY - rect.top + e.currentTarget.scrollTop;
                  
                  // Get dragged item's size
                  const draggedSize = gridCellSizes[draggedItem.id] || { width: 450, height: 350 };
                  
                  // Check collision with all other tables
                  const otherTables = gridSymbols.filter(s => s.id !== draggedItem.id);
                  
                  // Function to check if two rectangles actually overlap (no gap consideration)
                  const checkActualOverlap = (x1, y1, w1, h1, x2, y2, w2, h2) => {
                    return !(x1 + w1 <= x2 || x2 + w2 <= x1 || 
                             y1 + h1 <= y2 || y2 + h2 <= y1);
                  };
                  
                  // Function to get the minimum adjustment needed to avoid overlap with gap
                  const getAdjustment = (x1, y1, w1, h1, x2, y2, w2, h2) => {
                    const gap = gridGap;
                    // Calculate how much we need to move in each direction
                    const moveRight = (x2 + w2 + gap) - x1;  // Move right of other table
                    const moveLeft = x1 + w1 + gap - x2;     // How far we're past the left edge
                    const moveDown = (y2 + h2 + gap) - y1;   // Move below other table
                    const moveUp = y1 + h1 + gap - y2;       // How far we're past the top edge
                    
                    // Find minimum adjustment
                    const adjustments = [
                      { dx: moveRight, dy: 0, dist: Math.abs(moveRight) },
                      { dx: -moveLeft, dy: 0, dist: Math.abs(moveLeft) },
                      { dx: 0, dy: moveDown, dist: Math.abs(moveDown) },
                      { dx: 0, dy: -moveUp, dist: Math.abs(moveUp) }
                    ].filter(a => a.dist > 0);
                    
                    return adjustments.sort((a, b) => a.dist - b.dist)[0] || { dx: 0, dy: 0 };
                  };
                  
                  // Only adjust if there's actual overlap
                  for (const other of otherTables) {
                    const otherSize = gridCellSizes[other.id] || { width: 450, height: 350 };
                    const otherIndex = gridSymbols.findIndex(s => s.id === other.id);
                    const otherPos = gridCellPositions[other.id] || { 
                      x: (otherIndex % 3) * 460, 
                      y: Math.floor(otherIndex / 3) * 360 
                    };
                    
                    // Check if tables would actually overlap (touching or intersecting)
                    const wouldOverlap = !(
                      newX + draggedSize.width + gridGap <= otherPos.x ||
                      otherPos.x + otherSize.width + gridGap <= newX ||
                      newY + draggedSize.height + gridGap <= otherPos.y ||
                      otherPos.y + otherSize.height + gridGap <= newY
                    );
                    
                    if (wouldOverlap) {
                      // Calculate which edge to snap to (minimum movement)
                      const snapRight = otherPos.x + otherSize.width + gridGap;
                      const snapLeft = otherPos.x - draggedSize.width - gridGap;
                      const snapBelow = otherPos.y + otherSize.height + gridGap;
                      const snapAbove = otherPos.y - draggedSize.height - gridGap;
                      
                      const options = [
                        { x: snapRight, y: newY, dist: Math.abs(snapRight - newX) },
                        { x: snapLeft, y: newY, dist: Math.abs(snapLeft - newX) },
                        { x: newX, y: snapBelow, dist: Math.abs(snapBelow - newY) },
                        { x: newX, y: snapAbove, dist: Math.abs(snapAbove - newY) }
                      ].filter(o => o.x >= 0 && o.y >= 0);
                      
                      if (options.length > 0) {
                        const best = options.sort((a, b) => a.dist - b.dist)[0];
                        newX = best.x;
                        newY = best.y;
                      }
                    }
                  }
                  
                  // Ensure position is not negative
                  newX = Math.max(0, newX);
                  newY = Math.max(0, newY);
                  
                  setGridCellPositions(prev => ({
                    ...prev,
                    [draggedItem.id]: { x: newX, y: newY }
                  }));
                  setDraggedItem(null);
                  setDragOverItem(null);
                } else if (draggedItem && !dragOverItem) {
                  // Normal mode - move to end
                  e.preventDefault();
                  setGridSymbols(prev => {
                    const newSymbols = [...prev];
                    const draggedIndex = newSymbols.findIndex(s => s.id === draggedItem.id);
                    const [removed] = newSymbols.splice(draggedIndex, 1);
                    newSymbols.push(removed);
                    return newSymbols;
                  });
                  setDraggedItem(null);
                  setDragOverItem(null);
                }
              }}
              style={freePositionMode ? (() => {
                // Calculate container size to cover all tables plus extra drop space
                let maxRight = 0;
                let maxBottom = 0;
                gridSymbols.forEach((item, idx) => {
                  const size = gridCellSizes[item.id] || { width: 450, height: 350 };
                  const pos = gridCellPositions[item.id];
                  let x, y;
                  if (pos) {
                    x = pos.x;
                    y = pos.y;
                  } else {
                    // Calculate default position
                    x = (idx % 3) * (450 + gridGap);
                    y = Math.floor(idx / 3) * (350 + gridGap);
                  }
                  maxRight = Math.max(maxRight, x + size.width);
                  maxBottom = Math.max(maxBottom, y + size.height);
                });
                return {
                  position: 'relative',
                  minHeight: Math.max(maxBottom + 500, 800) + 'px', // Extra space for dropping
                  minWidth: Math.max(maxRight + 500, 1200) + 'px',
                  height: 'auto',
                  width: 'auto'
                };
              })() : { 
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'flex-start',
                alignContent: 'flex-start',
                gap: `${gridGap}px`
              }}
            >
              {gridSymbols.map((gridItem, index) => {
                const cellSize = gridCellSizes[gridItem.id] || { width: 450, height: 350 };
                const cellPosition = gridCellPositions[gridItem.id];
                const isDragging = draggedItem?.id === gridItem.id;
                const isDragOver = dragOverItem?.id === gridItem.id;
                
                // Calculate default position for free position mode using gap setting
                // Calculate cumulative positions based on actual sizes
                let defaultX = 0;
                let defaultY = 0;
                let rowY = 0;
                let rowMaxHeight = 0;
                const containerWidth = 1800;
                
                for (let i = 0; i < index; i++) {
                  const prevSize = gridCellSizes[gridSymbols[i].id] || { width: 450, height: 350 };
                  const nextWidth = defaultX + prevSize.width + gridGap;
                  
                  if (nextWidth + cellSize.width > containerWidth && defaultX > 0) {
                    // Move to next row
                    defaultX = 0;
                    rowY += rowMaxHeight + gridGap;
                    rowMaxHeight = 0;
                  } else {
                    defaultX = nextWidth;
                  }
                  rowMaxHeight = Math.max(rowMaxHeight, prevSize.height);
                }
                defaultY = rowY;
                
                // Resize handler factory - creates robust handlers with proper cleanup
                const createResizeHandler = (direction) => (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  
                  const startX = e.clientX;
                  const startY = e.clientY;
                  const startWidth = cellSize.width;
                  const startHeight = cellSize.height;
                  const gridId = gridItem.id;
                  const currentCellSize = { ...cellSize };
                  
                  // Set cursor and disable text selection (but NOT pointer events!)
                  const cursorType = direction === 'right' ? 'ew-resize' : direction === 'bottom' ? 'ns-resize' : 'nwse-resize';
                  document.body.style.cursor = cursorType;
                  document.body.style.userSelect = 'none';
                  document.body.style.webkitUserSelect = 'none';
                  
                  // Create overlay to capture all mouse events during resize
                  const overlay = document.createElement('div');
                  overlay.style.cssText = 'position:fixed;top:0;left:0;right:0;bottom:0;z-index:99999;cursor:' + cursorType;
                  document.body.appendChild(overlay);
                  
                  const onMouseMove = (moveE) => {
                    moveE.preventDefault();
                    moveE.stopPropagation();
                    
                    if (direction === 'right' || direction === 'corner') {
                      // No minimum - allow any width
                      currentCellSize.width = Math.max(50, startWidth + (moveE.clientX - startX));
                    }
                    if (direction === 'bottom' || direction === 'corner') {
                      // No minimum - allow any height  
                      currentCellSize.height = Math.max(50, startHeight + (moveE.clientY - startY));
                    }
                    
                    // Use requestAnimationFrame for smooth updates
                    requestAnimationFrame(() => {
                      handleCellSizeChange(gridId, { ...currentCellSize });
                    });
                  };
                  
                  const onMouseUp = () => {
                    // Remove overlay
                    if (overlay.parentNode) {
                      overlay.parentNode.removeChild(overlay);
                    }
                    
                    // Clean up event listeners
                    document.removeEventListener('mousemove', onMouseMove, true);
                    document.removeEventListener('mouseup', onMouseUp, true);
                    overlay.removeEventListener('mousemove', onMouseMove);
                    overlay.removeEventListener('mouseup', onMouseUp);
                    window.removeEventListener('blur', onMouseUp);
                    
                    // Reset body styles
                    document.body.style.cursor = '';
                    document.body.style.userSelect = '';
                    document.body.style.webkitUserSelect = '';
                  };
                  
                  // Add listeners to overlay for reliable capture
                  overlay.addEventListener('mousemove', onMouseMove);
                  overlay.addEventListener('mouseup', onMouseUp);
                  // Also add to document for edge cases
                  document.addEventListener('mousemove', onMouseMove, true);
                  document.addEventListener('mouseup', onMouseUp, true);
                  window.addEventListener('blur', onMouseUp);
                };
                
                return (
                  <div
                    key={`${gridItem.id}_${gridRefreshKey}`}
                    draggable={customLayoutMode}
                    onDragStart={(e) => handleDragStart(e, gridItem)}
                    onDragOver={(e) => handleDragOver(e, gridItem)}
                    onDrop={(e) => handleDrop(e, gridItem)}
                    onDragEnd={handleDragEnd}
                    className={`relative group select-none transition-transform ${isDragging ? 'opacity-50 scale-95' : ''} ${isDragOver ? 'ring-2 ring-purple-500 ring-offset-2 ring-offset-black scale-[1.02]' : ''}`}
                    style={freePositionMode ? {
                      position: 'absolute',
                      left: cellPosition?.x ?? defaultX,
                      top: cellPosition?.y ?? defaultY,
                      width: cellSize.width,
                      height: cellSize.height,
                      zIndex: isDragging ? 100 : 1
                    } : {
                      width: cellSize.width,
                      height: cellSize.height
                    }}
                  >
                    {/* Drag handle indicator */}
                    <div className="absolute top-0 left-0 right-0 h-8 bg-gradient-to-b from-purple-500/20 to-transparent z-10 cursor-move flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      <span className="text-[9px] text-purple-300 font-medium">⋮⋮ DRAG TO MOVE ⋮⋮</span>
                    </div>
                    
                    {/* Resize handle - Right */}
                    <div
                      className="absolute right-0 top-10 bottom-10 w-4 cursor-ew-resize z-20 group/resize hover:bg-cyan-500/10"
                      onMouseDown={createResizeHandler('right')}
                    >
                      <div className="absolute right-0.5 top-1/2 -translate-y-1/2 w-1.5 h-16 rounded-full bg-cyan-500/40 group-hover/resize:bg-cyan-400 transition-colors" />
                    </div>
                    
                    {/* Resize handle - Bottom */}
                    <div
                      className="absolute bottom-0 left-10 right-10 h-4 cursor-ns-resize z-20 group/resize hover:bg-cyan-500/10"
                      onMouseDown={createResizeHandler('bottom')}
                    >
                      <div className="absolute bottom-0.5 left-1/2 -translate-x-1/2 h-1.5 w-16 rounded-full bg-cyan-500/40 group-hover/resize:bg-cyan-400 transition-colors" />
                    </div>
                    
                    {/* Resize handle - Corner */}
                    <div
                      className="absolute right-0 bottom-0 w-6 h-6 cursor-nwse-resize z-20 flex items-center justify-center rounded-tl-lg bg-cyan-500/20 hover:bg-cyan-500/40 transition-colors"
                      onMouseDown={createResizeHandler('corner')}
                    >
                      <svg className="w-4 h-4 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <path d="M21 15L15 21M21 8L8 21" />
                      </svg>
                    </div>
                    
                    {/* Size indicator */}
                    <div className="absolute bottom-8 right-8 bg-black/80 text-cyan-400 text-[9px] px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 transition-opacity font-mono z-10 pointer-events-none">
                      {Math.round(cellSize.width)} × {Math.round(cellSize.height)}
                    </div>
                    
                    <GridCell
                      token={gridItem.token}
                      gridId={gridItem.id}
                      instanceNum={gridItem.instance}
                      symbol={getSymbolName(instruments, gridItem.token)}
                      ticks={filterTicksByMarketHours(stableTickHistory[gridItem.token] || [], gridItem.token).slice(0, lightweightMode ? Math.min(maxTicksPerSymbol, 1000) : maxTicksPerSymbol)}
                      columns={lightweightMode ? lightweightCols : gridVisibleCols}
                      columnOrder={lightweightMode ? lightweightCols : gridColumnOrder}
                      exchange={getExchangeFromInstrument(getInstrumentByToken(instruments, gridItem.token))}
                      onRemove={() => removeGridSymbol(gridItem.id)}
                      compact={compactMode || lightweightMode}
                      onLoadMore={handleLoadMore}
                      isLoadingMore={loadMoreState[gridItem.token]?.loading || false}
                      hasMoreHistory={loadMoreState[gridItem.token]?.hasMore !== false}
                      isInitialLoading={isInitialLoading || (isLoadingHistory && !historicalTicksLoaded.has(gridItem.token))}
                      smartFilters={smartFilters}
                      globalFilterMode={globalFilterSaveMode}
                      globalHideTimestamp={globalHideTimestampRef.current}
                      onAddToGlobalSaved={addToGlobalSavedTicks}
                      globalAlertEnabled={globalAlertEnabled}
                    />
                  </div>
                );
              })}
            </div>
          ) : (
            /* Normal Grid Layout */
            <div className="grid gap-3 h-full" style={{ gridTemplateColumns: `repeat(${gridColumns}, 1fr)`, gridAutoRows: 'minmax(300px, 1fr)' }}>
              {gridSymbols.map(gridItem => (
                <GridCell
                  key={`${gridItem.id}_${gridRefreshKey}`}
                  token={gridItem.token}
                  gridId={gridItem.id}
                  instanceNum={gridItem.instance}
                  symbol={getSymbolName(instruments, gridItem.token)}
                  ticks={filterTicksByMarketHours(stableTickHistory[gridItem.token] || [], gridItem.token).slice(0, lightweightMode ? Math.min(maxTicksPerSymbol, 1000) : maxTicksPerSymbol)}
                  columns={lightweightMode ? lightweightCols : gridVisibleCols}
                  columnOrder={lightweightMode ? lightweightCols : gridColumnOrder}
                  exchange={getExchangeFromInstrument(getInstrumentByToken(instruments, gridItem.token))}
                  onRemove={() => removeGridSymbol(gridItem.id)}
                  compact={compactMode || lightweightMode}
                  onLoadMore={handleLoadMore}
                  isLoadingMore={loadMoreState[gridItem.token]?.loading || false}
                  hasMoreHistory={loadMoreState[gridItem.token]?.hasMore !== false}
                  isInitialLoading={isInitialLoading || (isLoadingHistory && !historicalTicksLoaded.has(gridItem.token))}
                  smartFilters={smartFilters}
                  globalFilterMode={globalFilterSaveMode}
                  globalHideTimestamp={globalHideTimestampRef.current}
                  onAddToGlobalSaved={addToGlobalSavedTicks}
                  globalAlertEnabled={globalAlertEnabled}
                />
              ))}
            </div>
          )}
        </div>
      )}
      
      {/* Footer - Premium Styling */}
      <div className="h-10 flex items-center justify-between px-4 border-t border-[#333] bg-gradient-to-r from-[#0f0f0f] via-[#111] to-[#0f0f0f] flex-shrink-0">
        <div className="flex items-center gap-4 text-[11px]">
          {viewMode === "single" && selectedToken && (
            <>
              <span className="text-[#71717A]">Total: <span className="text-white font-bold font-mono">{currentTicks.length}</span></span>
              <span className="text-[#71717A]">Buys: <span className="text-green-400 font-bold font-mono">{currentTicks.filter(t => t.agg_buy > 0).length}</span></span>
              <span className="text-[#71717A]">Sells: <span className="text-red-400 font-bold font-mono">{currentTicks.filter(t => t.agg_sell > 0).length}</span></span>
            </>
          )}
          {viewMode === "multi" && (
            <span className="text-[#71717A]">Symbols: <span className="text-white font-bold font-mono">{multiSymbols.length}</span> / {MAX_SYMBOLS}</span>
          )}
          {viewMode === "grid" && (
            <span className="text-[#71717A]">Grid: <span className="text-white font-bold font-mono">{gridSymbols.length}</span> / {MAX_SYMBOLS} • {gridColumns} columns</span>
          )}
          {/* Lightweight mode indicator */}
          {lightweightMode && (
            <span className="text-yellow-400 font-bold">⚡ LIGHT MODE ({lightweightCols.length} cols)</span>
          )}
        </div>
        <div className="text-[10px] text-[#52525B]">
          ESC to close • Auto-save: 3:30 PM & 11:55 PM • 100ms updates • Max 2L ticks
        </div>
      </div>
    </div>
  );
};

// Export with Error Boundary wrapper
const TickHistoryWithErrorBoundary = (props) => (
  <TickHistoryErrorBoundary>
    <TickHistory {...props} />
  </TickHistoryErrorBoundary>
);

export default TickHistoryWithErrorBoundary;
